import { WebPCodec, WorkerQueue } from '@playcanvas/splat-transform';

import { version as appVersion } from '../package.json';
import { Color, createGraphicsDevice, Mat4, Vec3, XRSPACE_LOCALFLOOR, XRTYPE_VR } from 'playcanvas';

import { Camera } from './camera';
import { registerCameraPosesEvents } from './camera-poses';
import { CommandQueue } from './command-queue';
import { registerDocEvents } from './doc';
import { EditHistory } from './edit-history';
import { registerEditorEvents } from './editor';
import { Events } from './events';
import { initFileHandler } from './file-handler';
import { FtgsConvergenceAxes } from './ftgs/convergence-axes';
import type { FtgsDatasetConfig } from './ftgs/dataset-config';
import type { PlaybackTiming } from './ftgs/playback-timing';
import { watchGlassesScreen } from './ftgs/glasses-screen';
import type { GlassesScreen, GlassesPermission } from './ftgs/glasses-screen';
import type { FtgsStereoPreset } from './ftgs/stereo-presets';
import { registerIframeApi } from './iframe-api';
import { registerPreferences } from './preferences';
import { registerPublishEvents } from './publish';
import { registerRenderEvents } from './render';
import { Scene } from './scene';
import { getSceneConfig } from './scene-config';
import { registerSelectionEvents } from './selection';
import { registerSequenceEvents } from './sequence';
import { ShortcutManager } from './shortcut-manager';
import { registerTimelineEvents } from './timeline';
import { BoxSelection } from './tools/box-selection';
import { BrushSelection } from './tools/brush-selection';
import { EyedropperSelection } from './tools/eyedropper-selection';
import { FloodSelection } from './tools/flood-selection';
import { LassoSelection } from './tools/lasso-selection';
import { MeasureTool } from './tools/measure-tool';
import { MoveTool } from './tools/move-tool';
import { OrientTool } from './tools/orient-tool';
import { PolygonSelection } from './tools/polygon-selection';
import { RectSelection } from './tools/rect-selection';
import { RotateTool } from './tools/rotate-tool';
import { ScaleTool } from './tools/scale-tool';
import { SphereSelection } from './tools/sphere-selection';
import { ToolManager } from './tools/tool-manager';
import { registerTrackManagerEvents } from './track-manager';
import { registerTransformHandlerEvents } from './transform-handler';
import { BoundDimensionsOverlay } from './ui/bound-dimensions-overlay';
import { EditorUI } from './ui/editor';
import { FtgsDatasetPicker } from './ui/ftgs-dataset-picker';
import { FtgsPlaybackBar } from './ui/ftgs-playback-bar';
import { FtgsStatsOverlay, FrameTimings } from './ui/ftgs-stats-overlay';
import { FtgsCenterNotice } from './ui/ftgs-center-notice';
import { FtgsFpsBadge } from './ui/ftgs-fps-badge';
import type { SceneUnitsSource } from './ui/ftgs-stereo-controls';
import { FtgsStereoCursor } from './ui/ftgs-stereo-cursor';
import { FtgsStereoMirror, StereoSide } from './ui/ftgs-stereo-mirror';
import { registerTooltipFit } from './ui/ftgs-tooltip-fit';
import { i18n } from './ui/localization';
import { createPointerMoveFilter } from './ui/pointer-move-filter';
import { registerSelectCursor } from './ui/select-cursor';

declare global {
    interface LaunchParams {
        readonly files: FileSystemFileHandle[];
    }

    interface Window {
        launchQueue: {
            setConsumer: (callback: (launchParams: LaunchParams) => void) => void;
        };
        scene: Scene;
    }
}

const getURLArgs = () => {
    // extract settings from command line in non-prod builds only
    const config = {};

    const apply = (key: string, value: string) => {
        let obj: any = config;
        key.split('.').forEach((k, i, a) => {
            if (i === a.length - 1) {
                obj[k] = value;
            } else {
                if (!obj.hasOwnProperty(k)) {
                    obj[k] = {};
                }
                obj = obj[k];
            }
        });
    };

    const params = new URLSearchParams(window.location.search.slice(1));
    params.forEach((value: string, key: string) => {
        apply(key, value);
    });

    return config;
};

const main = async () => {
    // root events object
    const events = new Events();

    // url
    const url = new URL(window.location.href);

    // shared command queue for all async splat work (GPU readbacks + history mutations).
    // every consumer that needs ordering relative to other commands enqueues here.
    const commandQueue = new CommandQueue();

    // edit history (uses the shared queue internally)
    const editHistory = new EditHistory(events, commandQueue);

    // expose the queue as an event for any module that needs to serialise async work
    // alongside history mutations.
    events.function('queue', (fn: () => Promise<void> | void) => commandQueue.enqueue(fn));

    // init localization
    await i18n.init();

    // Configure WebP WASM for SOG format (used for both reading and writing)
    WebPCodec.wasmUrl = new URL('static/lib/webp/webp.wasm', document.baseURI).toString();

    // Run SOG writing inline rather than in worker threads. We don't ship
    // splat-transform's worker.mjs, so leaving the pool enabled makes it try to
    // spawn a worker that 404s; under SOG's parallel task load it then hangs
    // instead of falling back, producing an empty export.
    WorkerQueue.maxWorkers = 0;

    // register events that only need the events object (before UI is created)
    registerTimelineEvents(events);
    registerCameraPosesEvents(events);
    registerTrackManagerEvents(events);
    registerTransformHandlerEvents(events);
    registerPublishEvents(events);
    registerIframeApi(events);

    // initialize shortcuts
    const shortcutManager = new ShortcutManager(events);
    events.function('shortcutManager', () => shortcutManager);

    // editor ui
    const editorUI = new EditorUI(events);

    // Default to a clean full-canvas viewer: hide all the normal SuperSplat
    // editor chrome (menu bar, scene/settings/color panels, toolbars, view
    // cube, status bar) unless ?ftgsShowUI=1 is explicitly passed to bring
    // the normal editing UI back (e.g. for actually using the editor
    // features, not just viewing/recording the FTGS live scene).
    // Independent of ?ftgsDebug either way.
    if (!new URLSearchParams(window.location.search).has('ftgsShowUI')) {
        editorUI.setChromeVisible(false);
    }

    // create the graphics device
    //
    // deviceTypes: ['webgpu'] prefers WebGPU and falls back to WebGL2
    // automatically (the engine appends DEVICETYPE_WEBGL2 to the list if it's
    // not already present). glslangUrl/twgslUrl are required so our own
    // custom GLSL shaders (src/shaders/**) get transpiled to WGSL at
    // runtime when the WebGPU device is used - PlayCanvas's built-in shaders
    // are already WGSL and don't need this, but ours are hand-written GLSL.
    // These WASM transpilers aren't published in the `playcanvas` npm
    // package; they're vendored from the engine repo's own WebGPU examples
    // (playcanvas/engine examples/assets/wasm/{glslang,twgsl}, pinned to
    // this project's engine version) into static/lib/webgpu/.
    const graphicsDevice = await createGraphicsDevice(editorUI.canvas, {
        deviceTypes: ['webgpu'],
        antialias: false,
        depth: false,
        stencil: false,
        xrCompatible: false,
        powerPreference: 'high-performance',
        glslangUrl: new URL('static/lib/webgpu/glslang.js', document.baseURI).toString(),
        twgslUrl: new URL('static/lib/webgpu/twgsl.js', document.baseURI).toString()
    });

    const urlArgs = getURLArgs();

    const overrides = [
        urlArgs
    ];

    // resolve scene config
    const sceneConfig = getSceneConfig(overrides);

    // construct the manager
    const scene = new Scene(
        events,
        sceneConfig,
        editorUI.canvas,
        graphicsDevice,
        commandQueue
    );

    // colors
    const bgClr = new Color();
    const selectedClr = new Color();
    const unselectedClr = new Color();
    const lockedClr = new Color();

    const setClr = (target: Color, value: Color, event: string) => {
        if (!target.equals(value)) {
            target.copy(value);
            events.fire(event, target);
        }
    };

    const setBgClr = (clr: Color) => {
        setClr(bgClr, clr, 'bgClr');
    };
    const setSelectedClr = (clr: Color) => {
        setClr(selectedClr, clr, 'selectedClr');
    };
    const setUnselectedClr = (clr: Color) => {
        setClr(unselectedClr, clr, 'unselectedClr');
    };
    const setLockedClr = (clr: Color) => {
        setClr(lockedClr, clr, 'lockedClr');
    };

    events.on('setBgClr', (clr: Color) => {
        setBgClr(clr);
    });
    events.on('setSelectedClr', (clr: Color) => {
        setSelectedClr(clr);
    });
    events.on('setUnselectedClr', (clr: Color) => {
        setUnselectedClr(clr);
    });
    events.on('setLockedClr', (clr: Color) => {
        setLockedClr(clr);
    });

    events.function('bgClr', () => {
        return bgClr;
    });
    events.function('selectedClr', () => {
        return selectedClr;
    });
    events.function('unselectedClr', () => {
        return unselectedClr;
    });
    events.function('lockedClr', () => {
        return lockedClr;
    });

    events.on('bgClr', (clr: Color) => {
        const cnv = (v: number) => `${Math.max(0, Math.min(255, (v * 255))).toFixed(0)}`;
        document.body.style.backgroundColor = `rgba(${cnv(clr.r)},${cnv(clr.g)},${cnv(clr.b)},1)`;
    });
    events.on('selectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('unselectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('lockedClr', (clr: Color) => {
        scene.forceRender = true;
    });

    // initialize colors from application config
    const toColor = (value: { r: number, g: number, b: number, a: number }) => {
        return new Color(value.r, value.g, value.b, value.a);
    };
    setBgClr(toColor(sceneConfig.bgClr));
    setSelectedClr(toColor(sceneConfig.selectedClr));
    setUnselectedClr(toColor(sceneConfig.unselectedClr));
    setLockedClr(toColor(sceneConfig.lockedClr));

    // create the mask selection canvas
    const maskCanvas = document.createElement('canvas');
    const maskContext = maskCanvas.getContext('2d');
    maskCanvas.setAttribute('id', 'mask-canvas');
    maskContext.globalCompositeOperation = 'copy';

    const mask = {
        canvas: maskCanvas,
        context: maskContext
    };

    // tool manager
    const toolManager = new ToolManager(events);
    toolManager.register('rectSelection', new RectSelection(events, editorUI.toolsContainer.dom));
    toolManager.register('brushSelection', new BrushSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('floodSelection', new FloodSelection(events, editorUI.toolsContainer.dom, mask, editorUI.canvasContainer));
    toolManager.register('polygonSelection', new PolygonSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('lassoSelection', new LassoSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('sphereSelection', new SphereSelection(events, scene, editorUI.canvasContainer, editorUI.tooltips));
    toolManager.register('boxSelection', new BoxSelection(events, scene, editorUI.canvasContainer, editorUI.tooltips));
    toolManager.register('eyedropperSelection', new EyedropperSelection(events, editorUI.toolsContainer.dom, editorUI.canvasContainer));
    toolManager.register('move', new MoveTool(events, scene));
    toolManager.register('rotate', new RotateTool(events, scene));
    toolManager.register('scale', new ScaleTool(events, scene));
    toolManager.register('measure', new MeasureTool(events, scene, editorUI.canvasContainer));
    toolManager.register('orient', new OrientTool(events, scene, editorUI.toolsContainer.dom, editorUI.canvasContainer));

    const boundDimensionsOverlay = new BoundDimensionsOverlay(events, scene, editorUI.canvasContainer);

    editorUI.toolsContainer.dom.appendChild(maskCanvas);

    // show the active selection op (add/remove/intersect) at the cursor
    registerSelectCursor(events, editorUI.toolsContainer.dom);
    registerTooltipFit();

    window.scene = scene;

    // register events that need scene or other dependencies
    registerEditorEvents(events, editHistory, scene);
    registerSelectionEvents(events, scene);
    registerSequenceEvents(events, scene);
    registerDocEvents(scene, events);
    registerRenderEvents(scene, events);
    initFileHandler(scene, events, editorUI.appContainer.dom);

    // apply stored user preferences and start capturing changes to them.
    // registered after the boot-time initialization events above so they are
    // never captured as user changes.
    registerPreferences(events, sceneConfig, urlArgs);

    // load async models
    scene.start();

    // TEMP DEBUG - live scene-graph integration verification (FtgsSplatSceneNode).
    // Remove once this replaces the manual per-frame test loop with a real
    // playback/SegmentManager driver. Adds a real segment as a persistent
    // MeshInstance to scene.worldLayer and drives it from scene.app's own
    // 'update' event (forcing renders, since scene.app.autoRender is false
    // and nothing else would trigger one). On by default (this deployment
    // is set up as a dedicated FTGS viewer, so a bare http://localhost:3001
    // should show a dataset playing immediately, not a blank canvas) - pass
    // ?ftgsDebug=0 to opt back into a genuinely empty SuperSplat editor.
    //
    // Multi-dataset support: `uploads/<id>/` folders (each with its own
    // dataset.json - see ftgs/dataset-config.ts) are discovered via the
    // static `uploads/datasets.json` index (no backend here - see
    // package.json's `serve` script - so a real directory listing isn't
    // possible) and offered through the left-edge FtgsDatasetPicker rail.
    // Clicking a button tears down whatever's currently loaded
    // (`disposeCurrentDataset`) and runs `startFtgsDataset` for the new one.
    // Everything that doesn't depend on WHICH dataset is loaded (playback
    // bar, stats overlay, VR/fullscreen/spacebar wiring, the GPU error/
    // device-lost/latency-probe hooks) is set up exactly once below,
    // outside `startFtgsDataset`, and just gets re-pointed at the new
    // dataset's state on each switch (e.g. `playbackBar.onSeek` is
    // reassigned inside `startFtgsDataset` to close over that session's own
    // `playT`/`loopDuration`).
    if (new URLSearchParams(window.location.search).get('ftgsDebug') !== '0') {
        const {
            loadSegmentStreaming, FtgsSplatSceneNode, FtgsSplatSceneElement, loadHeroCameraPose, loadColmapCameraNames, colmapExactPose, fetchDatasetListing,
            SegmentManager, segmentIndexAtTime, fetchDatasetConfigs, datasetBaseUrl, segmentPlyUrl, binSegmentUrl, variantDirName,
            resolveInitialTier, saveTierChoice, lowTierAvailable, FtgsDepthSorter,
            FtgsFaceTrackerClient, GLASSES_TRACKER_URL, fetchStereoPresets, fetchDatasetStereoPresets, saveDatasetStereoPreset,
            VSEI_LAYOUT, prepareBinDataset, GofStream, GpuLumaProbe, GpuChunkUnpacker, compareGpuRecords,
            DEFAULT_PLAYBACK_TIMING, resolvePlaybackTiming, contentRate, presentedSceneTime,
            listOpfsUsage, deleteDatasetDecodes, deleteAllDecodes, issues, classifyLoadError,
            watchGpuDevice, checkGpuPlan, startBreadcrumb, breadcrumbStage, breadcrumbDetail, FTGS_TILE_MAX_PAIRS, VSEI_PROPERTY_NAMES
        } = await import('./ftgs');
        const { FtgsSourceBadges } = await import('./ui/ftgs-source-badges');
        const { FtgsStereoControls } = await import('./ui/ftgs-stereo-controls');
        const { FtgsDownloadProgressBar } = await import('./ui/ftgs-download-progress-bar');

        // Thin top-center download progress line (see
        // ui/ftgs-download-progress-bar.ts) - reflects the byte-level
        // download progress of whichever segment is currently gating
        // playback (SegmentManager's activeIndex), wired up per-dataset
        // inside startFtgsDataset() below via createSegmentManager's
        // onProgress/onReady/onError.
        const downloadProgressBar = new FtgsDownloadProgressBar();
        editorUI.canvasContainer.append(downloadProgressBar);

        // Playback issue panel (ui/ftgs-issue-panel.ts): hidden until
        // something goes wrong - a stall, a decoder that will not deliver, a
        // failed load - then shows the cause and the evidence, with a copy
        // button; every issue also goes to the server's client-issues/
        // folder (ftgs/issue-reporter.ts). Always on, no URL parameter.
        const { FtgsIssuePanel } = await import('./ui/ftgs-issue-panel');
        const issuePanel = new FtgsIssuePanel(issues);
        editorUI.canvasContainer.dom.appendChild(issuePanel.dom);
        (window as unknown as { __ftgsIssues?: unknown }).__ftgsIssues = issues;
        // abnormal-exit record (ftgs/session-breadcrumb.ts): reports a
        // previous visit the browser ended (out of memory) - it could not
        // report itself - then records this one
        startBreadcrumb(appVersion);

        // Version tag, top-left: shows which BUILD this browser is actually
        // running - the app-shell is served cache-first by the service
        // worker keyed on this same version string (see sw.ts), so during
        // remote testing "is the client on the new build yet?" is otherwise
        // guesswork (hit live several times on 2026-08-31: stale bundles
        // being debugged as if current). Plain text, no interaction. Hidden
        // by default and revealed like the playback bar: mouse movement
        // shows it, and it fades out whenever the bar's idle auto-hide does
        // (wired up below, once the bar exists).
        // Snapshot output folder, picked once per session and kept here
        // (outside startFtgsDataset) so switching datasets doesn't re-prompt.
        // See the Shift+K capture below for why the page asks for a folder at
        // all: a page cannot write to a path of its own choosing.
        let snapshotDir: any = null;

        // 1.3x the original 11px text and 6/10px corner offsets (user
        // request 2026-10-21, same factor as the playback bar and stats
        // panel), then 1.5x of that on every platform (2026-10-26) = 1.95x
        const VERSION_TAG_SCALE = 1.3 * 1.5;
        const versionTag = document.createElement('div');
        versionTag.textContent = `v${appVersion}`;
        versionTag.style.cssText = `position:absolute;top:${6 * VERSION_TAG_SCALE}px;left:${10 * VERSION_TAG_SCALE}px;z-index:9999;color:rgba(255,255,255,0.55);font:${11 * VERSION_TAG_SCALE}px monospace;pointer-events:none;user-select:none;text-shadow:0 1px 2px rgba(0,0,0,0.6);opacity:0;transition:opacity 0.6s ease;`;
        editorUI.canvasContainer.dom.appendChild(versionTag);

        // DIAGNOSTIC (mobile-Chrome black screen on rotate/fullscreen,
        // 2026-08-28): both triggers are canvas-size changes, and the two
        // candidate causes split cleanly on whether the WebGPU device is
        // being LOST (engine restores its own resources on 'devicerestored',
        // but the FTGS renderer's custom GPU buffers/pipelines are not
        // re-uploaded - black splats over a working engine) or the swapchain
        // itself going bad (a Chromium Android surface-rotation issue -
        // nothing our code can fix). These logs, read via chrome://inspect
        // USB remote debugging, tell the two apart: 'devicelost' firing on
        // rotation = case 1, no device events + no errors = case 2.
        scene.graphicsDevice.on('devicelost', () => {
            console.error('[ftgsDebug] WebGPU DEVICE LOST');
        });
        scene.graphicsDevice.on('devicerestored', () => {
            console.warn('[ftgsDebug] WebGPU device restored - FTGS GPU resources are NOT auto-restored');
        });
        window.addEventListener('orientationchange', () => {
            console.log(`[ftgsDebug] orientationchange -> ${screen.orientation?.type}, canvas ${scene.canvas.width}x${scene.canvas.height}`);
        });

        // Raw WebGPU-level diagnostics, beneath the engine's own events -
        // these fire even when the engine's devicelost/devicerestored
        // wrappers don't (e.g. a loss the engine fails to observe, or
        // validation errors that silently blank the output):
        //  - wgpu.lost: the GPUDevice's own loss promise
        //  - uncapturederror: validation/OOM errors not caught by any error
        //    scope - a black-but-alive canvas with these firing per frame
        //    means a broken pipeline/bind group after the resize
        const rawGpu = scene.graphicsDevice as unknown as { wgpu?: GPUDevice };
        if (rawGpu.wgpu) {
            // the same two, as issues for the panel and client-issues/
            // (ftgs/gpu-issues.ts) - the console lines below stay
            watchGpuDevice(rawGpu.wgpu);
            rawGpu.wgpu.lost.then((info) => {
                console.error(`[ftgsDebug] GPUDevice.lost: reason=${info.reason} message=${info.message}`);
            });
            rawGpu.wgpu.addEventListener('uncapturederror', (event) => {
                console.error(`[ftgsDebug] WebGPU uncaptured error: ${(event as GPUUncapturedErrorEvent).error?.message}`);
            });
        }

        // ?ftgsMobileDebug=1 - on-screen console/liveness overlay for
        // debugging on a phone with no USB cable available for
        // chrome://inspect (see ui/ftgs-mobile-debug-overlay.ts)
        if (new URLSearchParams(window.location.search).has('ftgsMobileDebug')) {
            const { FtgsMobileDebugOverlay } = await import('./ui/ftgs-mobile-debug-overlay');
            const dbg = new FtgsMobileDebugOverlay(() => {
                const gd = scene.graphicsDevice as unknown as { backBufferSize?: { x: number; y: number } };
                return {
                    canvas: scene.canvas,
                    width: scene.graphicsDevice.width,
                    height: scene.graphicsDevice.height,
                    backBufferSize: gd.backBufferSize
                };
            });
            dbg.attach(editorUI.canvasContainer.dom);
            scene.app.on('frameend', () => dbg.tick());
        }

        // Ctrl+Alt+X: wipe every locally-held segment copy and reload - the
        // keyboard version of ?ftgsFreshSegments=1, for the re-encode
        // iteration loop (ftgs-lq-encoder rewrites low/segment_*.ply bytes
        // at the same URLs; sw.ts's segment cache serves the OLD bytes for
        // up to its 1h TTL otherwise, and a hard refresh does NOT clear
        // Cache Storage - this trap cost real debugging time on 2026-08-28).
        // Matches the cache by PREFIX (sw.ts bumps the suffix on layout
        // migrations - the old hardcoded 'ftgs-segments-v1' silently
        // stopped clearing anything after the v3 bump) and ALSO drops the
        // OPFS `ftgs-bin/` tree holding P2 bin datasets' browser decodes
        // (ftgs/bin-decode/opfs.ts), so the next load re-downloads AND
        // re-decodes from scratch - and the `ftgs-gof/` tree of cached
        // GOF-container bitstreams (ftgs/gof-decode/bitstream-cache.ts).
        // Was Ctrl+Alt+C, but Evernote's global screen-clip hotkey grabs
        // that at the OS level on this user's machine before the browser
        // ever sees it - X chosen as a nearby key with no common global hook.
        // One implementation, two triggers: this shortcut and the stats
        // overlay's "Clear Cache & Reload" button (wired where the overlay is
        // created, below).
        const clearLocalCachesAndReload = () => {
            const note = document.createElement('div');
            note.textContent = 'Segment cache + OPFS bin decodes + cached bitstreams cleared - reloading...';
            Object.assign(note.style, {
                position: 'fixed', top: '40%', left: '50%', transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.8)', color: '#fff', padding: '10px 18px',
                borderRadius: '8px', font: '13px sans-serif', zIndex: '2000'
            } as Partial<CSSStyleDeclaration>);
            document.body.appendChild(note);
            const wipeCaches = window.caches ?
                caches.keys().then(keys => Promise.all(keys.filter(k => k.startsWith('ftgs-segments')).map(k => caches.delete(k)))) :
                Promise.resolve();
            const wipeOpfs = navigator.storage?.getDirectory ?
                navigator.storage.getDirectory().then(root => Promise.all(['ftgs-bin', 'ftgs-gof'].map(name => root.removeEntry(name, { recursive: true }).catch(() => {})))) :
                Promise.resolve();
            Promise.all([wipeCaches, wipeOpfs]).finally(() => {
                // brief pause so the toast is actually visible before the reload
                setTimeout(() => window.location.reload(), 400);
            });
        };

        window.addEventListener('keydown', (e) => {
            if (e.ctrlKey && e.altKey && e.code === 'KeyX') {
                e.preventDefault();
                clearLocalCachesAndReload();
            }
        });

        // On-screen play/pause + scrub bar (see ui/ftgs-playback-bar.ts) -
        // NOT part of EditorUI's chrome list, so it stays visible even in
        // the default clean-viewer mode (see setChromeVisible above).
        // Created once and reused across dataset switches; `onTogglePlay`/
        // `onSeek` get rewired to the active session inside
        // startFtgsDataset() below.
        const playbackBar = new FtgsPlaybackBar();
        editorUI.canvasContainer.append(playbackBar);

        // version tag follows the bar's auto-hide (see its creation above):
        // any mouse movement shows it, the bar's idle fade hides it again -
        // so the bar's 2 s delay and hover-to-keep rule apply to it as well
        // 위치가 그대로인 이동 신호(포인터 모양 변경 때 Windows 가 보냄)는 무시 - ui/pointer-move-filter.ts
        const isRealVersionTagMove = createPointerMoveFilter();
        window.addEventListener('pointermove', (event: PointerEvent) => {
            if (isRealVersionTagMove(event)) {
                versionTag.style.opacity = '1';
            }
        });
        playbackBar.onIdleChange = (idle: boolean) => {
            versionTag.style.opacity = idle ? '0' : '1';
        };
        // The mouse pointer hides on its OWN, longer idle timer (2026-10-21;
        // it used to follow the bar's 2 s fade): 5 s without any mouse
        // activity anywhere in the window, back on the next movement, click
        // or wheel. Root class - see ftgs-playback-bar.scss; covers the
        // stereo software cursor as well. Window CAPTURE listeners: the
        // overlays stopPropagation their own events, and the stereo cursor
        // swallows the real ones at window capture - registered here at
        // startup, these run before it (same-target capture listeners fire
        // in registration order).
        const CURSOR_IDLE_HIDE_MS = 5000;
        let cursorIdleTimer: number | null = null;
        const wakeCursor = () => {
            document.documentElement.classList.remove('ftgs-cursor-idle');
            if (cursorIdleTimer !== null) {
                window.clearTimeout(cursorIdleTimer);
            }
            cursorIdleTimer = window.setTimeout(() => {
                cursorIdleTimer = null;
                document.documentElement.classList.add('ftgs-cursor-idle');
            }, CURSOR_IDLE_HIDE_MS);
        };
        ['pointerdown', 'wheel'].forEach((type) => {
            window.addEventListener(type, wakeCursor, { capture: true, passive: true });
        });
        // 이동은 위치가 바뀐 것만: 포인터를 숨기면 모양이 바뀌고, Windows 가 그때 보내는 제자리
        // 이동 신호로 다시 깨어나는 반복을 막음 (ui/pointer-move-filter.ts)
        const isRealCursorMove = createPointerMoveFilter();
        window.addEventListener('pointermove', (event: PointerEvent) => {
            if (isRealCursorMove(event)) {
                wakeCursor();
            }
        }, { capture: true, passive: true });
        // counts from page load, like the bar's own countdown
        wakeCursor();

        // Source (PLY/BIN) + Quantization (Gauss/Lin) badges (see
        // ui/ftgs-source-badges.ts). These took the Model Quality row's
        // place in the stats overlay panel (user request 2026-09-09), so
        // they share that panel's visibility (hidden by default, shown via
        // the playback bar's rightmost button). Read-only: `configure()` is
        // re-called inside startFtgsDataset() on each dataset switch, and
        // nothing here switches anything.
        const sourceBadges = new FtgsSourceBadges();
        // Decode Type row (SW | 2): a viewer setting held by this one
        // instance, so it survives dataset switches. Every pick is written
        // to uploads/decode-config.json at once and read back on the next
        // page load (refresh, Ctrl+Alt+X), SW without the file - see
        // ftgs/decode-config.ts and ui/ftgs-source-badges.ts. Only SW is
        // implemented; "2" is a placeholder and the bin path still decodes
        // with HM either way, so for now the value is just logged and saved.
        const decodeConfig = import('./ftgs/decode-config');
        sourceBadges.onDecodeTypeChange = (decodeType) => {
            console.log(`[ftgsDebug] decode type -> ${decodeType}`);
            void decodeConfig.then(({ saveDecodeType }) => saveDecodeType(decodeType));
        };
        void decodeConfig.then(({ fetchDecodeType }) => fetchDecodeType()).then((decodeType) => {
            sourceBadges.applyStartupDecodeType(decodeType);
            console.log(`[ftgsDebug] decode type at startup: ${sourceBadges.decodeType}`);
        });

        // Stereo controls rows (see ui/ftgs-stereo-controls.ts): Eye
        // Distance (IPD) + Convergence sliders and the preset buttons, nested
        // in the stats overlay right below the source badges; UI handles on
        // the same live-retune paths as __ftgsStereoBaseline (wired below,
        // next to the stereo toggle handler).
        //
        // Shown on every platform (user request 2026-10-21 - the panel must
        // list all its items on mobile too). It used to be Windows-desktop
        // only (2026-08-26, the Odyssey 3D workflow); since then it sits
        // collapsed under its Advanced header, so it costs one row elsewhere.
        const stereoControls = new FtgsStereoControls();

        // Glass-pane stats overlay (frame breakdown/camera position;
        // CPU/GPU/VRAM *usage* are permanently N/A - see
        // ui/ftgs-stats-overlay.ts's module doc for why), toggled by the
        // playback bar's rightmost button.
        // Panel size per platform (2026-10-26): 1.2x larger on Windows and
        // macOS only - see ftgs-panel-scale.scss (the dataset rail's name
        // bubble keys a 1.3x font off the same class, ftgs-dataset-picker.scss).
        // The client-hint platform
        // ("Windows" / "macOS" / "Android" ...) when the browser has one
        // (Chromium), else the UA string (Safari, Firefox) - never
        // navigator.platform, which stays "Win32" under device emulation.
        // iPadOS Safari's UA claims a Mac ("Macintosh") but has touch
        // points, which a Mac does not, so that keeps it on the default size.
        const uaPlatform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
        const isWindows = uaPlatform ? uaPlatform === 'Windows' : /Windows NT/.test(navigator.userAgent);
        const isMac = uaPlatform ? uaPlatform === 'macOS' : /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints <= 1;
        document.documentElement.classList.toggle('ftgs-panel-desktop', isWindows || isMac);
        console.log(`[ftgsDebug] stats panel scale: ${isWindows || isMac ? 'desktop (Windows/macOS) 1.2x' : 'default'}`);
        const statsOverlay = new FtgsStatsOverlay();
        statsOverlay.onClearCache = clearLocalCachesAndReload;
        editorUI.canvasContainer.append(statsOverlay);
        // as the first rows, above FRAME/UPDATE/SUBMIT/GPU/RAM/CAMERA/...
        statsOverlay.insertControlsBelowVariant(sourceBadges.dom);
        statsOverlay.dom.insertBefore(stereoControls.dom, sourceBadges.dom.nextSibling);

        // Top-right fps readout while the stats panel is closed (2026-10-26,
        // see ui/ftgs-fps-badge.ts): fed by the frame window below, hidden
        // with the panel open, and faded with the playback bar's idle cycle
        // (chained onto the bar's handler that already drives the version tag)
        const fpsBadge = new FtgsFpsBadge();
        editorUI.canvasContainer.dom.appendChild(fpsBadge.dom);
        fpsBadge.setStatsOpen(statsOverlay.visible);
        const versionTagIdle = playbackBar.onIdleChange;
        playbackBar.onIdleChange = (idle: boolean) => {
            versionTagIdle?.(idle);
            fpsBadge.setIdle(idle);
        };

        // --- `stat unit`-style frame breakdown ---------------------------
        // Unreal splits the frame into Game / Draw / GPU; the browser has
        // the same three boundaries, just on one thread, and AppBase hands
        // them to us directly as app-level events:
        //     frameupdate -> [update phase] -> framerender -> [render] -> frameend
        // so UPDATE and SUBMIT (Unreal's Game and Draw lines) fall straight out of those, and GPU comes from
        // the engine's own timestamp-query profiler.
        //
        // Deliberately NOT app.stats.frame.updateTime/renderTime: rollup
        // strips the profiler instrumentation out of the RELEASE engine
        // build, where `renderStart`/`updateStart` are never assigned yet
        // `renderTime = now() - renderStart` still runs - so those fields
        // read as 0 and as a now()-sized garbage value respectively unless
        // you happen to be on BUILD_TYPE=debug. Timing it here works in
        // both builds. (Compare build/playcanvas/src/framework/app-base.js
        // against build/playcanvas.dbg/... if this ever needs re-checking.)
        const TIMING_EMA_ALPHA = 0.1;
        const smoothMs = (prev: number, sample: number) => {
            return prev === 0 ? sample : prev * (1 - TIMING_EMA_ALPHA) + sample * TIMING_EMA_ALPHA;
        };

        // pc's WebgpuGpuProfiler: `_frameTime` is the span from the first
        // render pass's begin timestamp to the last one's end (so it
        // includes gaps between passes, matching what "GPU" means in stat
        // unit). It's in ms and @ignore/private in the engine's public
        // types, hence the cast - stable enough to read, but treat it as an
        // engine internal if pc ever renames it. (A per-pass breakdown also
        // exists as `passTimings: Map<name, ms>` and was displayed for a
        // while - dropped from the overlay per user request, the split
        // wasn't worth a line once the sort got its own row.)
        type PcGpuProfiler = { enabled: boolean; _frameTime: number };
        const gpuDeviceInfo = graphicsDevice as unknown as {
            gpuProfiler?: PcGpuProfiler;
            supportsTimestampQuery?: boolean;
        };
        // pc requests the `timestamp-query` feature automatically when the
        // adapter offers it (WebgpuGraphicsDevice's requireFeature list), so
        // this is usually true on desktop Chrome/Edge - but it is a real
        // opt-in feature and machines/browsers without it exist, in which
        // case the GPU row honestly reports N/A rather than a made-up
        // number. Note the timings it does produce are quantized to ~100us
        // by the browser and land 1-2 frames late (async readback) - same
        // as Unreal's GPU line, and fine at ms granularity.
        const gpuProfiler = gpuDeviceInfo.gpuProfiler ?? null;
        const gpuTimingSupported = !!gpuProfiler && gpuDeviceInfo.supportsTimestampQuery === true;

        // FRAME is a TRUE WINDOWED AVERAGE, not an EMA like the other rows.
        // The first version EMA'd the rAF interval and let the overlay
        // snapshot it once a second - under intermittent hitches (this
        // scene shows gpuMaxMs spikes of ~50ms) that snapshot is a lottery:
        // land right after a hitch and the EMA is still inflated (~75fps
        // shown), land in a calm stretch and the hitch has been washed out
        // (~123fps shown), while the independent HUD renders/s counter sat
        // at a steady 94 the whole time. Summing every raw frame interval
        // over the window and dividing once cannot be fooled that way -
        // every hitch is counted at full weight exactly once, matching
        // renders/s by construction. The per-phase rows (UPDATE/SUBMIT/GPU)
        // stay EMA: they're stable per-frame costs where light smoothing is
        // fine and per-window bookkeeping would be noise.
        const FRAME_WINDOW_MS = 1000;
        let frameWindowStart = 0;
        let frameWindowAccumMs = 0;
        let frameWindowCount = 0;
        let frameMsWindowed = 0;

        // Sorts per rendered frame, measured over the same 1s window as
        // FRAME. FtgsDepthSorter.lastSortGpuMs is the cost of ONE sort, but
        // the overlay shows SORT as a slice of the per-frame GPU total, so
        // it has to be scaled by how often a sort actually happens: 1.0 in
        // the default every-frame mode (Sort Limit OFF), ~20/fps once the
        // throttle is on. Seeded at 1 so the first window reads as
        // every-frame rather than as "no sorts" - by which point
        // lastSortGpuMs is still 0 anyway, so nothing is displayed either way.
        let sortWindowStartCount = FtgsDepthSorter.sortCount;
        let sortsPerFrame = 1;

        let updateMsSmoothed = 0;
        let submitMsSmoothed = 0;
        let gpuMsSmoothed = 0;
        // SORT gets the same EMA as DRAW, fed on the same profiled frames.
        // It used to display FtgsDepthSorter.lastSortGpuMs raw - a single
        // sort's cost, which steps with every segment swap (bitonic sort
        // padded to a power of two) - next to a multi-second-smoothed DRAW
        // and a 1s-averaged FRAME, so the panel's own rule "FRAME is the
        // larger of CPU and GPU" was visibly violated by a fresh sort spike
        // (seen: GPU 5.20 > FRAME 4.71). Smoothing both sub-rows the same
        // way keeps them comparable; it does not make the sum a lower
        // bound on FRAME (the two are still measured on different frames).
        let sortMsSmoothed = 0;
        let tickStartTime = 0;
        let updateEndTime = 0;
        let tickWillRender = false;
        let tickDidRender = false;

        // Duty-cycle the GPU profiler rather than leaving it on for every
        // frame the overlay is visible: resolving the timestamp queries and
        // reading the result buffer back costs a real ~0.4ms/frame
        // (user-measured 240 -> ~220fps with it always on). The overlay
        // only repaints once a second anyway, so sampling a short burst of
        // consecutive frames each second gives the same displayed number
        // for ~1/20th of the overhead. Burst length covers the profiler's
        // async pipeline: enable is processed at the next frameStart and
        // results land 1-2 frames later, so 8 frames yields ~4-5 usable
        // samples per burst.
        const GPU_SAMPLE_INTERVAL_MS = 1000;
        const GPU_SAMPLE_BURST_FRAMES = 8;
        let gpuSampleWindowStart = -Infinity;
        let gpuSampleFramesLeft = 0;

        scene.app.on('frameupdate', (ms: number) => {
            // `ms` is the raw wall-clock rAF interval: unlike the `dt`
            // handed to update handlers it is neither clamped to
            // maxDeltaTime nor scaled by timeScale, so it's the honest
            // frame period even during a hitch
            tickStartTime = performance.now();
            frameWindowAccumMs += ms;
            frameWindowCount++;
            if (tickStartTime - frameWindowStart >= FRAME_WINDOW_MS) {
                if (frameWindowCount > 0) {
                    frameMsWindowed = frameWindowAccumMs / frameWindowCount;
                    fpsBadge.setFps(frameMsWindowed > 0 ? 1000 / frameMsWindowed : 0);
                    sortsPerFrame = (FtgsDepthSorter.sortCount - sortWindowStartCount) / frameWindowCount;
                }
                frameWindowStart = tickStartTime;
                frameWindowAccumMs = 0;
                frameWindowCount = 0;
                sortWindowStartCount = FtgsDepthSorter.sortCount;
            }

            if (gpuTimingSupported && statsOverlay.visible) {
                if (tickStartTime - gpuSampleWindowStart >= GPU_SAMPLE_INTERVAL_MS) {
                    gpuSampleWindowStart = tickStartTime;
                    gpuSampleFramesLeft = GPU_SAMPLE_BURST_FRAMES;
                }
                gpuProfiler.enabled = gpuSampleFramesLeft > 0;
                if (gpuSampleFramesLeft > 0) {
                    gpuSampleFramesLeft--;
                }
            }
        });
        scene.app.on('framerender', () => {
            updateEndTime = performance.now();
            updateMsSmoothed = smoothMs(updateMsSmoothed, updateEndTime - tickStartTime);
            // must be sampled HERE: app.tick() clears renderNextFrame the
            // instant render() returns, so by 'frameend' there's no way
            // left to tell a drawn frame from a skipped one
            tickWillRender = scene.app.autoRender || scene.app.renderNextFrame;
        });
        scene.app.on('frameend', () => {
            tickDidRender = tickWillRender;
            if (tickDidRender) {
                // render() has returned by now, so this covers culling,
                // command encoding and submission - i.e. the CPU side of
                // drawing, which is what stat unit's Draw line means
                submitMsSmoothed = smoothMs(submitMsSmoothed, performance.now() - updateEndTime);
            }
            if (gpuTimingSupported && gpuProfiler.enabled) {
                const gpuMs = gpuProfiler._frameTime;
                // 0 = results for this frame haven't been read back yet;
                // holding the previous value beats blinking to zero
                if (gpuMs > 0) {
                    gpuMsSmoothed = smoothMs(gpuMsSmoothed, gpuMs);
                }
                // lastSortGpuMs only changes when a timed sort lands, so
                // between landings this re-feeds the held value - same
                // "hold, don't blink to zero" behaviour as gpuMs above
                const sortGpuMs = FtgsDepthSorter.lastSortGpuMs;
                if (sortGpuMs > 0) {
                    sortMsSmoothed = smoothMs(sortMsSmoothed, sortGpuMs);
                }
            }
        });

        const readFrameTimings = (): FrameTimings => {
            // GPU is reported as SORT + DRAW because that is what the frame
            // actually costs on the GPU, and the two are measured by
            // different instruments: DRAW is the engine profiler's span over
            // the render passes, while the depth sort submits its own
            // compute work OUTSIDE those passes and so is invisible to it
            // (see FtgsDepthSorter's timing statics). Summing them here
            // rather than showing the sort as a detached row means the
            // sub-rows add up to their parent.
            //
            // sortMsSmoothed is the (smoothed) cost of ONE sort, so it is
            // scaled onto frames by the measured sort:frame ratio - 1.0 in
            // the default every-frame mode, ~20/fps with Sort Limit on.
            // Without that scaling a throttled sort would be charged to
            // every frame and the GPU total would be true of no actual frame.
            const drawMs = gpuTimingSupported ? gpuMsSmoothed : -1;
            const sortMs = gpuTimingSupported ? sortMsSmoothed * sortsPerFrame : -1;
            // CPU is the same idea one column over: UPDATE + SUBMIT, summed
            // here so the panel's two halves (CPU and GPU) are built the same
            // way. FRAME is NOT cpuMs + gpuMs - the two overlap across
            // frames, so FRAME tracks whichever is larger.
            // render-on-demand: reporting the (near-zero) gap of a skipped
            // tick as a real number would read as "drawing got cheaper"
            // rather than "nothing was drawn"
            const submitMs = tickDidRender ? submitMsSmoothed : 0;
            return {
                fps: frameMsWindowed > 0 ? 1000 / frameMsWindowed : 0,
                frameMs: frameMsWindowed,
                cpuMs: updateMsSmoothed + submitMs,
                updateMs: updateMsSmoothed,
                submitMs,
                gpuMs: gpuTimingSupported ? drawMs + sortMs : -1,
                sortMs,
                drawMs
            };
        };

        playbackBar.onToggleStats = () => {
            statsOverlay.toggle();
            playbackBar.setStatsVisible(statsOverlay.visible);
            fpsBadge.setStatsOpen(statsOverlay.visible);
            // only pay for timestamp queries while the panel is actually up
            // - and even then only in short bursts (see the duty-cycle
            // block in the frameupdate handler, which owns
            // gpuProfiler.enabled while the panel is visible; all this
            // does is force-off on hide and re-arm the burst on show)
            if (gpuTimingSupported) {
                if (statsOverlay.visible) {
                    gpuSampleWindowStart = -Infinity;
                } else {
                    gpuProfiler.enabled = false;
                    gpuSampleFramesLeft = 0;
                }
                // the sorter's own sort-cost instrumentation follows the
                // plain visibility gate (see FtgsDepthSorter's timing doc)
                // - no duty-cycle needed there, it's one tiny readback per
                // ~20Hz sort rather than per frame
                FtgsDepthSorter.timingEnabled = statsOverlay.visible;
            }
        };

        // Timing/state HUD - kept computing (used by window.__ftgsProbe()'s
        // `hud` field for console-based debugging) but no longer shown
        // on screen: this was diagnostic scaffolding for an earlier
        // fade/disappear bug hunt, now resolved, and the on-screen text
        // was cluttering normal screenshots/recordings. Not appended to
        // document.body - flip this back on (and re-add the
        // appendChild) if that class of bug needs baked-into-recording
        // evidence again.
        const hud = document.createElement('div');
        hud.style.cssText = 'position:fixed;top:8px;left:8px;z-index:99999;background:rgba(0,0,0,0.7);color:#0f0;font:12px monospace;padding:4px 8px;pointer-events:none;white-space:pre;display:none;';

        // catch WebGPU validation/OOM errors that aren't tied to a
        // create*() call (e.g. queue submission errors) - these don't
        // always get auto-logged to devtools console the way
        // createComputePipeline's do
        (graphicsDevice as unknown as { wgpu: GPUDevice }).wgpu.addEventListener('uncapturederror', (e: Event) => {
            const message = (e as unknown as { error: GPUError }).error.message;
            // "Destroyed texture ... used in a submit" is a confirmed
            // pre-existing Windows Chrome/Dawn (D3D12) swapchain-texture
            // race in PlayCanvas's own frameStart()/frameEnd()
            // (webgpu-graphics-device.js) - reproduces even in vanilla,
            // unmodified SuperSplat with no FTGS code involved, so it's
            // not something this app can fix. The browser already logs
            // it natively; downgrade our own re-log to console.debug
            // (instead of console.error) so it doesn't read as two
            // stacked errors for the one known-harmless event, while any
            // OTHER uncaptured error still surfaces loudly as before.
            if (message.includes('Destroyed texture') && message.includes('used in a submit')) {
                console.debug('[ftgsDebug] known-benign WebGPU swapchain race (ignored):', message);
                return;
            }
            console.error('[ftgsDebug] uncaptured WebGPU error:', message);
        });

        // NEW hypothesis: a full-scene blackout that recovers after a
        // second or two, independent of our own resort rate, matches
        // Windows GPU driver TDR (timeout detection & recovery) losing
        // and recreating the WebGPU device under sustained load - not
        // something our code can prevent, but every GPUDevice exposes
        // `.lost` (a Promise that resolves once, with a reason, if/when
        // this happens) so we can at least confirm/deny it directly
        // instead of continuing to guess. Logs loudly to both console
        // and the HUD (device-level, not per-dataset - a lost device
        // outlives whichever dataset happened to be active) so it
        // survives a screen recording.
        let deviceLostMsg = '';
        void (graphicsDevice as unknown as { wgpu: GPUDevice }).wgpu.lost.then((info) => {
            const msg = `[ftgsDebug] *** WebGPU DEVICE LOST *** reason=${info.reason} message=${info.message}`;
            console.error(msg);
            deviceLostMsg = msg;
        });

        // GPU latency probe: every ~250ms, time how long
        // queue.onSubmittedWorkDone() takes to resolve - this measures
        // actual GPU completion latency directly, independent of our
        // own submission rate. A spike here during the fade/disappear
        // would mean the GPU itself is genuinely stalled (driver/
        // hardware issue); a latency that stays normal while content
        // still vanishes would rule out the GPU queue entirely and
        // point at something in JS/scene-graph/material state instead.
        // Device-level like the two listeners above - runs continuously
        // regardless of which dataset (if any) is currently loaded.
        let gpuMaxLatencyMs = 0;
        let gpuProbeInFlight = false;
        const gpuDevice = (graphicsDevice as unknown as { wgpu: GPUDevice }).wgpu;
        setInterval(() => {
            if (gpuProbeInFlight) {
                return;
            }
            gpuProbeInFlight = true;
            const start = performance.now();
            gpuDevice.queue.onSubmittedWorkDone().then(() => {
                const ms = performance.now() - start;
                if (ms > gpuMaxLatencyMs) {
                    gpuMaxLatencyMs = ms;
                }
                gpuProbeInFlight = false;
            }).catch(() => {
                gpuProbeInFlight = false;
            });
        }, 250);

        // Slim the per-frame render pipeline down to clear/main/final for
        // the FTGS viewer - the dropped splat/gizmo passes only ever render
        // editor content (editor gsplats, selection outlines, gizmos) that
        // this deployment never creates, yet each costs a full render-pass
        // begin/end with 4K attachment load/stores every frame (measured as
        // part of __ftgsGpuBench's non-splat baseline). ?ftgsFullPasses=1
        // restores the full editor pipeline for comparison/debugging.
        const fullPasses = new URLSearchParams(window.location.search).has('ftgsFullPasses');
        if (!fullPasses) {
            scene.camera.setViewerPassMode(true);
        }

        // Stereo split-view toggle button (see ui/ftgs-playback-bar.ts):
        // Odyssey-style side-by-side - the canvas splits into exact
        // left/right halves rendered simultaneously from two eye
        // positions (at 4K, two ~2K views); clicking again restores the
        // single full-width view.
        //
        // UNITS (user-requested 2026-09-02, matching the reference
        // player's gsplat_player.json/GUI): the canonical stereo distances
        // live in METERS - IPD default 0.063m (shown as cm in the stats
        // panel, like the reference's ImGui slider), convergence default
        // 1.4m. The camera itself still works in COLMAP scene units;
        // metersToSceneUnits converts on every write. The eye-rotation math
        // is a ratio of the two distances, so it's factor-independent.
        //
        // Default 1 (user request 2026-10-21): slider value = scene units,
        // for any dataset whose preset doesn't carry its own factor. It
        // used to be 0.2/0.063 = 3.1746 (the 2026-08-26 Odyssey tuning of
        // 'cart') scaled per dataset by its COLMAP capture-rig radius
        // (2026-09-04 auto-calibration). That auto-calibration is OFF now -
        // the user wants a plain 1 everywhere - and the datasets that relied
        // on it got that factor written into their own stereo-preset-*.json
        // (musical 0.3411, the rest 3.1746), so they look as before.
        const defaultMetersToSceneUnits = 1;
        let metersToSceneUnits = defaultMetersToSceneUnits;
        // shown next to the factor in the stats panel's edit area
        let metersToSceneUnitsSource: SceneUnitsSource = 'default';
        // a manual factor (edit area / console) wins over presets and the
        // default for the rest of the session, across dataset switches
        let sceneUnitsManuallySet = false;
        // on each dataset load, before its presets: back to the default
        // unless a manual factor is in effect
        const resetSceneUnits = (datasetId: string) => {
            if (sceneUnitsManuallySet) {
                console.log(`[ftgsDebug] [${datasetId}] stereo scene units: manual metersToSceneUnits ${metersToSceneUnits.toFixed(4)} kept`);
                return;
            }
            metersToSceneUnits = defaultMetersToSceneUnits;
            metersToSceneUnitsSource = 'default';
            syncCameraStereo();
        };
        // Defaults tuned live on the real Odyssey 3D (user, 2026-09-02;
        // second sweep same day), replacing the reference player's config
        // values (0.063m / -3deg / 1.4m): copying those numbers verbatim
        // put the convergence point far in front of this scene's actual
        // content (its COLMAP depth layout differs from the reference's
        // metric ~1.4m subject), which gave everything a large screen
        // disparity - read as ghosting/crosstalk smear during head
        // movement. These values were swept for minimal ghosting on this
        // dataset. There is no toe-in any more (removed 2026-10-21; it was
        // 0.1 here): convergence alone sets where the eyes' axes cross.
        const defaultBaselineM = 0.014;   // 1.4 cm
        const defaultConvergenceM = 12.54;
        let baselineM = defaultBaselineM;
        let convergenceM = defaultConvergenceM;
        // 3D Mode (stats panel Advanced row, 2026-10-26): Off puts IPD 0 on
        // the CAMERA only - baselineM keeps the set value, so the slider,
        // presets, keys and Save all go on with it and On needs no restore
        // step. Session-wide, like Eye Swap; startup is On.
        let threeDMode = true;
        const syncCameraStereo = () => {
            scene.camera.splitBaseline = (threeDMode ? baselineM : 0) * metersToSceneUnits;
            scene.camera.splitConvergenceDistance = convergenceM * metersToSceneUnits;
            // XREAL anchor-mode head position (meters) uses the same scale as the IPD
            scene.camera.glassesMetersToSceneUnits = metersToSceneUnits;
            scene.forceRender = true;
            stereoControls.setMetersToSceneUnits(metersToSceneUnits, metersToSceneUnitsSource);
        };
        // Convergence point axes (G key, 2026-10-23): an X/Y/Z triad fixed
        // where both eyes converged at the G press - Convergence Dist
        // straight ahead of the eyes' center (see ftgs/convergence-axes.ts).
        // Off at startup; shown only in stereo view. G shows it, the next G
        // hides it.
        const convergenceAxes = new FtgsConvergenceAxes();
        scene.add(convergenceAxes);
        events.on('stereo.toggleConvergenceAxes', () => {
            convergenceAxes.toggle();
        });
        // 2D overlays mirrored into both halves in stereo split view, so
        // each eye sees them (see ui/ftgs-stereo-mirror.ts). The original
        // stays on the side the overlay is anchored to. Overlays created
        // later (the dataset rail) register through addStereoMirror, which
        // also catches them up if stereo is already on.
        const stereoMirrors: FtgsStereoMirror[] = [];
        const addStereoMirror = (el: HTMLElement, originalSide: StereoSide) => {
            const mirror = new FtgsStereoMirror(el, originalSide);
            stereoMirrors.push(mirror);
            if (scene.camera.splitEnabled) {
                mirror.setEnabled(true, scene.camera.splitAnamorphic);
            }
        };
        addStereoMirror(statsOverlay.dom, 'right');
        addStereoMirror(playbackBar.dom, 'right');
        addStereoMirror(versionTag, 'left');
        addStereoMirror(issuePanel.dom, 'left');
        // center-of-view text flash ("3D Mode" / "2D Mode" on the 3D Mode
        // switch) - mirrored so it sits at the center of each eye's half
        const centerNotice = new FtgsCenterNotice();
        editorUI.canvasContainer.dom.appendChild(centerNotice.dom);
        addStereoMirror(centerNotice.dom, 'left');
        addStereoMirror(fpsBadge.dom, 'right');

        // Software cursor for the anamorphic (Odyssey) SBS view - the OS
        // cursor shows in one eye only there and sits on the display-only
        // overlay copies (see ui/ftgs-stereo-cursor.ts). On whenever both
        // conditions hold, off the moment either doesn't - no click, no
        // lock, nothing for the display's own 3D switch to break.
        //
        // Only while ALSO in fullscreen - the playback bar's button (the
        // Fullscreen API) or the browser's own F11 fullscreen (see
        // isBrowserFullscreen): the display splits the whole SCREEN at its
        // center, so the SBS image is only right when the canvas fills it. A windowed stereo view is a
        // layout check on an ordinary monitor, where the OS cursor reaches
        // the originals directly. Leaving fullscreen (Esc / F11) therefore
        // also hands back the OS cursor.
        // Not in the ?ftgsSbsDual 2D demo either.
        const stereoCursor = new FtgsStereoCursor(editorUI.canvasContainer.dom, editorUI.canvas);
        (window as any).__ftgsStereoCursor = stereoCursor;
        // F11 is the browser's fullscreen, not the page's: no
        // fullscreenElement, no fullscreenchange. It shows as the window
        // turning into a chrome-less, screen-sized window, so that is what
        // this checks: the outer window covers the screen AND the viewport
        // fills the window (no tab strip/address bar - which is what tells
        // it apart from a maximized window over an auto-hidden taskbar).
        // inner* is in CSS px and shrinks with page zoom while outer* does
        // not, so the viewport is compared after scaling by the zoom the
        // widths imply (this page has no scrollbar to skew the widths).
        // An estimate by nature; display-mode is checked first for browsers
        // that report F11 through it.
        const isBrowserFullscreen = () => {
            if (window.matchMedia('(display-mode: fullscreen)').matches) {
                return true;
            }
            const { outerWidth, outerHeight, innerWidth, innerHeight } = window;
            // all zero in a hidden/offscreen view - 0 == 0 is no evidence
            if (!innerWidth || !innerHeight || !outerWidth || !outerHeight || !screen.width || !screen.height) {
                return false;
            }
            const coversScreen = Math.abs(screen.width - outerWidth) <= 2 && Math.abs(screen.height - outerHeight) <= 2;
            const zoom = outerWidth / innerWidth;
            return coversScreen && Math.abs(innerHeight * zoom - outerHeight) <= 2;
        };
        const updateStereoCursor = () => {
            const fullscreen = !!document.fullscreenElement || isBrowserFullscreen();
            stereoCursor.setEnabled(scene.camera.splitEnabled && scene.camera.splitAnamorphic && fullscreen);
        };
        document.addEventListener('fullscreenchange', updateStereoCursor);
        // F11 in or out resizes the window - but in SEVERAL steps while the
        // browser hides/shows its toolbars, and the in-between sizes read as
        // "not fullscreen" - evaluating each one would flick the cursor on
        // and off. So only the settled size counts.
        const RESIZE_SETTLE_MS = 300;
        let resizeSettleTimer: number | null = null;
        window.addEventListener('resize', () => {
            if (resizeSettleTimer !== null) {
                window.clearTimeout(resizeSettleTimer);
            }
            resizeSettleTimer = window.setTimeout(() => {
                resizeSettleTimer = null;
                updateStereoCursor();
            }, RESIZE_SETTLE_MS);
        });
        playbackBar.onToggleStereo = (active: boolean) => {
            const search = new URLSearchParams(window.location.search);
            // ?ftgsBaseline=<METERS> (e.g. 0.063) overrides on toggle -
            // NOTE: changed from scene units when the UI went metric
            const baselineParam = search.get('ftgsBaseline');
            if (baselineParam && Number.isFinite(Number(baselineParam))) {
                applyBaseline(Number(baselineParam));
            }
            // default = anamorphic half-SBS (the signal the Odyssey 3D's
            // Reality Hub SBS mode expects - looks squeezed on a normal
            // monitor); ?ftgsSbsDual=1 switches to the dual-view demo
            // (correct proportions on a 2D monitor, wrong on the 3D one).
            scene.camera.splitAnamorphic = !search.has('ftgsSbsDual');
            scene.camera.setSplitView(active);
            // 2D overlays into both halves, so each eye sees them
            for (const mirror of stereoMirrors) {
                mirror.setEnabled(active, scene.camera.splitAnamorphic);
            }
            stereoControls.setThreeDModeVisible(active);
            updateStereoCursor();
            syncCameraStereo();
        };
        // one shared retune path per parameter for the stats-panel sliders
        // and the console helpers - each keeps the other in sync via the
        // setters (which never re-fire the onChange handlers). Distances
        // are in meters end to end; only syncCameraStereo touches scene
        // units.
        const applyBaseline = (meters: number) => {
            baselineM = meters;
            syncCameraStereo();
            stereoControls.setBaseline(meters * 100); // slider shows cm
            stereoControls.setActivePreset(null);
            return baselineM;
        };
        const applyConvergence = (meters: number) => {
            convergenceM = meters;
            syncCameraStereo();
            stereoControls.setConvergence(meters);
            stereoControls.setActivePreset(null);
            return convergenceM;
        };
        // SBS half order (LR/RL) = the stats panel's Eye Swap row (Off =
        // LR, On = RL) and the __ftgsStereoSbsFormat console helper. A
        // display property rather than a dataset one, so the
        // presets never touch it - see ftgs-stereo-controls.ts's
        // module doc.
        const applySbsFormat = (format: 'LR' | 'RL') => {
            scene.camera.setSbsFormat(format);
            stereoControls.setEyeSwap(scene.camera.splitSbsFormat === 'RL');
            return scene.camera.splitSbsFormat;
        };
        stereoControls.onEyeSwapChange = swap => applySbsFormat(swap ? 'RL' : 'LR');
        // Reset Camera (stats panel button / R key) - startFtgsDataset sets
        // it to the loaded dataset's initial pose, its teardown clears it
        let resetCameraToInitial: (() => void) | null = null;
        stereoControls.onResetCamera = () => resetCameraToInitial?.();
        stereoControls.onThreeDModeChange = (on) => {
            threeDMode = on;
            stereoControls.setThreeDMode(on);
            syncCameraStereo();
            // a quick re-switch replaces the text still up at once
            centerNotice.show(on ? '3D Mode' : '2D Mode');
            console.log(`[ftgsDebug] 3D Mode ->${on ? 'On' : 'Off (IPD 0 at the camera)'}`);
        };
        stereoControls.onBaselineChange = cm => applyBaseline(cm / 100);
        stereoControls.onConvergenceChange = applyConvergence;
        // Edit Stereo Preset's range fields: a narrower range pulls the
        // live value inside it (the slider would clamp its thumb anyway, so
        // value and slider never disagree)
        const clampTo = (v: number, range: { min: number; max: number }) => Math.min(Math.max(v, range.min), range.max);
        stereoControls.onBaselineRangeChange = (range) => {
            stereoControls.setBaselineRange(range);
            applyBaseline(clampTo(baselineM * 100, range) / 100);
        };
        stereoControls.onConvergenceRangeChange = (range) => {
            stereoControls.setConvergenceRange(range);
            applyConvergence(clampTo(convergenceM, range));
        };

        // Stereo presets (uploads/stereo-presets.json, see
        // ftgs/stereo-presets.ts): each carries the three values above
        // keyed by an explicit id 0-9 that IS the stats-panel button label
        // and the Ctrl+<id> shortcut. Until the async fetch lands, the map
        // holds just the builtin default as id 0 so preset 0/startup always
        // have a target. Note the apply* calls clear the active-preset
        // highlight (any manual retune path diverges from the preset), so
        // applyPreset re-marks it afterwards.
        const stereoPresetById = new Map<number, FtgsStereoPreset>();
        stereoPresetById.set(0, {
            id: 0,
            eyeDistanceCm: defaultBaselineM * 100,
            convergenceM: defaultConvergenceM
        });
        // Which preset slot a Save writes into. Set by selecting a preset and
        // deliberately NOT cleared when the sliders move afterwards: the whole
        // sweep workflow is "pick slot N, retune, save to N", and applyBaseline
        // & co. drop the active-preset HIGHLIGHT on every change.
        let targetPresetId = 0;
        const applyPreset = (id: number) => {
            const preset = stereoPresetById.get(id);
            if (!preset) {
                return false;
            }
            targetPresetId = id;
            // a dataset file may carry its own slider ranges and scene-units
            // factor (stereo-presets.ts); a preset without them reverts to
            // the panel defaults / the default factor (1), so switching
            // presets never leaves the previous one's behind.
            // Ranges first: setValue below only widens past them.
            stereoControls.setBaselineRange(preset.eyeDistanceCmRange ?? null);
            stereoControls.setConvergenceRange(preset.convergenceMRange ?? null);
            if (!sceneUnitsManuallySet) {
                const factor = preset.metersToSceneUnits ?? defaultMetersToSceneUnits;
                metersToSceneUnitsSource = preset.metersToSceneUnits === undefined ? 'default' : 'preset';
                if (factor !== metersToSceneUnits) {
                    metersToSceneUnits = factor;
                    console.log(`[ftgsDebug] stereo preset ${id}: metersToSceneUnits=${factor.toFixed(4)} (${preset.metersToSceneUnits === undefined ? 'default' : 'from the preset'})`);
                }
            }
            applyBaseline(preset.eyeDistanceCm / 100);
            applyConvergence(preset.convergenceM);
            stereoControls.setActivePreset(id);
            return true;
        };
        // (Eye Swap - the SBS half order - is never touched by a preset:
        // it belongs to the display, not the stereo tuning)
        stereoControls.onPresetSelect = applyPreset;
        // Which dataset a Save writes into. Set by the dataset load path
        // below (the stereo wiring here runs once, before any dataset
        // exists), and the button stays disabled until then.
        let stereoPresetDatasetId: string | null = null;
        stereoControls.setSaveEnabled(false);
        stereoControls.onSave = () => {
            if (!stereoPresetDatasetId) {
                return;
            }
            const datasetId = stereoPresetDatasetId;
            // the LIVE applied values, NOT stereoControls' slider getters:
            // those read `<input type="range" step="0.1">`, which SNAPS to
            // the step, so preset 0's 0.54 cm / 16.24 m would be filed as
            // 0.5 / 16.2. The panel's READOUT does show the
            // true value (toFixed(2)) - these fields are what it prints, and
            // saveDatasetStereoPreset rounds them to 2 decimals the same way.
            // Units already match the file's: cm / m.
            stereoControls.setSaveFeedback('Saving...', 60000);
            void saveDatasetStereoPreset(datasetId, targetPresetId, {
                eyeDistanceCm: baselineM * 100,
                eyeDistanceCmRange: stereoControls.baselineRange,
                convergenceM,
                convergenceMRange: stereoControls.convergenceRange,
                metersToSceneUnits
            }).then((outcome) => {
                if (outcome.status === 'saved') {
                    stereoControls.setSaveFeedback(outcome.downloaded ? `Downloaded ${outcome.filename} - move it into the dataset folder` : `Saved ${outcome.filename}`);
                } else if (outcome.status === 'cancelled') {
                    stereoControls.setSaveFeedback('Save cancelled');
                } else {
                    console.warn(`[ftgs] saving the stereo preset for "${datasetId}" failed:`, outcome.message);
                    stereoControls.setSaveFeedback('Save failed - see console', 4000);
                }
            });
        };
        // apply (not just display) so the camera already carries the
        // defaults when the stereo toggle first turns split view on -
        // the builtin id-0 seed now, the file's own preset 0 as soon as
        // the fetch below resolves
        applyPreset(0);
        const builtinStereoPreset = stereoPresetById.get(0)!;
        const applyStereoPresets = (presets: FtgsStereoPreset[]) => {
            stereoPresetById.clear();
            for (const preset of presets) {
                stereoPresetById.set(preset.id, preset);
            }
            stereoControls.setPresets(presets);
            applyPreset(0);
        };
        // the global file is fetched once; each dataset load then asks
        // whether that dataset overrides it (uploads/<id>/stereo-preset.json,
        // the values swept on the real display for that scene). Awaiting the
        // SAME promise there keeps the order deterministic - the global
        // presets can never land on top of a dataset's own.
        const globalStereoPresets = fetchStereoPresets(builtinStereoPreset);
        globalStereoPresets.then(applyStereoPresets);
        // console helpers take the SAME units as the reference config:
        // __ftgsStereoBaseline(meters), __ftgsStereoConvergence(meters).
        // (__ftgsStereoToeIn went with toe-in, 2026-10-21.) __ftgsMetersToSceneUnits(f)
        // recalibrates this dataset's scene scale in one place.
        (window as any).__ftgsStereoBaseline = applyBaseline;
        (window as any).__ftgsStereoConvergence = applyConvergence;
        (window as any).__ftgsStereoSbsFormat = applySbsFormat;
        // shared by the console helper and the edit area's metersToSceneUnits field
        const applyMetersToSceneUnits = (factor: number) => {
            // a manual calibration wins over the per-dataset
            // auto-calibration for the rest of the session (it would be
            // silently clobbered on the next dataset switch otherwise)
            sceneUnitsManuallySet = true;
            metersToSceneUnits = factor;
            metersToSceneUnitsSource = 'manual';
            syncCameraStereo();
            stereoControls.setActivePreset(null);
            return metersToSceneUnits;
        };
        (window as any).__ftgsMetersToSceneUnits = applyMetersToSceneUnits;
        stereoControls.onMetersToSceneUnitsChange = applyMetersToSceneUnits;

        // FaceTracker head-coupled camera (docs/stereo-view-design.md §5):
        // the client connects to eye_tracking's WebSocket server
        // (ws://127.0.0.1:27016 - its GUI's Listen button must be on) and
        // the newest smoothed pose is polled into the camera every frame.
        // Camera-level like the stereo wiring above - wired up once,
        // independent of dataset switches. Works with stereo OFF too
        // (plain 2D motion parallax).
        // default OFF: the client is created idle and only ever starts on
        // an explicit user action (stats-panel Enable FaceTracking toggle,
        // the playback-bar button, or the F key - all three share
        // setFaceTrackerOn below and stay in sync via onStateChange)
        const faceTracker = new FtgsFaceTrackerClient();
        // XREAL glasses head tracking (xreal_eye_tracker, ws 27018): a second
        // client of the same kind, owned by the VR button's glasses mode
        // below - it runs while glasses are detected (VR icon turns green
        // once the tool answers) but only drives the camera while glasses
        // mode is on, where it takes precedence over the face tracker.
        const glassesTracker = new FtgsFaceTrackerClient(GLASSES_TRACKER_URL);
        // 글래스는 받은 최신 패킷 값을 그대로 씀 (2026-11-02 사용자 요청으로 시각 기반 보간·
        // 따라가기 모두 제거)
        glassesTracker.smoothingTau = 0;
        let glassesModeActive = false;
        faceTracker.onStateChange = (state) => {
            playbackBar.setFaceTrackerState(state);
            stereoControls.setFaceTrackingState(state);
        };
        // shared by the playback-bar button and the F key below; the
        // button's coloring follows via onStateChange either way
        const setFaceTrackerOn = (on: boolean) => {
            if (on) {
                faceTracker.start();
            } else {
                faceTracker.stop();
                scene.camera.faceTrackerPose = null;
                scene.forceRender = true;
            }
        };
        playbackBar.onToggleFaceTracker = setFaceTrackerOn;
        stereoControls.onFaceTrackingChange = setFaceTrackerOn;
        // Head Mode (Shift / LookAt) - session-wide, see Camera.faceTrackerLookAt
        stereoControls.onHeadModeLookAtChange = (lookAt) => {
            scene.camera.faceTrackerLookAt = lookAt;
            stereoControls.setHeadModeLookAt(lookAt);
            scene.forceRender = true;
            console.log(`[ftgsDebug] Head Mode ->${lookAt ? 'LookAt' : 'Shift'}`);
        };

        // Enable Sort Limit (stats panel): ON (default) keeps the 20Hz
        // depth re-sort throttle below; OFF re-sorts every update tick
        // like the reference player. Shared mutable flag - the per-dataset
        // updateHandler in startFtgsDataset() closes over it.
        // default OFF (user-tuned 2026-09-02): re-sort every frame like
        // the reference player; ON restores the 20Hz throttle if the GPU
        // sort cost ever needs shaving
        const sortLimit = { enabled: false };
        stereoControls.onSortLimitChange = (on: boolean) => {
            sortLimit.enabled = on;
            stereoControls.setSortLimit(on);
            scene.forceRender = true;
        };
        stereoControls.setSortLimit(sortLimit.enabled);

        // Render Target (stats panel): main color target format, 16 bit
        // RGBA16F (default) or 8 bit RGBA8, swapped live by
        // Camera.setColorBits. The start value still honours ?ftgsLdr=1.
        stereoControls.onRenderTarget16Change = (on16: boolean) => {
            scene.camera.setColorBits(on16 ? 16 : 8);
            stereoControls.setRenderTarget16(scene.camera.colorBits === 16);
            scene.forceRender = true;
        };
        stereoControls.setRenderTarget16(scene.camera.colorBits === 16);

        // Sort Method (stats panel): bitonic schedule of the GPU depth sort -
        // shared-memory optimized (default) or global-memory. Read by every
        // FtgsDepthSorter.sort() call, so it applies from the next sort;
        // ?ftgsSortGlobal=1 starts on the global-memory schedule.
        FtgsDepthSorter.sharedMemory = !new URLSearchParams(window.location.search).has('ftgsSortGlobal');
        stereoControls.onSortSharedChange = (shared: boolean) => {
            FtgsDepthSorter.sharedMemory = shared;
            stereoControls.setSortShared(shared);
            scene.forceRender = true;
        };
        stereoControls.setSortShared(FtgsDepthSorter.sharedMemory);

        // Clear Pass (stats panel): Separate (default) keeps the dedicated
        // clear pass; Merged clears inside the main pass instead - see
        // Camera.mergeClear. Viewer mode only. ?ftgsMergeClear=1 starts merged.
        scene.camera.setMergeClear(new URLSearchParams(window.location.search).has('ftgsMergeClear'));
        stereoControls.onMergeClearChange = (merged: boolean) => {
            scene.camera.setMergeClear(merged);
            stereoControls.setMergeClear(scene.camera.mergeClear);
            scene.forceRender = true;
        };
        stereoControls.setMergeClear(scene.camera.mergeClear);

        // Playback timing (ftgs/playback-timing.ts): encoded fps, frames
        // consumed per second, scene states presented per second - 30 / 30
        // / 60 by default, i.e. real time presented at 60 Hz. One shared
        // mutable object: the per-dataset updateHandler in
        // startFtgsDataset() reads it every tick, startFtgsDataset()
        // reloads it from each dataset's config (so every dataset starts at
        // 1x), and the stats panel's Playback Speed slider retunes
        // playbackFps live (shown as a multiple of real time, so 0.5x = 15
        // of 30 encoded frames per second). presentHz has no panel control
        // - it only sets how often the presented scene state changes, never
        // the speed.
        const playbackTiming: PlaybackTiming = { ...DEFAULT_PLAYBACK_TIMING };
        // ?ftgsFrameTrace=1 - per-tick playback trace for frame-pacing
        // profiling (window.__ftgsFrameTrace): one record per update tick
        // (rAF time, engine dt, clock/presented scene time, handler CPU ms,
        // resort/swap flags) plus timestamped chunk-decode events, kept in
        // capped ring buffers. Off = null, no per-tick cost.
        type FrameTraceRecord = { t: number; dt: number; playT: number; renderT: number; ms: number; seg: number; resort: boolean; swap: boolean; avail: boolean };
        type FrameTraceEvent = { t: number; kind: string; index: number; decodeMs?: number; unpackMs?: number; waitMs?: number; video?: number; encodeMs?: number; doneMs?: number };
        const frameTrace = new URLSearchParams(window.location.search).has('ftgsFrameTrace') ?
            { frames: [] as FrameTraceRecord[], events: [] as FrameTraceEvent[], max: 20000 } : null;
        (window as unknown as { __ftgsFrameTrace?: typeof frameTrace }).__ftgsFrameTrace = frameTrace;
        const reflectPlaybackTiming = () => {
            stereoControls.setPlaybackSpeed(contentRate(playbackTiming));
        };
        stereoControls.onPlaybackSpeedChange = (speed: number) => {
            playbackTiming.playbackFps = speed * playbackTiming.sourceFps;
            reflectPlaybackTiming();
            scene.forceRender = true;
        };
        reflectPlaybackTiming();
        // Root Rotation (stats panel Advanced row, 2026-10-26): the live
        // dataset's splat node, set by the dataset load path below and
        // cleared on teardown. Opening a dataset starts it at that
        // dataset.json's `rootRotation` (0 0 0 without one); a Rate / Source /
        // Velocity Quant switch rebuilds the node for the SAME dataset and
        // keeps what the panel holds - rootRotationDatasetId tells the two
        // apart.
        let rootRotationNode: InstanceType<typeof FtgsSplatSceneNode> | null = null;
        let rootRotationDatasetId: string | null = null;
        // dataset.json's `rootRotation`, or 0 0 0 when absent / malformed
        const datasetRootRotation = (config: FtgsDatasetConfig) => {
            const r = config.rootRotation;
            if (r === undefined) {
                return { x: 0, y: 0, z: 0 };
            }
            if (!Array.isArray(r) || r.length !== 3 || !r.every(v => typeof v === 'number' && Number.isFinite(v))) {
                console.warn(`[ftgs] [${config.id}] dataset.json rootRotation must be [x, y, z] in degrees - got ${JSON.stringify(r)}, using 0 0 0`);
                return { x: 0, y: 0, z: 0 };
            }
            return { x: r[0], y: r[1], z: r[2] };
        };
        stereoControls.onRootRotationChange = (rotation) => {
            stereoControls.setRootRotation(rotation);
            rootRotationNode?.setRootRotation(rotation.x, rotation.y, rotation.z);
            scene.forceRender = true;
            console.log(`[ftgsDebug] root rotation -> x ${rotation.x} y ${rotation.y} z ${rotation.z} deg`);
        };
        scene.app.on('update', (dt: number) => {
            // sample both every frame so each keeps its own easing state
            const facePose = faceTracker.sample(dt);
            const glassesPose = glassesTracker.sample(dt);
            const useGlasses = glassesModeActive && glassesPose !== null;
            const pose = useGlasses ? glassesPose : facePose;
            scene.camera.faceTrackerPose = pose;
            scene.camera.faceTrackerSource = useGlasses ? 'glasses' : 'face';
            if (pose) {
                // keep frames coming while the head drives the view, even
                // when nothing else (playback, controller) is moving
                scene.forceRender = true;
            }
        });
        // XREAL glasses diagnosis: is glasses mode on, is a pose arriving,
        // and what scale turns its meters into scene units
        (window as any).__ftgsGlassesDebug = () => ({
            glassesModeActive,
            trackerState: glassesTracker.state(),
            pose: glassesTracker.latestPose(),
            cameraSource: scene.camera.faceTrackerSource,
            metersToSceneUnits: scene.camera.glassesMetersToSceneUnits,
            fullscreen: !!document.fullscreenElement
        });
        // live-retune helpers, same convention as the stereo ones: scene
        // units per meter for the head offsets and the client's per-frame
        // easing time constant (One Euro 필터는 2026-11-02 제거)
        (window as any).__ftgsFaceTrackerScale = (v: number) => {
            scene.camera.faceTrackerTranslationScale = v;
            return scene.camera.faceTrackerTranslationScale;
        };
        // per-frame easing time constant in seconds (0 = off)
        (window as any).__ftgsFaceTrackerTau = (v: number) => {
            faceTracker.smoothingTau = v;
            return faceTracker.smoothingTau;
        };

        // keyboard stereo tuning, after the reference player's
        // gsplat_player.cpp key handler, with a user-chosen layout
        // (2026-09-02) of -/+ pairs per slider row, top to bottom:
        //   1/2 = IPD -/+          (reference: 4/6)
        //   4/5 = convergence -/+  (no reference equivalent - json-fixed there)
        //   (7/8 toe-in -/+ removed 2026-10-19; toe-in itself is gone
        //   since 2026-10-21)
        // (top row or numpad; see the TAP_STEP/MAX_RATE block below for
        // the step model), F = face-tracker toggle. Active regardless of
        // whether split view is on (2026-09-23: the reference gates on
        // stereo_enabled, but pre-tuning before toggling stereo is the
        // natural flow here, and the sliders reflect the values either
        // way). Key events originating inside
        // the stats panel/playback bar never reach window (those
        // components stopPropagation), so typing there can't trigger
        // these.
        const heldKeys = new Set<string>();
        const TUNING_KEY_CODES = new Set([
            'Digit1', 'Numpad1', 'Digit2', 'Numpad2', // IPD -/+
            'Digit4', 'Numpad4', 'Digit5', 'Numpad5'  // convergence -/+
        ]);
        const isEditable = (t: EventTarget | null) => t instanceof HTMLElement &&
            (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
        // CAPTURE phase on window - the editor's own Shortcuts class
        // consumes keys at document capture with stopPropagation ('1'/'2'
        // are its move/rotate tool keys, 'f' is camera.focus - see
        // shortcut-manager.ts), which never lets a bubble-phase window
        // listener run. Window capture fires before document capture, so
        // we see the keys first; we stopPropagation ONLY for keys we
        // actually consume (tuning keys and F) so the editor shortcuts
        // keep working otherwise.
        // live Shift state for the sensitivity modifier below - read off
        // every key event (reliable even if Shift goes down/up mid-hold)
        let tuningShiftHeld = false;
        window.addEventListener('keydown', (e: KeyboardEvent) => {
            tuningShiftHeld = e.shiftKey;
            // Ctrl+<digit> applies the stereo preset with that id (works
            // with split view off too - pre-selecting before toggling
            // stereo on is the natural flow). preventDefault stops the
            // page-zoom reset on Ctrl+0; Chrome's Ctrl+1-8 tab switching
            // is a reserved browser shortcut we can't suppress in a
            // normal tab, but the Odyssey runs fullscreen/app-mode where
            // there are no tabs to switch to.
            if (e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey && !isEditable(e.target)) {
                const digit = /^(?:Digit|Numpad)(\d)$/.exec(e.code)?.[1];
                if (digit && !e.repeat && applyPreset(parseInt(digit, 10))) {
                    e.stopPropagation();
                    e.preventDefault();
                }
                return;
            }
            if (e.altKey || e.ctrlKey || e.metaKey || isEditable(e.target)) {
                return;
            }
            if (e.code === 'KeyF' && !e.shiftKey) {
                if (!e.repeat) {
                    setFaceTrackerOn(faceTracker.state() === 'off');
                }
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            // V = the playback bar's Stereo View button (2026-10-26; V was
            // SuperSplat's orbit/fly toggle, unbound in shortcut-manager.ts).
            // e.code, so it also works with the Korean IME active. Same path
            // as the click, so glasses mode, the mirrors and the cursor
            // follow exactly as they do for the button.
            if (e.code === 'KeyV' && !e.shiftKey) {
                if (!e.repeat) {
                    playbackBar.toggleStereo();
                }
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            // X = 재생바의 VR 아이콘 (2026-10-31, XREAL 의 X). 클릭과 같은 경로라 PC 화면에서는
            // 글래스로 전체 화면, 글래스 화면에서는 PC 로 돌아옴. 키 입력도 사용자 동작이라 전체 화면
            // 전환이 됨. e.code 라 한글 입력 상태에서도 동작. Ctrl+Alt+X(캐시 초기화)는 위에서 걸러짐.
            if (e.code === 'KeyX' && !e.shiftKey) {
                if (!e.repeat) {
                    playbackBar.toggleVr();
                }
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            // M = the stats panel's 3D Mode switch (2026-10-26; M was
            // SuperSplat's camera.toggleMode, unbound in shortcut-manager.ts).
            // Stereo view only, like the 3D Mode row itself - in the single
            // view it does nothing. Same path as the row's buttons, so the
            // center "3D Mode" / "2D Mode" notice shows too.
            if (e.code === 'KeyM' && !e.shiftKey) {
                if (!e.repeat && scene.camera.splitEnabled) {
                    stereoControls.onThreeDModeChange?.(!threeDMode);
                }
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            // R = the stats panel's Reset Camera button (2026-11-04; R was
            // SuperSplat's rect selection, unbound in shortcut-manager.ts).
            // e.code, so it also works with the Korean IME active.
            if (e.code === 'KeyR' && !e.shiftKey) {
                if (!e.repeat) {
                    resetCameraToInitial?.();
                }
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            // ` (Backquote) = the playback bar's stats button. e.code, so it
            // also works with the Korean IME active; e.key as a fallback for
            // synthetic/remote-desktop events that leave e.code empty. Same
            // callback as the click so the button state and GPU timing gates
            // stay in sync.
            if (e.code === 'Backquote' || (!e.code && e.key === '`')) {
                if (!e.repeat) {
                    playbackBar.onToggleStats?.();
                }
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            if (TUNING_KEY_CODES.has(e.code)) {
                heldKeys.add(e.code);
                e.stopPropagation();
                e.preventDefault();
            }
        }, true);
        window.addEventListener('keyup', (e: KeyboardEvent) => {
            tuningShiftHeld = e.shiftKey;
            heldKeys.delete(e.code);
        }, true);
        // keyups are lost when the window loses focus mid-hold
        window.addEventListener('blur', () => {
            heldKeys.clear();
            tuningShiftHeld = false;
        });

        // key step model (user-requested 2026-09-02, steps retuned
        // 2026-09-04): a short TAP moves the value by exactly 0.01 in its
        // DISPLAYED unit (cm / m / deg - matching the readouts' two
        // fraction digits); holding ramps a continuous rate up to ~0.1
        // per second (t^2 easing over RAMP_SECONDS). Shift multiplies
        // both by 50 as the fast lane (0.5 per tap).
        const TAP_STEP = 0.01;    // displayed units per key press
        const MAX_RATE = 0.1;     // displayed units per second at full ramp
        const RAMP_SECONDS = 1.5;
        // seconds each key has been held (0 = not held)
        const stereoKeyHolds = { ipdDec: 0, ipdInc: 0, convDec: 0, convInc: 0 };
        const accelStep = (holds: typeof stereoKeyHolds, key: keyof typeof stereoKeyHolds, down: boolean, dt: number) => {
            if (!down) {
                holds[key] = 0;
                return 0;
            }
            const firstTick = holds[key] === 0;
            holds[key] += dt;
            if (firstTick) {
                // the tap contribution - applied once on the press edge
                return TAP_STEP;
            }
            const t = Math.min(holds[key] / RAMP_SECONDS, 1);
            return MAX_RATE * t * t * dt;
        };
        const keyDown = (...codes: string[]) => codes.some(c => heldKeys.has(c));
        scene.app.on('update', (dt: number) => {
            const decIpd = keyDown('Digit1', 'Numpad1');
            const incIpd = keyDown('Digit2', 'Numpad2');
            const decConv = keyDown('Digit4', 'Numpad4');
            const incConv = keyDown('Digit5', 'Numpad5');
            // Shift = 50x fast lane (0.01 -> 0.5 per tap); steps come out
            // of accelStep in the DISPLAYED unit of each row, so the IPD
            // (shown in cm, stored in meters) divides by 100 while
            // convergence (m) passes through as-is
            const sens = tuningShiftHeld ? 50 : 1;
            const ipdDecStep = accelStep(stereoKeyHolds, 'ipdDec', decIpd, dt) * sens / 100;
            const ipdIncStep = accelStep(stereoKeyHolds, 'ipdInc', incIpd, dt) * sens / 100;
            const convDecStep = accelStep(stereoKeyHolds, 'convDec', decConv, dt) * sens;
            const convIncStep = accelStep(stereoKeyHolds, 'convInc', incConv, dt) * sens;
            if (decIpd || incIpd) {
                // meters, clamped to the slider's current range (cm, default
                // 0..30 - a preset may set its own)
                const ipdRange = stereoControls.baselineRange;
                applyBaseline(Math.min(ipdRange.max / 100, Math.max(ipdRange.min / 100, baselineM - ipdDecStep + ipdIncStep)));
            }
            if (decConv || incConv) {
                // meters, clamped to the slider's current range (default
                // 0..50, 0 = parallel rig - a preset may set its own)
                const convRange = stereoControls.convergenceRange;
                applyConvergence(Math.min(convRange.max, Math.max(convRange.min, convergenceM - convDecStep + convIncStep)));
            }
        });

        // VR toggle button (see ui/ftgs-playback-bar.ts). app.xr always
        // exists once XrManager is registered in pc-app.ts (even on a
        // browser/device with no XR hardware at all), so `scene.app.xr`
        // itself is never null here - what varies is
        // `.supported`/`.isAvailable(XRTYPE_VR)`. Camera-level, not
        // per-dataset - wired up once.
        const cam = scene.camera.camera;
        const xr = scene.app.xr;
        // WebXR path switched off for now (user request 2026-10-19): the
        // VR button drives "glasses mode" instead (below). Flip to true to
        // bring the WebXR session button back.
        const VR_ENABLED = false;
        if (!VR_ENABLED) {
            // 글래스 모드: USB-C DisplayPort 로 연결한 XREAL 글래스는 외부 화면으로 잡힙니다.
            // 연결되어 있는 동안 VR 버튼이 켜지고 글래스 이름을 표시합니다.
            // 2026-10-30 변경: 글래스 모드는 '창이 글래스 화면에 놓여 있는가'로 자동 결정합니다
            // (glasses-screen.ts 의 currentScreen). VR 버튼으로 옮겼든, 안경 재연결 뒤 Windows 가
            // 창을 되돌렸든, 창을 끌어다 놓았든 글래스 화면에 있으면 회전은 항상, 위치는 툴이
            // 보내는 대로(툴의 RGB 'Enable' 체크) 적용됩니다. 스테레오 자동 켜기·Follow 요청도 함께.
            // VR 버튼: PC 화면에서는 글래스로 이동(전체 화면), 글래스 화면에서는 PC 복귀 한 가지뿐.
            let glassesScreen: GlassesScreen | null = null;
            let glassesPermission: GlassesPermission = 'prompt';
            let glassesMode = false;
            let onGlassesScreen = false;
            // 글래스 모드가 직접 켠 것만 되돌림
            let stereoFromGlasses = false;
            const glassesName = () => glassesScreen?.label ?? null;
            const setStereo = (active: boolean) => {
                playbackBar.setStereoActive(active);
                playbackBar.onToggleStereo?.(active);
            };
            // 안경 버튼으로 앵커 모드 등이 되어 있어도 Follow 로 되돌림: xreal_eye_tracker 툴이
            // 글래스 제어 포트로 전달(브라우저는 그 포트에 직접 접속 불가). 툴이 없으면 아무 일 없음.
            // 이후 사용자가 앵커 모드로 바꾸면 툴이 회전을 멈추고 위치만 보냄(이중 회전 방지).
            const requestFollow = () => {
                const sent = glassesTracker.sendCommand('follow');
                console.log(`[ftgsDebug] glasses mode: follow request ${sent ? 'sent' : 'deferred (tracker tool not connected yet)'}`);
            };
            const enterGlassesMode = () => {
                if (glassesMode) {
                    return;
                }
                glassesMode = true;
                glassesModeActive = true;
                scene.forceRender = true;
                if (!scene.camera.splitEnabled) {
                    setStereo(true);
                    stereoFromGlasses = true;
                }
                requestFollow();
                playbackBar.setGlasses(glassesName(), true, glassesPermission);
                console.log('[ftgsDebug] glasses mode: on (window is on the glasses display)');
            };
            const leaveGlassesMode = () => {
                if (!glassesMode) {
                    return;
                }
                glassesMode = false;
                glassesModeActive = false;
                scene.forceRender = true;
                if (stereoFromGlasses && scene.camera.splitEnabled) {
                    setStereo(false);
                }
                stereoFromGlasses = false;
                playbackBar.setGlasses(glassesName(), false, glassesPermission);
                console.log('[ftgsDebug] glasses mode: off (window left the glasses display)');
            };
            playbackBar.setGlasses(null, false, glassesPermission);
            glassesTracker.onStateChange = (state) => {
                playbackBar.setGlassesTracking(state);
                // 글래스 모드에 들어갈 때 툴이 아직 연결 전이었으면, 연결되는 순간 Follow 요청
                if (state === 'connected' && glassesMode) {
                    requestFollow();
                }
            };
            const glassesWatch = watchGlassesScreen((s, permission, onGlasses) => {
                glassesScreen = s;
                glassesPermission = permission;
                onGlassesScreen = onGlasses;
                console.log(`[ftgsDebug] glasses display: ${s ? s.label : 'disconnected'} (permission ${permission}, window on glasses: ${onGlasses})`);
                // 머리 추적은 글래스 연결을 따름: 글래스가 감지되는 동안 xreal_eye_tracker 접속을 계속 시도
                if (s) {
                    glassesTracker.start();
                } else {
                    glassesTracker.stop();
                }
                if (s && onGlasses) {
                    enterGlassesMode();
                } else {
                    leaveGlassesMode();
                }
                playbackBar.setGlasses(glassesName(), glassesMode, glassesPermission);
            });
            // 전체 화면에서 나온 뒤 창이 어느 화면에 남았는지 다시 확인(글래스 화면에 남으면 계속 글래스 모드)
            document.addEventListener('fullscreenchange', () => glassesWatch.refresh());
            playbackBar.onToggleVr = () => {
                if (!glassesScreen) {
                    // 아직 권한을 묻지 않음: 이 클릭으로 요청
                    if (glassesPermission === 'prompt') {
                        glassesWatch.request();
                    }
                    return;
                }
                if (onGlassesScreen) {
                    // 글래스 화면 안에서는 'PC 화면으로 복귀' 한 가지 역할
                    if (document.fullscreenElement) {
                        // 전체 화면 전 위치(PC 화면)로 돌아감 → currentscreenchange 로 글래스 모드 자동 꺼짐
                        document.exitFullscreen().catch(() => glassesWatch.refresh());
                        return;
                    }
                    // 전체 화면이 아닌 채 글래스 화면에 놓인 창(재연결·끌어다 놓기): 브라우저는 일반 창을
                    // 스크립트로 옮길 수 없으므로 PC 화면에서 전체 화면으로 엶. 여기서 Esc 로 나오면 Chrome 이
                    // 원래 자리(글래스 화면)로 되돌릴 수 있음 - 그때는 Win+Shift+←/→ 로 옮겨야 함.
                    const pc = glassesWatch.pcScreen();
                    document.documentElement.requestFullscreen((pc ? { screen: pc } : {}) as FullscreenOptions).catch((error: unknown) => {
                        console.warn('[ftgsDebug] fullscreen on the PC display failed:', error);
                    });
                    console.log('[ftgsDebug] glasses mode: window was not fullscreen on the glasses - opened fullscreen on the PC display (Win+Shift+Left/Right moves the window if Esc sends it back)');
                    return;
                }
                // 클릭 처리 안에서 바로 실행해야 함(전체 화면은 사용자 동작이 필요)
                document.documentElement.requestFullscreen({ screen: glassesScreen } as FullscreenOptions).then(() => {
                    // 보통 currentscreenchange 가 먼저 와서 이미 켜져 있음 - 안 왔을 때를 대비
                    glassesWatch.refresh();
                    enterGlassesMode();
                }).catch((error: unknown) => {
                    console.warn('[ftgsDebug] fullscreen on glasses failed:', error);
                });
            };
        } else if (xr?.supported) {
            // starts false until the async device probe resolves (see
            // XrManager's constructor _deviceAvailabilityCheck()) -
            // reflect it immediately in case it's already resolved, AND
            // subscribe for when it resolves/changes later (e.g. a
            // headset gets plugged in after page load).
            playbackBar.setVrAvailable(xr.isAvailable(XRTYPE_VR));
            xr.on(`available:${XRTYPE_VR}`, (available: boolean) => {
                playbackBar.setVrAvailable(available);
            });

            playbackBar.onToggleVr = () => {
                if (xr.active) {
                    xr.end();
                    return;
                }
                // must be called directly inside this click handler (not
                // after an await/timer) - WebXR requires a user gesture,
                // see XrManager.start()'s own doc comment.
                xr.start(cam, XRTYPE_VR, XRSPACE_LOCALFLOOR, {
                    callback: (error: Error | null) => {
                        if (error) {
                            console.error('[ftgsDebug] failed to start VR session:', error);
                        }
                    }
                });
            };
            // source of truth for the icon is the real session state,
            // not just click intent - also catches a session ended from
            // inside the headset's own system menu, not just our button.
            xr.on('start', () => playbackBar.setVrActive(true));
            xr.on('end', () => playbackBar.setVrActive(false));
            xr.on('error', (error: Error) => {
                console.error('[ftgsDebug] VR session error:', error);
                playbackBar.setVrActive(false);
            });
        } else {
            // WebXR not supported at all in this browser (or a
            // Permissions-Policy blocks it, e.g. an embedding iframe
            // without allow="xr-spatial-tracking") - hide the button
            // rather than leave a dead control that errors on click.
            playbackBar.setVrAvailable(false);
        }

        // Spacebar play/pause, same as any video player. NOT routed
        // through ShortcutManager's existing Space binding
        // ('timeline.togglePlay', SuperSplat's own unrelated keyframe-
        // animation timeline) - deliberately separate, per this block's
        // module doc elsewhere. That existing binding is exactly why
        // this listener MUST be capture-phase: shortcuts.ts registers
        // its own Space handler on `document` in the BUBBLE phase and
        // calls e.stopPropagation() the instant it matches (confirmed
        // by reading shortcuts.ts directly) - a real keypress starts at
        // whatever's focused and bubbles up THROUGH document before
        // ever reaching a bubble-phase `window` listener, so that
        // stopPropagation() was killing this listener before it ever
        // ran. Capture phase runs window -> document -> ... -> target,
        // strictly BEFORE any bubble-phase listener anywhere, so this
        // now wins first - event.stopPropagation() here in turn stops
        // shortcuts.ts's handler from also firing afterward for the
        // same keypress.
        //
        // Delegates straight to playbackBar.onTogglePlay, which
        // startFtgsDataset() rewires to the currently-active dataset's
        // own paused/playT state on every switch - so this single
        // listener automatically tracks whichever dataset is loaded
        // without needing its own reference to "the current session".
        //
        // Skips when a text field has focus (contenteditable rows in
        // camera-info-overlay.ts, any future input) so typing a literal
        // space doesn't also toggle playback, and ignores key-repeat so
        // holding Space doesn't rapid-toggle.
        window.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.code !== 'Space' || event.repeat) {
                return;
            }
            const target = event.target as HTMLElement | null;
            const tag = target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            playbackBar.onTogglePlay?.();
        }, true);

        /**
         * Loads and starts playing one dataset: builds its segment manifest,
         * fetches segment 0, positions the hero camera, registers the
         * scene's per-frame driver, and (re)assigns every window.__ftgs*
         * debug hook to close over THIS dataset's data - so whichever
         * dataset was loaded most recently automatically "wins" for those
         * hooks, without needing to track/clear them individually. Returns a
         * dispose() that undoes everything scene-graph/listener-side (but
         * leaves the singleton UI/device-level wiring above untouched).
         */
        const startFtgsDataset = async (dataset: FtgsDatasetConfig, abortSignal?: AbortSignal): Promise<() => void> => {
            // Per-dataset, re-applied on every switch so returning to a
            // dataset without the flag brings the tag back (see the field's
            // doc in dataset-config.ts). Unset = '' = the stylesheet's own
            // display, i.e. exactly what it has always been.
            versionTag.style.display = dataset.hideVersionTag ? 'none' : '';
            // A pose override LOCKS the camera and lives on the camera, which
            // outlives every dataset switch - so it MUST be cleared here or
            // switching away from an exact-COLMAP dataset would strand the
            // next one at that pose, frozen. Only the opt-in branch further
            // down sets it, so every other dataset starts and stays at null,
            // i.e. exactly the controller-driven camera it always had.
            scene.camera.setPoseOverride(null);
            // Each bitstream generation has exactly one decode path, so the
            // Decode Type row shows that path whatever the saved setting is:
            // a GOF-container bitstream decodes with WebCodecs only (HW),
            // every other dataset with the HM-WASM prepare stage (SW)
            sourceBadges.lockDecodeType(dataset.source === 'bin' && dataset.binFormat === 'gof' ? 'type2' : 'sw');
            // Same reasoning for the FOV, and this one actually bit: the
            // exact-COLMAP branch writes camera.fov (53.7deg for musical) and
            // nothing else ever writes it back, because the only other writer
            // is the hero-pose block - which a `cameraSpawn` dataset skips
            // entirely. Switching musical -> anything therefore carried
            // musical's focal length over to it. Restoring the configured
            // default here is a no-op for every dataset that never changed it
            // (camera.ts:455 sets this same value at startup).
            scene.camera.fov = scene.config.camera.fov;
            // (set below - it needs the manifest the tier resolution builds)
            const segmentDuration = dataset.segmentDuration;
            // FTGSC_FORMAT_SPEC.md: each segment file is a fixed-duration
            // TRANSMISSION unit (not a strict temporal partition - see
            // ftgs-playback-range.ts's module doc for why a single file's
            // own `time` field can span far outside its nominal window),
            // and the reference rebases playback time to an absolute
            // [0, segmentCount*duration) loop starting at 0 (scene/
            // manager.py: `t_offset=0.0`), NOT derived per-file.
            //
            // Model Quality tiers: the low tier is the SAME segments
            // subsampled offline into `uploads/<id>/low/` by ftgs-lq-encoder
            // (identical filenames/format, fewer splats) - so a tier is
            // purely which directory the manifest's URLs point at. Both
            // manifests share the same index->time mapping, which is what
            // makes the mid-playback tier switch below safe.
            const lowAvailable = lowTierAvailable(dataset);
            let qualityTier = resolveInitialTier(dataset);
            // segmentPlyUrl() also handles the rate ladder: for a laddered
            // PLY variant it points at the real decode inside the variant
            // folder, for a laddered BIN variant at the OPFS-backed URL sw.ts
            // answers. The low tier only exists in the legacy flat layout.
            const buildManifest = (tier: 'high' | 'low') => Array.from({ length: dataset.segmentCount }, (_, i) => ({
                index: i,
                url: segmentPlyUrl(dataset, i, tier),
                startTime: i * segmentDuration,
                duration: segmentDuration
            }));
            let manifest = buildManifest(qualityTier);
            // What the Variant row names: the file this session actually
            // pulls. For a bin dataset that is the bitstream, NOT the
            // manifest URL - the latter has no file behind it (sw.ts
            // answers it from the OPFS decode), so showing it would name
            // something that does not exist on the server.
            statsOverlay.setVariant((dataset.source === 'bin' ? binSegmentUrl(dataset, 0) : manifest[0].url).replace(/^\/uploads\//, ''));
            issues.setContext({
                dataset: dataset.id,
                variant: (dataset.source === 'bin' ? binSegmentUrl(dataset, 0) : manifest[0].url).replace(/^\/uploads\//, ''),
                app: `v${appVersion}`
            });

            // P2 bin datasets (docs/p2-browser-bin-decode-design.md 3): the
            // segment URLs above have no file behind them on the server -
            // the prepare stage decodes `segment_NNN.bin` in the browser
            // (worker pool, HM-WASM) into OPFS, and sw.ts answers those same
            // URLs from there. Everything from the bootstrap load down is
            // then byte-for-byte the plain-ply path. Clip 0 is awaited here
            // (nothing can come up without it); the rest keep decoding in
            // the background while the scene already plays - SegmentManager
            // simply finds them (a not-yet-decoded clip 404s and is retried
            // on its normal backoff).
            let binPrepare: ReturnType<typeof prepareBinDataset> | null = null;
            // GOF-container bitstreams (makeup, 8-bit): no prepare stage and
            // no OPFS - gof-stream.worker.ts streams the compressed bitstream
            // and decodes each chunk on demand with WebCodecs (hardware
            // HEVC), handing SegmentManager in-memory segments through its
            // `loadIndexed` option. Chunk k = segment k = seconds k..k+1.
            let gofStream: InstanceType<typeof GofStream> | null = null;
            if (dataset.source === 'bin' && dataset.binFormat === 'gof') {
                // WebCodecs exists only in a secure context (https, localhost,
                // or a chrome://flags insecure-origin exception) - say so up
                // front instead of failing inside the worker
                if (typeof VideoDecoder === 'undefined') {
                    throw new Error(`VideoDecoder not available (isSecureContext ${window.isSecureContext}, origin ${location.origin})`);
                }
                const label = binSegmentUrl(dataset, 0).replace(/^\/uploads\//, '');
                // The bin path's "SW Decoding Time · Unpacking" notice, for
                // the hardware path: the first chunk's WebCodecs decode (its
                // three pictures, copy-out included) and unpack times, shown
                // once per load where the download bar was - no chunk counter
                // (user request 2026-09-28): it is always the first chunk,
                // so "(1/10)" carried no information. Every later
                // chunk's times go to the console only - one a second would
                // bury the screen. While the download bar is still running
                // the notice waits for it (a bar update() cancels a notice).
                let downloading = false;
                let downloadDone = false;
                let decodeNotice: string | null = null;
                const showDecodeNotice = () => {
                    if (decodeNotice !== null && !downloading) {
                        downloadProgressBar.complete(decodeNotice);
                        decodeNotice = '';
                    }
                };
                // device test: hash every chunk (all) or the first N against the picture hash SEI instead of the first 2
                const verifyChunks = ((v) => (v === 'all' ? 1e9 : Number(v) > 0 ? Number(v) : undefined))(new URLSearchParams(window.location.search).get('ftgsVerifyChunks'));
                // ?ftgsGpuProbe=1 (C안 0단계 시험, 2026-11-02): 디코딩한 프레임의 복제본을 렌더용 WebGPU
                // 장치에서 Y 추출해 보고 정확도·비용을 기록 (ftgs/gpu-luma-probe.ts). 재생 데이터는 그대로.
                // 결과는 콘솔 '[ftgs] gpu probe' 와 __ftgsGpuProbe.summary()
                const rawGpuDevice = (scene.graphicsDevice as unknown as { wgpu?: GPUDevice }).wgpu;
                const gpuProbeOn = new URLSearchParams(window.location.search).has('ftgsGpuProbe') && !!rawGpuDevice;
                let gpuLumaProbe: InstanceType<typeof GpuLumaProbe> | null = null;
                abortSignal?.addEventListener('abort', () => gpuLumaProbe?.destroy(), { once: true });
                // ?ftgsGpuUnpack=1 (C안 1단계, 2026-11-02): MD5 검증이 끝난 청크는 렌더용 WebGPU 장치에서
                // 언팩 (ftgs/gpu-unpack.ts) - GPU→CPU 읽기, CPU 언팩, 49 MB 전달, 분할 업로드가 없어짐.
                // 속성 재사용(makeup-v3)도 2단계(2026-11-02)부터 GPU 에서 함. ?ftgsGpuUnpackVerify=1 은
                // 같은 청크를 CPU 로도 언팩(+재사용)해 열 묶음별로 비교 (결과는 콘솔 '[ftgs] gpu unpack')
                const reuseAttributes = dataset.reuseAttributes === true &&
                    new URLSearchParams(window.location.search).get('ftgsReuseAttributes') !== '0';
                // 2026-11-02: Windows PC(Chromium WebGPU)는 기본으로 켬 - 노트북·PC 에서만 검증됨.
                // 모바일·Mac·iPad 는 꺼진 채 유지. ?ftgsGpuUnpack=1 은 어느 기기든 강제로 켜고 =0 은 끔
                const gpuUnpackParam = new URLSearchParams(window.location.search).get('ftgsGpuUnpack');
                const gpuUnpackAsked = gpuUnpackParam === '1' || (gpuUnpackParam !== '0' && isWindows);
                const gpuUnpackOn = gpuUnpackAsked && !!rawGpuDevice && dataset.residentSegments !== true;
                const gpuUnpackVerify = gpuUnpackOn && new URLSearchParams(window.location.search).get('ftgsGpuUnpackVerify') === '1';
                if (gpuUnpackAsked && !gpuUnpackOn) {
                    console[gpuUnpackParam === '1' ? 'warn' : 'info'](`[ftgs] gpu unpack: not used for ${dataset.id} - ${!rawGpuDevice ? 'no WebGPU device' : 'resident segments'}`);
                }
                const gpuUnpacker = gpuUnpackOn ? new GpuChunkUnpacker(rawGpuDevice) : null;
                const gpuUnpackStats = { chunks: 0, encodeMs: [] as number[], compared: 0, exact: 0, lines: [] as string[], reuseLines: [] as string[], reuseMismatch: 0 };
                if (gpuUnpacker) {
                    abortSignal?.addEventListener('abort', () => gpuUnpacker.destroy(), { once: true });
                    gpuUnpacker.onAsyncError = (message) => {
                        console.error(`[ftgs] gpu unpack: WebGPU error - ${message}`);
                        gofStream?.disableGpuUnpack(`WebGPU 오류: ${message}`, -1);
                    };
                    (window as unknown as { __ftgsGpuUnpack?: unknown }).__ftgsGpuUnpack = gpuUnpackStats;
                }
                gofStream = new GofStream(binSegmentUrl(dataset, 0), {
                    onGpuSegment: gpuUnpacker ? (chunk) => {
                        const { ref, encodeMs, reuse } = gpuUnpacker.unpack(chunk.frames, chunk.plan, chunk.reuse);
                        chunk.timings.gpuEncodeMs = encodeMs;
                        gpuUnpackStats.chunks++;
                        gpuUnpackStats.encodeMs.push(encodeMs);
                        if (reuse) {
                            // C안 2단계: GPU 재사용 적중 수 (비교 모드면 CPU 적중 수와 대조)
                            const cpuHits = chunk.reuse?.cpuHits;
                            reuse.hits.then((hits) => {
                                if (cpuHits !== undefined && cpuHits !== hits) {
                                    gpuUnpackStats.reuseMismatch++;
                                }
                                const line = `chunk ${chunk.index}: GPU reuse ${hits} gaussians kept (${reuse.chain}` +
                                    `${reuse.chain === 'handoff' ? `, previous CPU chunk uploaded in ${reuse.handoffMs.toFixed(1)} ms` : ''}` +
                                    `${cpuHits !== undefined ? `, CPU ${cpuHits} ${cpuHits === hits ? 'same' : 'DIFFERS'}` : ''})`;
                                gpuUnpackStats.reuseLines.push(line);
                                console.info(`[ftgs] gpu unpack: ${line}`);
                            }).catch((e: unknown) => console.warn(`[ftgs] gpu unpack: chunk ${chunk.index} reuse count read failed - ${(e as Error).message}`));
                        }
                        const records = chunk.records;
                        if (records) {
                            gpuUnpacker.readback(ref).then((gpu) => {
                                const r = compareGpuRecords(gpu, records, chunk.header.vertexCount);
                                gpuUnpackStats.compared++;
                                if (r.exact) {
                                    gpuUnpackStats.exact++;
                                }
                                const line = `chunk ${chunk.index}: GPU vs CPU ${r.exact ? 'IDENTICAL' : 'differs'} - ${r.line}`;
                                gpuUnpackStats.lines.push(line);
                                console.info(`[ftgs] gpu unpack: ${line}`);
                            }).catch((e: unknown) => console.warn(`[ftgs] gpu unpack: chunk ${chunk.index} readback failed - ${(e as Error).message}`));
                        }
                        return { header: chunk.header, buffer: new ArrayBuffer(chunk.header.dataByteOffset), gpu: ref };
                    } : undefined,
                    onProbeFrame: (probe) => {
                        if (!gpuLumaProbe) {
                            const stream = gofStream;
                            gpuLumaProbe = new GpuLumaProbe(rawGpuDevice,
                                (bytes, stride, width, rows, codedHeight) => stream.probeHash(bytes, stride, width, rows, codedHeight),
                                (e) => {
                                    if (frameTrace && frameTrace.events.length < frameTrace.max) {
                                        frameTrace.events.push({ t: performance.now(), kind: 'gpuProbe', index: e.index, video: e.video, encodeMs: e.encodeMs, doneMs: e.doneMs });
                                    }
                                });
                            (window as unknown as { __ftgsGpuProbe?: unknown }).__ftgsGpuProbe = gpuLumaProbe;
                        }
                        gpuLumaProbe.process(probe);
                    },
                    onProgress: (received, total) => {
                        if (downloadDone) {
                            return;
                        }
                        if (received >= total) {
                            downloadDone = true;
                            downloading = false;
                            breadcrumbDetail('수신', `완료 ${(total / 1e6).toFixed(1)} MB`);
                            if (decodeNotice) {
                                showDecodeNotice();
                            } else {
                                downloadProgressBar.complete();
                            }
                            return;
                        }
                        downloading = true;
                        downloadProgressBar.update(received, total, label);
                        breadcrumbDetail('수신', `${(received / 1e6).toFixed(1)}/${(total / 1e6).toFixed(1)} MB`);
                    },
                    onChunk: (index, t) => {
                        if (frameTrace && frameTrace.events.length < frameTrace.max) {
                            frameTrace.events.push({ t: performance.now(), kind: 'chunk', index, decodeMs: t.decodeMs, unpackMs: t.unpackMs, waitMs: t.waitMs });
                        }
                        console.log(`[ftgsDebug] [${dataset.id}] chunk ${index + 1}/${dataset.segmentCount} decoded: ` +
                            `WebCodecs ${t.decodeMs.toFixed(0)} ms, ${t.gpu ? `GPU unpack (tables ${t.unpackMs.toFixed(0)} ms in the worker, main thread ${(t.gpuEncodeMs ?? 0).toFixed(1)} ms)` : `unpack ${t.unpackMs.toFixed(0)} ms`}` +
                            (t.reuse ? `, reuse ${t.reuse.ms.toFixed(0)} ms (${t.reuse.hits} gaussians kept)` : '') +
                            (t.predecodeMs ? `, pre-decode ${t.predecodeMs.toFixed(0)} ms` : '') +
                            (t.verifyMs ? `, luma MD5 ${t.verifyMs.toFixed(0)} ms` : '') +
                            (t.waitMs >= 1 ? ` (waited ${t.waitMs.toFixed(0)} ms for its bytes)` : ''));
                        if (decodeNotice === null) {
                            decodeNotice = `HW Decoding Time: ${(t.decodeMs / 1000).toFixed(2)} s · ` +
                                `Unpacking: ${(t.unpackMs / 1000).toFixed(2)} s`;
                            showDecodeNotice();
                        }
                    },
                    onOpened: ({ fromCache, fileSize, gaussians }) => {
                        // every buffer this dataset will need, against this
                        // device's WebGPU limits, before any is created
                        // (ftgs/gpu-issues.ts); the total goes into the
                        // abnormal-exit record
                        const wgpu = (scene.graphicsDevice as unknown as { wgpu?: GPUDevice }).wgpu;
                        if (wgpu) {
                            const padded = Math.max(1024, 2 ** Math.ceil(Math.log2(Math.max(1, gaussians))));
                            const plan = checkGpuPlan(wgpu, [
                                { label: '가우시안 데이터(청크 1개)', bytes: gaussians * VSEI_PROPERTY_NAMES.length * 4 },
                                { label: '깊이 정렬 키·인덱스(각)', bytes: padded * 4 },
                                { label: '타일 래스터 투영 데이터', bytes: gaussians * 64 },
                                { label: '타일 래스터 쌍 목록', bytes: FTGS_TILE_MAX_PAIRS * 4 },
                                { label: '화면 출력 버퍼', bytes: Math.max(1, scene.graphicsDevice.width) * Math.max(1, scene.graphicsDevice.height) * 4 }
                            ]);
                            breadcrumbDetail('GPU', plan);
                        }
                        breadcrumbDetail('가우시안', `${gaussians}개/청크`);
                        if (fromCache) {
                            console.log(`[ftgsDebug] [${dataset.id}] ${label}: compressed bitstream from the OPFS cache (${(fileSize / 1e6).toFixed(1)} MB) - no download, chunks still decoded live`);
                        }
                    },
                    onCached: (stored) => {
                        console.log(`[ftgsDebug] [${dataset.id}] ${label}: download complete${stored ? ', stored in the OPFS cache for the next visit' : ' (not cached)'}`);
                    },
                    onError: (error) => {
                        console.error(`[ftgsDebug] [${dataset.id}] bitstream stream failed:`, error);
                        downloadProgressBar.hide();
                    },
                    onStage: (index, stage) => {
                        breadcrumbStage(`${dataset.id} 청크 ${index}: ${stage ?? '완료'}`);
                    },
                    onPredecode: (done, total) => {
                        // Android's split schedule: the small videos are decoded
                        // before the first chunk can show - ride the bar on it
                        downloadProgressBar.update(done, total, `${label} · Pre-decoding`);
                        if (done >= total) {
                            downloadProgressBar.complete();
                        }
                    },
                    onDecodePath: ({ lumaPath, schedule, hevcInput, renderer }) => {
                        breadcrumbDetail('디코딩 경로', `luma ${lumaPath}, schedule ${schedule}, HEVC input ${hevcInput}`);
                        console.log(`[ftgsDebug] [${dataset.id}] decode path: luma ${lumaPath}, schedule ${schedule}, HEVC input ${hevcInput}${renderer ? ` (${renderer})` : ''}`);
                    }
                }, {
                    // unpaced decode (flush + one copyTo per picture) is the
                    // default since 2026-10-02: the paced strip readback
                    // stuttered on a 4K 60 Hz monitor while unpaced was
                    // smooth on both it and the 240 Hz laptop panel (see
                    // gof-stream.worker.ts). ?ftgsPacedDecode=1 brings the
                    // paced readback back for A/B comparison.
                    paced: new URLSearchParams(window.location.search).get('ftgsPacedDecode') === '1',
                    // 재생 중 청크는 flush 없이 디코딩하고 그림이 나온 뒤 N ms 기다려 한 번에 꺼냄
                    // (gof-stream.worker.ts). 2026-10-31 노트북 Chrome 실측: 매초 끊김이 교체 1회당
                    // 61 → 24 ms (N=120), 대기 효과는 120 ms 근처에서 포화. 측정한 PC Chrome(Edge 등
                    // Chromium 데스크톱)에서만 기본으로 켜고, 모바일·Safari 는 기존 flush 방식 유지.
                    // ?ftgsNoFlush=N 으로 대기 시간 지정(모든 환경), ?ftgsNoFlush=off 로 끔.
                    noFlushSettleMs: ((v) => {
                        if (v === 'off') {
                            return undefined;
                        }
                        if (v !== null && v !== '' && Number(v) >= 0) {
                            return Number(v);
                        }
                        const ua = navigator.userAgent;
                        return /Chrome\/\d+/.test(ua) && !/Android|Mobile/i.test(ua) ? 120 : undefined;
                    })(new URLSearchParams(window.location.search).get('ftgsNoFlush')),
                    // 재생 중 청크의 영상 3장을 동시에 말고 하나씩, 사이에 N ms 쉬며 디코딩
                    // (gof-stream.worker.ts decodePlanes). 2026-10-31 노트북 Chrome 실측(flush 없음
                    // 120 ms 위에서): 11 ms 넘는 프레임이 교체 1회당 1.0 → 0.1개, 최대 23 → 12 ms.
                    // flush 없는 방식과 같이 PC Chromium 에서만 기본 100 ms, ?ftgsDecodeGapMs=N 으로
                    // 바꾸고 0 으로 끔.
                    decodeGapMs: ((v) => {
                        if (v !== null && v !== '' && Number(v) >= 0) {
                            return Number(v);
                        }
                        const ua = navigator.userAgent;
                        return /Chrome\/\d+/.test(ua) && !/Android|Mobile/i.test(ua) ? 100 : 0;
                    })(new URLSearchParams(window.location.search).get('ftgsDecodeGapMs')),
                    // each chunk's unpack is split across 3 workers by
                    // default (2026-10-02: median 153 -> 102 ms on makeup;
                    // more workers gained little); ?ftgsUnpackWorkers=N
                    // overrides it, 0/1 = inline
                    unpackWorkers: Number(new URLSearchParams(window.location.search).get('ftgsUnpackWorkers') ?? 3),
                    // ETRI reuse_shared_attributes (makeup-v3's decoder,
                    // 2026-10-07) - only for datasets that declare it, so
                    // the original makeup decodes exactly as before;
                    // ?ftgsReuseAttributes=0 turns it off for A/B
                    reuseAttributes,
                    // Android (2026-10-12): RGBA decoder frames are read back
                    // through WebGL2 and the small videos are pre-decoded
                    // (gof-stream.worker.ts); both are chosen automatically
                    // from the first frame / the platform and can be forced
                    // for A/B: ?ftgsLumaPath=copyto|webgl|webgl-limited, ?ftgsDecodeSchedule=concurrent|split
                    lumaPath: (['copyto', 'webgl', 'webgl-limited'].find(x => x === new URLSearchParams(window.location.search).get('ftgsLumaPath')) ?? 'auto') as 'auto' | 'copyto' | 'webgl' | 'webgl-limited',
                    schedule: (['concurrent', 'split'].find(x => x === new URLSearchParams(window.location.search).get('ftgsDecodeSchedule')) ?? 'auto') as 'auto' | 'concurrent' | 'split',
                    // iPad Safari (2026-10-14): the input form WebCodecs gets
                    // (gof-decode/hevc-input.ts) changes on its own when the
                    // stored form will not decode; forced for A/B with
                    // ?ftgsHevcInput=annexb|annexb-fixed|hvcc-fixed|hvcc-fixed-l51
                    hevcInput: (['annexb', 'annexb-fixed', 'hvcc-fixed', 'hvcc-fixed-l51'].find(x => x === new URLSearchParams(window.location.search).get('ftgsHevcInput')) ?? 'auto') as 'auto' | 'annexb' | 'annexb-fixed' | 'hvcc-fixed' | 'hvcc-fixed-l51',
                    verifyChunks,
                    gpuProbe: gpuProbeOn,
                    gpuUnpack: gpuUnpackOn,
                    gpuUnpackVerify
                });
                abortSignal?.addEventListener('abort', () => gofStream?.destroy(), { once: true });
                (window as unknown as { __ftgsGofStream?: unknown }).__ftgsGofStream = gofStream;
            } else if (dataset.source === 'bin') {
                // the REAL bitstream path, same `/uploads/`-stripped form the
                // ply path's label uses - a laddered dataset fetches
                // `<variant>/bitstream.bin`, not the flat `segment_NNN.bin`
                // this used to hardcode, and showing a filename that doesn't
                // exist makes it impossible to tell which variant is loading.
                //
                // The clip counter is dropped for a single-clip dataset (every
                // ladder folder holds exactly one bitstream, so it would read a
                // constant "(1/1)"): a fraction that never moves carries no
                // information and reads as if it were the same kind of thing as
                // the detail's own "Decoding 4/5", which does move.
                const clipLabel = (index: number) => {
                    const path = binSegmentUrl(dataset, index).replace(/^\/uploads\//, '');
                    return dataset.segmentCount > 1 ? `${path} (${index + 1}/${dataset.segmentCount})` : path;
                };
                binPrepare = prepareBinDataset(dataset, {
                    signal: abortSignal,
                    onProgress: (index, _phase, fraction, detail) => {
                        downloadProgressBar.update(fraction * 1000, 1000, `${clipLabel(index)} — ${detail}`);
                    },
                    onSegmentReady: (index, fromCache, ms, decodeMs, unpackMs, writeMs) => {
                        console.log(`[ftgsDebug] [${dataset.id}] clip ${index + 1}/${dataset.segmentCount} ${fromCache ? 'already decoded (OPFS)' : `prepared in ${(ms / 1000).toFixed(1)} s (SW decoding ${(decodeMs / 1000).toFixed(1)} s, unpack+write ${((unpackMs ?? 0) / 1000).toFixed(2)} s on ${binPrepare?.stats.poolSize ?? 1} workers)`}`);
                        if (!fromCache) {
                            // once the bar has faded out, the same spot briefly
                            // reads the software HEVC decode time and the unpack
                            // time, each on its own (download and the OPFS write
                            // excluded) - see the bar's complete()
                            const clipNo = dataset.segmentCount > 1 ? ` (${index + 1}/${dataset.segmentCount})` : '';
                            const unpackOnlyMs = Math.max(0, (unpackMs ?? 0) - (writeMs ?? 0));
                            downloadProgressBar.complete(`SW Decoding Time${clipNo}: ${(decodeMs / 1000).toFixed(1)} s · Unpacking: ${(unpackOnlyMs / 1000).toFixed(1)} s`);
                        }
                    },
                    onSegmentError: (index, error) => {
                        issues.report(classifyLoadError(`bin-clip:${dataset.id}:${index}`, `클립 ${index} 디코딩(10-bit)`, error,
                            ['클립 하나가 실패하면 현재는 재시도하지 않으므로, 재생이 이 클립 앞에서 멈춥니다.']));
                        console.error(`[ftgsDebug] [${dataset.id}] clip ${index} decode failed:`, error);
                        downloadProgressBar.hide();
                    },
                    onAllDone: (stats) => {
                        const decoded = stats.clips.filter(c => c.status === 'ready' && !c.fromCache).length;
                        const cached = stats.clips.filter(c => c.fromCache).length;
                        const failed = stats.clips.filter(c => c.status === 'error').length;
                        console.log(`[ftgsDebug] [${dataset.id}] prepare done in ${(stats.totalMs / 1000).toFixed(1)} s ` +
                            `(first clip ${(stats.firstClipMs / 1000).toFixed(1)} s, ${decoded} decoded, ${cached} from OPFS, ${failed} failed, ${stats.poolSize} workers)`);
                    }
                });
                // debug hook: prepare timing + OPFS storage management
                // (docs/p2-browser-bin-decode-design.md 5: decodes are
                // 128 MB per clip, so the origin's OPFS grows by GBs across
                // datasets; automatic LRU eviction lives in bin-prepare's
                // ensureOpfsSpace - this is the manual view/wipe)
                (window as unknown as { __ftgsBinPrepare?: unknown }).__ftgsBinPrepare = {
                    stats: () => binPrepare?.stats,
                    opfsUsage: async () => {
                        const usage = await listOpfsUsage();
                        const estimate = navigator.storage?.estimate ? await navigator.storage.estimate() : null;
                        return {
                            datasets: usage.map(u => ({ ...u, MB: +(u.bytes / 1e6).toFixed(1), lastDecodedAt: u.lastDecodedAt ? new Date(u.lastDecodedAt).toISOString() : null })),
                            totalMB: +(usage.reduce((a, u) => a + u.bytes, 0) / 1e6).toFixed(1),
                            originUsageMB: estimate ? +((estimate.usage ?? 0) / 1e6).toFixed(1) : null,
                            originQuotaMB: estimate ? +((estimate.quota ?? 0) / 1e6).toFixed(1) : null
                        };
                    },
                    clear: (id?: string) => (id ? deleteDatasetDecodes(id) : deleteAllDecodes())
                };
                try {
                    await binPrepare.whenReady(0);
                } catch (error) {
                    binPrepare.destroy();
                    throw error;
                }
            }

            // Bootstrap load of segment 0 (bounds/framing need its bytes
            // before anything else can come up) - runs BEFORE the
            // SegmentManager (whose onProgress wiring normally drives the
            // download bar) exists, so it drives the bar directly: on first
            // ever contact this is the single longest wait the user hits
            // (the buffering gate holds playback for exactly this file),
            // and it used to pass with no feedback at all. Cache-served
            // (re-visit) loads keep the bar hidden, same as everywhere
            // else (see segment-manager.ts's servedFromCache handling).
            const initialLabel = manifest[0].url.replace(/^\/uploads\//, '');
            let initialReceived = 0;
            let initialTotal = 0;
            let initialFromCache = false;
            // a GOF stream shows its own (whole-bitstream) download progress
            // and must not outlive a failed bootstrap
            const segment = gofStream ? await gofStream.load(0, abortSignal).catch((error: Error) => {
                gofStream.destroy();
                throw error;
            }) : await loadSegmentStreaming(manifest[0].url, {
                // cancellation Rule: switching datasets DURING this
                // bootstrap download aborts it (the manager-owned loads all
                // abort via destroy(), but this one predates the manager -
                // observed live: cart's segment 0 kept downloading
                // alongside makeup's after a mid-bootstrap switch)
                signal: abortSignal,
                onHeader: (header, fromCache) => {
                    initialFromCache = fromCache;
                    if (initialFromCache) {
                        return;
                    }
                    initialTotal = header.dataByteOffset + header.vertexCount * header.strideBytes;
                    downloadProgressBar.update(initialReceived, initialTotal, initialLabel);
                },
                onChunk: (_byteOffset, bytes) => {
                    if (initialFromCache) {
                        return;
                    }
                    initialReceived += bytes.byteLength;
                    downloadProgressBar.update(initialReceived, initialTotal, initialLabel);
                }
            });
            if (!gofStream && !initialFromCache) {
                downloadProgressBar.complete();
            }
            // the download itself rejects with an AbortError when cancelled
            // mid-flight; this catches the raciest edge (abort landing
            // between the last chunk and the promise resolving) so a
            // superseded session never builds its scene state
            if (abortSignal?.aborted) {
                throw new DOMException('dataset load superseded', 'AbortError');
            }

            const view = new DataView(segment.buffer, segment.header.dataByteOffset);
            const stride = segment.header.strideBytes;
            const f = (i: number, off: number) => view.getFloat32(i * stride + off, true);
            let minX = Infinity; let maxX = -Infinity;
            let minY = Infinity; let maxY = -Infinity;
            let minZ = Infinity; let maxZ = -Infinity;
            const n = segment.header.vertexCount;
            const sampleStep = Math.max(1, Math.floor(n / 20000));
            const sampledX: number[] = []; const sampledY: number[] = []; const sampledZ: number[] = [];
            for (let i = 0; i < n; i += sampleStep) {
                const x = f(i, 0); const y = f(i, 4); const z = f(i, 8);
                if (x < minX) minX = x; if (x > maxX) maxX = x;
                if (y < minY) minY = y; if (y > maxY) maxY = y;
                if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
                sampledX.push(x); sampledY.push(y); sampledZ.push(z);
            }
            const extent = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 1);

            // CAMERA FRAMING target: median center + 90th-percentile*1.6
            // radius, matching freetime_viewer_demo/freetime_viewer/
            // segment.py's median_center()/bbox_center_radius() EXACTLY -
            // NOT the naive (min+max)/2 + full-span `extent` above (which
            // stays as the AABB used for culling/clip-planes, where being
            // conservative/inclusive is correct). A raw min/max average is
            // extremely outlier-sensitive - background clutter, exterior
            // scenery visible through windows, stray reconstruction
            // floaters, etc. all drag it around and can put the camera at a
            // distance/angle dominated by junk far outside the actual room
            // content.
            const median = (arr: number[]) => {
                const sorted = arr.slice().sort((a, b) => a - b);
                return sorted[Math.floor(sorted.length / 2)];
            };
            const medianCenter = new Vec3(median(sampledX), median(sampledY), median(sampledZ));
            const distFromMedian = sampledX.map((_, i) => Math.hypot(
                sampledX[i] - medianCenter.x, sampledY[i] - medianCenter.y, sampledZ[i] - medianCenter.z
            )).sort((a, b) => a - b);
            const p90Dist = distFromMedian[Math.floor(distFromMedian.length * 0.90)];
            const framingRadius = Math.max(0.5, p90Dist * 1.6);
            (window as unknown as { __ftgsBounds?: unknown }).__ftgsBounds = { minX, maxX, minY, maxY, minZ, maxZ, extent };

            // Console helper for stereo tuning (2026-10-21):
            // __ftgsDepthStats() - how far the first segment's splats lie in
            // front of the CURRENT camera, in scene units, as percentiles over
            // the splats inside the view (same ~20k sample as the framing
            // above; opacity > 0.1 only, so faint floaters don't stretch the
            // range). Eye Distance / Convergence Dist are picked from these
            // depths. Positions get the same D() flip as the framing below.
            {
                const opacityOff = VSEI_LAYOUT.opacity * 4;
                const depthSamples: [number, number, number][] = [];
                for (let i = 0; i < n; i += sampleStep) {
                    // stored as a logit, like the shader's sigmoid(readF(...))
                    if (1 / (1 + Math.exp(-f(i, opacityOff))) > 0.1) {
                        depthSamples.push([f(i, 0), -f(i, 4), -f(i, 8)]);
                    }
                }
                (window as any).__ftgsDepthStats = () => {
                    const cam = scene.camera.mainCamera;
                    const p = cam.getPosition(); const fw = cam.forward; const rt = cam.right; const up = cam.up;
                    const fov = scene.camera.camera.fov * Math.PI / 180;
                    const horizontal = scene.camera.camera.horizontalFov;
                    const aspect = scene.camera.camera.aspectRatio;
                    const tanH = horizontal ? Math.tan(fov / 2) : Math.tan(fov / 2) * aspect;
                    const tanV = horizontal ? Math.tan(fov / 2) / aspect : Math.tan(fov / 2);
                    const depths: number[] = [];
                    for (const [x, y, z] of depthSamples) {
                        const dx = x - p.x; const dy = y - p.y; const dz = z - p.z;
                        const d = dx * fw.x + dy * fw.y + dz * fw.z;
                        if (d <= 0) {
                            continue;
                        }
                        const sx = (dx * rt.x + dy * rt.y + dz * rt.z) / d;
                        const sy = (dx * up.x + dy * up.y + dz * up.z) / d;
                        if (Math.abs(sx) <= tanH && Math.abs(sy) <= tanV) {
                            depths.push(d);
                        }
                    }
                    depths.sort((a, b) => a - b);
                    const at =(q: number) => Number(depths[Math.min(depths.length - 1, Math.floor(q * depths.length))]?.toFixed(3));
                    return {
                        dataset: dataset.id, inView: depths.length, sampled: depthSamples.length,
                        p1: at(0.01), p5: at(0.05), p10: at(0.1), p25: at(0.25), p50: at(0.5), p75: at(0.75), p90: at(0.9), p95: at(0.95), p99: at(0.99)
                    };
                };
            }

            // debug-only: check whether the "thin diagonal streak" artifact
            // is explained by outlier splats with anomalously huge world-
            // space scale (float index of scale_0 comes from the dataset's
            // record layout - splat-layout.ts; values are stored log-scale,
            // real scale = exp(raw), same as the shader's `exp(readF(...))`).
            {
                const scaleByteOff = VSEI_LAYOUT.scale * 4;
                let maxScale = -Infinity; let maxScaleIdx = -1;
                const scaleSamples: number[] = [];
                for (let i = 0; i < n; i += sampleStep) {
                    const s0 = Math.exp(f(i, scaleByteOff));
                    const s1 = Math.exp(f(i, scaleByteOff + 4));
                    const s2 = Math.exp(f(i, scaleByteOff + 8));
                    const smax = Math.max(s0, s1, s2);
                    scaleSamples.push(smax);
                    if (smax > maxScale) { maxScale = smax; maxScaleIdx = i; }
                }
                scaleSamples.sort((a, b) => a - b);
                const pct = (p: number) => scaleSamples[Math.min(scaleSamples.length - 1, Math.floor(p * scaleSamples.length))];
                const scaleStats = {
                    count: scaleSamples.length,
                    median: pct(0.5),
                    p95: pct(0.95),
                    p99: pct(0.99),
                    p999: pct(0.999),
                    max: maxScale,
                    maxIdx: maxScaleIdx
                };
                (window as unknown as { __ftgsScaleStats?: unknown }).__ftgsScaleStats = scaleStats;
            }

            const node = new FtgsSplatSceneNode(graphicsDevice, scene.app.root);
            // a newly opened dataset starts at its dataset.json rotation; a
            // rebuild of the same one (rate/source switch) keeps the panel's
            const fileRotation = datasetRootRotation(dataset);
            const rootRotation = rootRotationDatasetId === dataset.id ? stereoControls.rootRotation : fileRotation;
            rootRotationDatasetId = dataset.id;
            stereoControls.setRootRotationFileValue(fileRotation);
            stereoControls.setRootRotation(rootRotation);
            node.setRootRotation(rootRotation.x, rootRotation.y, rootRotation.z);
            rootRotationNode = node;
            // per-dataset opt-in, off unless dataset.json asks for it - see
            // the field's doc on FtgsSplatSceneNode for what it changes, what
            // it costs in VRAM, and why only a per-frame PLY sequence needs it
            node.residentSegments = dataset.residentSegments === true;
            if (node.residentSegments) {
                node.setResidentSegment(0, segment);
            } else {
                node.setSegment(segment);
            }
            node.addToLayer(scene.worldLayer);
            let loadedSegmentIndex = 0;
            // identity of the SegmentData currently uploaded to the GPU -
            // the swap check compares against THIS (not just the index) so
            // a same-index, better-tier arrival still triggers a re-upload
            let loadedSegmentData = segment;
            // completed-data-is-kept Rule: on a tier switch, the outgoing
            // manager's fully-buffered segments live on here and keep
            // serving playback (per-index, old tier) until the new tier's
            // download for that index replaces them - so switching quality
            // never pauses a running scene. null until the first switch.
            let fallbackSegments: Map<number, typeof segment> | null = null;

            // Multi-segment streaming: SegmentManager owns fetch + prefetch-
            // window orchestration (FTGSC_FORMAT_SPEC.md 4.2) across all of
            // this dataset's segments. Re-fetches index 0 once more here
            // (it's a small dev-local file, not worth a seed-with-already-
            // loaded-data mechanism just to skip one duplicate request) -
            // the update loop's swap check below is a no-op for it since
            // `loadedSegmentIndex` already starts at 0.
            // `let` + factory rather than a single const: a Model Quality
            // tier switch (setQualityTier below) swaps the whole manager for
            // one pointing at the other tier's URLs. destroy() makes the old
            // manager ignore its own in-flight results, so old-tier data can
            // never land in the new manager's cache - no per-request
            // generation tracking needed.
            // Which in-flight download the bar shows. Several segments can
            // be loading concurrently (prefetchDepth, the initial burst -
            // and on a slow connection, e.g. mobile, they overlap
            // essentially all the time), so "latch onto whichever reports
            // progress first" made the bar visibly jump backwards on every
            // switch to a less-complete concurrent download. Instead, keep
            // the latest byte counts for EVERY in-flight download and always
            // display the one playback is actually waiting on: the loading
            // segment circularly closest ahead of the active index. The
            // display target then only changes for a meaningful reason (the
            // segment it showed finished, or playback moved on), not by
            // chunk-arrival race.
            const inflightProgress = new Map<number, { received: number, total: number }>();
            const displayedProgressIndex = () => {
                let best = -1;
                let bestDist = Infinity;
                const n = dataset.segmentCount;
                const active = Math.max(0, segmentManager ? segmentManager.activeIndex : 0);
                for (const idx of inflightProgress.keys()) {
                    const dist = ((idx - active) % n + n) % n;
                    if (dist < bestDist) {
                        bestDist = dist;
                        best = idx;
                    }
                }
                return best;
            };
            const refreshProgressBar = () => {
                const idx = displayedProgressIndex();
                if (idx === -1) {
                    downloadProgressBar.complete();
                    return;
                }
                const p = inflightProgress.get(idx)!;
                // "cart/segment_000.ply"-style readout under the bar:
                // the manifest URL is `/uploads/<id>[/low]/segment_NNN.ply`
                // (see buildManifest), so stripping the /uploads/
                // prefix leaves exactly the folder+filename form -
                // including the /low/ tier directory when active,
                // which usefully shows WHICH tier is downloading
                downloadProgressBar.update(p.received, p.total,
                    manifest[idx].url.replace(/^\/uploads\//, ''));
            };
            const createSegmentManager = () => new SegmentManager(manifest, {
                // GOF datasets: segments are decoded chunks, built in memory
                loadIndexed: gofStream ? (index, signal) => gofStream.load(index, signal) : undefined,
                // bin datasets: don't request a clip until its browser-side
                // decode has landed in OPFS (it would 404 + retry otherwise)
                canLoad: (index) => {
                    // Resident mode: this segment's buffer is on the GPU for
                    // the life of the dataset, so re-downloading it would put
                    // back exactly the per-swap traffic residency exists to
                    // remove. Guarded by the flag, so it is inert elsewhere.
                    if (node.residentSegments && node.hasResidentSegment(index)) {
                        return false;
                    }
                    // unchanged behaviour for every other dataset: bin
                    // datasets gate on their own decode, everything else has
                    // no gate at all (`true` here == the `undefined` this
                    // used to pass, see SegmentManager's `this.canLoad &&`)
                    return binPrepare ? binPrepare.isReady(index) : true;
                },
                onError: (index, error) => {
                    console.error(`[ftgsDebug] [${dataset.id}] segment ${index} failed to load:`, error);
                    // one entry per segment however often its 2 s retry fails
                    issues.report(classifyLoadError(`segment:${dataset.id}:${index}`, `청크 ${index} 로딩`, error,
                        [`재생 위치의 청크 ${segmentManager ? segmentManager.activeIndex : '?'}, 2초 뒤 자동 재시도`]));
                    if (inflightProgress.delete(index)) {
                        refreshProgressBar();
                    }
                },
                onReady: (index) => {
                    issues.resolve(`segment:${dataset.id}:${index}`, '재시도 후 로딩됨');
                    if (inflightProgress.delete(index)) {
                        refreshProgressBar();
                    }
                },
                onCancelled: (index) => {
                    // aborted on purpose (seek out of the window) - drop the
                    // dead download's bar entry so it doesn't sit frozen
                    if (inflightProgress.delete(index)) {
                        refreshProgressBar();
                    }
                },
                onProgress: (index, receivedBytes, totalBytes) => {
                    inflightProgress.set(index, { received: receivedBytes, total: totalBytes });
                    if (index === displayedProgressIndex()) {
                        refreshProgressBar();
                    }
                }
            });
            let segmentManager = createSegmentManager();

            // register with the Scene as a proper Element - see
            // ftgs-splat-scene-element.ts's module doc for why this matters:
            // without it, scene.bound (and therefore fitClippingPlanes()'s
            // near/far AND the orbit camera's sceneRadius-based distance
            // scaling) never learns this object exists at all, no matter
            // how correct its own MeshInstance custom AABB is.
            const sceneElement = new FtgsSplatSceneElement(node);
            await scene.add(sceneElement);
            sceneElement.invalidateBound();
            // force scene.bound's lazy getter to recompute NOW (rather than
            // whenever something else happens to read it next) - this ALSO
            // fires 'scene.boundChanged', which camera.ts subscribes to in
            // order to update its own cached `sceneRadius` field. That
            // subscription doesn't reliably fire for this out-of-band
            // (non-Splat) Element registration path, so it's set directly
            // right below too.
            void scene.bound;
            scene.camera.sceneRadius = Math.max(1e-3, scene.bound.halfExtents.length());

            // Initial camera: try to match the reference's ACTUAL startup
            // view exactly, the same way freetime_viewer_demo does
            // (viewport.py's _apply_scene: colmap_io.build_hero_camera(),
            // no extra zoom/pan on top) - the real COLMAP hero-camera
            // (dataset.heroCamera, e.g. 'c006') position, looking at
            // seg.median_center(). Falls back to the bounds/percentile
            // auto-framing heuristic below for datasets without a usable
            // images.txt/cameras.txt hero pose.
            const D = (v: Vec3) => new Vec3(v.x, -v.y, -v.z);
            // one directory listing up front (see dataset-listing.ts) so
            // the optional-file readers below - camera pose, stereo presets
            // - request only what the folder ships instead of probing every
            // candidate name; null on a host without listings = probe as before
            const listing = await fetchDatasetListing(datasetBaseUrl(dataset.id));
            // dataset.json's `cameraSpawn` is already a WORLD position (see its field
            // doc) - it needs neither the COLMAP -R^T*t inversion nor D, and
            // makes the camera file unnecessary, so don't even fetch one.
            const spawnEye = dataset.cameraSpawn ? new Vec3(...dataset.cameraSpawn) : null;
            // dataset.json's `cameraTarget` - the WORLD look-at point (see its
            // field doc). Checked here since dataset.json is hand-edited: a
            // bad value is reported and the median-centre aim kept.
            const target = dataset.cameraTarget;
            const spawnTarget = Array.isArray(target) && target.length === 3 && target.every(n => Number.isFinite(n)) ?
                new Vec3(target[0], target[1], target[2]) : null;
            if (target !== undefined && !spawnTarget) {
                console.warn(`[ftgs] [${dataset.id}] ignoring dataset.json cameraTarget - needs three numbers [x, y, z]:`, target);
            }
            const heroPose = spawnEye ? null : await loadHeroCameraPose(datasetBaseUrl(dataset.id), dataset.heroCamera, listing);
            // the scene-units factor starts from the default for each
            // dataset (no rig-radius auto-calibration since 2026-10-21 -
            // see resetSceneUnits); its presets may then set their own
            resetSceneUnits(dataset.id);
            // stereo presets for THIS dataset (its own file if it ships one,
            // else the global ones)
            applyStereoPresets(await fetchDatasetStereoPresets(
                dataset.id, await globalStereoPresets, builtinStereoPreset, listing
            ));
            stereoPresetDatasetId = dataset.id;
            stereoControls.setSaveEnabled(true);
            if (spawnEye || heroPose) {
                // FOV must be assigned BEFORE setPose(), not after - see
                // editor.ts:882's own comment on this exact ordering
                // requirement: setPose() computes its internal relative
                // distance as `l / sceneRadius * fovFactor`, and fovFactor
                // is derived from the camera's CURRENT fov at call time.
                if (heroPose?.fxNative && heroPose.camW) {
                    // SIMPLE_PINHOLE has one focal for both axes (square
                    // pixels) - convert horizontal-focal-at-camW into a
                    // vertical FOV in degrees using the COLMAP camera's own
                    // native height (1080, matching this dataset's 1920x1080
                    // captures) - FOV is a pure angle, independent of our
                    // own viewport's resolution/aspect.
                    const nativeHeight = 1080;
                    const vFovRad = 2 * Math.atan((nativeHeight / 2) / heroPose.fxNative);
                    cam.fov = vFovRad * (180 / Math.PI);
                }
                // D only applies to the COLMAP path - `cameraSpawn` is world already
                const eye = spawnEye ?? D(new Vec3(...heroPose.center));
                // Default target is the splats' median centre. `heroCameraOrientation`
                // (opt-in per dataset, see its field doc) instead aims along the hero
                // camera's own COLMAP view direction, to reproduce a reference
                // player's startup view - keeping the SAME eye-to-target distance so
                // the orbit pivot stays where the default framing would have put it.
                // Unset, or without a hero pose to read a direction from, this is
                // exactly the `D(medianCenter)` it replaced. dataset.json's
                // `cameraTarget` (world, no D) wins over the median centre -
                // unless it sits on the eye, which leaves no direction.
                const lookAt = spawnTarget && spawnTarget.distance(eye) > 1e-6 ? spawnTarget : D(medianCenter);
                if (spawnTarget && lookAt !== spawnTarget) {
                    console.warn(`[ftgs] [${dataset.id}] ignoring dataset.json cameraTarget - it is the camera position itself`);
                }
                scene.camera.setPose(eye, lookAt, 0);
                // setPose() only sets the camera CONTROLLER's target
                // state - it needs several real app.tick()s before the
                // actual camera transform (position/near/far) catches up.
                for (let i = 0; i < 10; i++) {
                    scene.app.tick();
                }
            } else {
                console.log(`[ftgsDebug] [${dataset.id}] initial camera: hero pose not found, falling back to bounds/percentile framing`);
                scene.camera.focus({ focalPoint: D(medianCenter), radius: framingRadius, speed: 0 });
            }

            // Reset Camera (stats panel button / R key, 2026-11-04): the
            // WORLD pose the framing above settled on, restored with no
            // damping like the startup framing itself. Kept as position +
            // target rather than the controller's distance, which is relative
            // to sceneRadius - that grows as the splats stream in, so the
            // same distance would land farther out later. The exact-COLMAP
            // branch below swaps in its unlocked hand-over pose instead.
            const initialFov = scene.camera.camera.fov;
            const initialTarget = scene.camera.focalPoint;
            const initialPosition = new Vec3();
            Camera.calcForwardVec(initialPosition, scene.camera.azim, scene.camera.elevation);
            initialPosition.mulScalar(scene.camera.distance * scene.camera.sceneRadius / scene.camera.fovFactor).add(initialTarget);
            let restoreInitialCamera = () => {
                // fov before setPose() - see the hero-pose block above
                scene.camera.camera.fov = initialFov;
                scene.camera.setPose(initialPosition, initialTarget, 0);
            };

            // listener registration for the camera-unlock below; only the
            // opt-in branch ever assigns it, and teardown aborts it so a
            // dataset switch can't leave a stale listener behind
            let releaseCameraLockAbort: AbortController | null = null;
            let snapshotKeyAbort: AbortController | null = null;

            // `heroCameraOrientation` (opt-in per dataset): reproduce the hero
            // camera's OWN view - optical axis, roll and focal length - which
            // is what the C++ gsplat-player renders, instead of the hero-pose
            // framing above that stands in the same place but aims at the
            // splats' median centre. Deliberately overrides that framing; see
            // docs/renderer-crosscheck-design.md 4.2 for why setPose() cannot
            // express it (azim/elev carries no roll) and poseOverride can.
            if (dataset.heroCameraOrientation && heroPose) {
                const exact = colmapExactPose(heroPose);
                // direction-independent clipping planes, same derivation as
                // the 360 capture path
                const boundRadius = scene.bound.halfExtents.length();
                const dist = new Vec3().sub2(scene.bound.center, exact.position).length();
                const far = dist + boundRadius;
                const near = Math.max(1e-6, dist < boundRadius ? far / (1024 * 16) : dist - boundRadius);
                // this app's camera runs with horizontalFov = true on any
                // landscape viewport, so `fov` is the HORIZONTAL angle there -
                // feeding it the vertical one narrows the view to 18.2deg
                // against the C++ player's 31.8 (that doc's "most expensive
                // trap"). Pick the one matching the camera's current mode.
                const horizontal = scene.camera.camera.horizontalFov;
                const fov = horizontal ? exact.hFovDeg : exact.vFovDeg;
                scene.camera.setPoseOverride({ position: exact.position, rotation: exact.rotation, fov, near, far });
                console.log(`[ftgsDebug] [${dataset.id}] exact COLMAP camera "${dataset.heroCamera}" applied ` +
                    `(pos=${exact.position.toString()}, fov=${fov.toFixed(4)}deg ${horizontal ? 'horizontal' : 'vertical'}, ` +
                    `hFov=${exact.hFovDeg.toFixed(4)} vFov=${exact.vFovDeg.toFixed(4)}). Released on first camera input.`);

                // An override LOCKS the camera, which is right for a pixel
                // comparison and wrong for looking around - so hand control
                // back the moment the user actually reaches for it.
                //
                // Releasing has to HAND OVER the pose, not just drop it: the
                // controller is still holding the median-centre framing from
                // setPose() above, so a bare setPoseOverride(null) would snap
                // the view somewhere else. Re-seating it on the exact pose
                // first makes the handover invisible apart from the roll,
                // which azim/elev genuinely cannot carry (2.75 deg here).
                // fov before setPose(), for the reason the block above states.
                const handOverExactPose = () => {
                    const fwd = exact.rotation.transformVector(new Vec3(0, 0, -1));
                    scene.camera.camera.fov = fov;
                    scene.camera.setPose(exact.position, new Vec3().add2(exact.position, fwd.mulScalar(dist)), 0);
                    scene.camera.setPoseOverride(null);
                };
                const releaseCameraLock = () => {
                    if (!scene.camera.poseOverride) {
                        return;
                    }
                    handOverExactPose();
                    console.log(`[ftgsDebug] [${dataset.id}] camera unlocked on user input (roll no longer applied)`);
                };
                // Reset Camera returns to the unlocked hand-over pose (same
                // place and direction, no roll) - it never re-locks
                restoreInitialCamera = handOverExactPose;
                // Bound to the element the camera controller itself listens on
                // (camera.ts's `#canvas-container`), so clicking the playback
                // bar, the picker or any other UI does NOT count as reaching
                // for the camera. Keyboard is deliberately excluded too - Space
                // is play/pause.
                const cameraInputTarget = document.getElementById('canvas-container');
                releaseCameraLockAbort = new AbortController();
                for (const evt of ['pointerdown', 'wheel']) {
                    cameraInputTarget?.addEventListener(evt, releaseCameraLock, {
                        signal: releaseCameraLockAbort.signal,
                        once: true,
                        passive: true
                    });
                }
            }

            resetCameraToInitial = () => {
                restoreInitialCamera();
                console.log(`[ftgsDebug] [${dataset.id}] camera reset to the initial pose`);
            };

            // ---- K: renderer cross-check capture (opt-in per dataset) -----
            // Walks a spread of this dataset's COLMAP cameras, renders each at
            // that camera's OWN pose and focal length, and hands the frames to
            // the dev server (POST /__snapshot), which writes them beside the
            // C++ player's captures as ftgs-supersplat-N.png. Registered ONLY
            // when the dataset asks for it, so for every other dataset the key
            // does not exist at all.
            if (dataset.colmapSnapshotKey) {
                const postRender = () => new Promise<void>((resolve) => {
                    const handle = scene.events.on('postrender', () => {
                        handle.off();
                        resolve();
                    });
                });

                const snapshotKey = async (e: KeyboardEvent) => {
                    // Shift+K since 2026-10-26: plain K is now the turn-in-place
                    // look-down key (shortcut-manager.ts camera.look.down).
                    // e.code, so it also works with the Korean IME active.
                    if (e.code !== 'KeyK' || !e.shiftKey) {
                        return;
                    }
                    if (e.ctrlKey || e.metaKey || e.altKey) {
                        return;
                    }
                    const target = e.target as HTMLElement | null;
                    if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) {
                        return;
                    }
                    e.preventDefault();
                    e.stopPropagation();

                    // Where to write. A page cannot write to a path of its own
                    // choosing, so the File System Access API asks the operator
                    // for the folder - point it at references/Compare/snapshots
                    // and the captures land beside the C++ player's own. Chrome
                    // reopens the picker where it last landed for this `id`, and
                    // the handle is kept for the session (declared outside
                    // startFtgsDataset), so this prompt appears once, not once
                    // per dataset and not once per capture.
                    //
                    // Called BEFORE any other await: showDirectoryPicker needs
                    // the user activation this keypress carries, and an earlier
                    // await would have spent it.
                    if (typeof (window as any).showDirectoryPicker !== 'function') {
                        console.error('[ftgs] snapshot: this browser has no File System Access API (Chrome/Edge only)');
                        return;
                    }
                    try {
                        if (snapshotDir) {
                            // a handle kept from earlier in the session can have
                            // had its permission revoked (site data cleared, or
                            // the user said no) - re-ask rather than throwing
                            // mid-capture
                            const state = await snapshotDir.queryPermission({ mode: 'readwrite' });
                            if (state !== 'granted' && await snapshotDir.requestPermission({ mode: 'readwrite' }) !== 'granted') {
                                snapshotDir = null;
                            }
                        }
                        if (!snapshotDir) {
                            snapshotDir = await (window as any).showDirectoryPicker({
                                id: 'ftgs-snapshots',
                                mode: 'readwrite'
                            });
                            console.log(`[ftgs] snapshot folder set to "${snapshotDir.name}" for this session`);
                        }
                    } catch (err) {
                        // dismissing the picker is a cancel, not a failure
                        if ((err as DOMException)?.name !== 'AbortError') {
                            console.error('[ftgs] snapshot: could not open the output folder:', err);
                        }
                        return;
                    }

                    // ?ftgsSnapshotSize=WxH overrides; the default matches the
                    // C++ player's gsplat_render_width/height so the two
                    // captures compare without resampling either one.
                    const sizeParam = new URLSearchParams(window.location.search).get('ftgsSnapshotSize');
                    const parsedSize = /^(\d+)x(\d+)$/.exec(sizeParam ?? '');
                    const snapWidth = parsedSize ? Number(parsedSize[1]) : 1920;
                    const snapHeight = parsedSize ? Number(parsedSize[2]) : 1080;

                    const canvas = scene.canvas;
                    const prevWidth = canvas.width;
                    const prevHeight = canvas.height;
                    const resized = prevWidth !== snapWidth || prevHeight !== snapHeight;
                    const savedOverride = scene.camera.poseOverride;
                    const savedFov = scene.camera.camera.fov;
                    let name = '(batch)';
                    try {
                        // every COLMAP camera in images.txt order - the same
                        // order the C++ player's camera_index counts
                        const camNames = await loadColmapCameraNames(datasetBaseUrl(dataset.id), listing);
                        if (camNames.length === 0) {
                            throw new Error('images.txt has no cameras');
                        }
                        // A subset spread EVENLY across the rig rather than the
                        // first N: a coordinate-frame error usually only shows
                        // from certain directions, so covering the span is what
                        // makes the cross-check worth running.
                        const limitParam = Number(new URLSearchParams(window.location.search).get('ftgsSnapshotCameras') ?? '9');
                        const limit = Number.isFinite(limitParam) && limitParam > 0 ?
                            Math.min(Math.floor(limitParam), camNames.length) : camNames.length;
                        const picks: number[] = limit >= camNames.length ?
                            camNames.map((_unused, i) => i) :
                            Array.from({ length: limit }, (_unused, i) => (limit === 1 ? 0 :
                                Math.round(i * (camNames.length - 1) / (limit - 1))));

                        if (resized) {
                            // consumed by applyCanvasResize() on the next
                            // pre-render - the same channel the ResizeObserver
                            // uses, so no layout fight
                            scene.canvasResize = { width: snapWidth, height: snapHeight };
                            scene.forceRender = true;
                            await postRender();
                        }

                        const flat = document.createElement('canvas');
                        flat.width = canvas.width;
                        flat.height = canvas.height;
                        const ctx = flat.getContext('2d');
                        if (!ctx) {
                            throw new Error('2d context unavailable');
                        }

                        console.log(`[ftgs] batch snapshot: ${picks.length} of ${camNames.length} cameras at ${flat.width}x${flat.height} (indices ${picks.join(',')})`);

                        for (let k = 0; k < picks.length; k++) {
                            const i = picks[k];
                            // the N in the file name is the CAPTURE ORDER, not
                            // the camera index - the C++ player numbers its
                            // files the same way over the same cameras in the
                            // same order, so N is the same viewpoint on both
                            name = `ftgs-supersplat-${k}.png`;
                            const pose = await loadHeroCameraPose(datasetBaseUrl(dataset.id), camNames[i], listing);
                            if (!pose) {
                                console.warn(`[ftgs] ${name}: camera "${camNames[i]}" not found - skipped`);
                                continue;
                            }
                            const ex = colmapExactPose(pose);
                            const boundRadius = scene.bound.halfExtents.length();
                            const camDist = new Vec3().sub2(scene.bound.center, ex.position).length();
                            const snapFar = camDist + boundRadius;
                            const snapNear = Math.max(1e-6, camDist < boundRadius ? snapFar / (1024 * 16) : camDist - boundRadius);
                            scene.camera.setPoseOverride({
                                position: ex.position,
                                rotation: ex.rotation,
                                fov: scene.camera.camera.horizontalFov ? ex.hFovDeg : ex.vFovDeg,
                                near: snapNear,
                                far: snapFar
                            });
                            // two frames: the override lands on update, then
                            // the splats re-sort for the new view
                            scene.forceRender = true;
                            await postRender();
                            scene.forceRender = true;
                            await postRender();

                            // flatten onto black - the canvas carries alpha
                            // while the C++ side writes opaque RGB over black
                            ctx.fillStyle = '#000';
                            ctx.fillRect(0, 0, flat.width, flat.height);
                            ctx.drawImage(canvas, 0, 0);

                            const px = ctx.getImageData(0, 0, flat.width, flat.height).data;
                            let nonBlack = 0;
                            for (let q = 0; q < px.length; q += 4) {
                                if (px[q] > 8 || px[q + 1] > 8 || px[q + 2] > 8) {
                                    nonBlack++;
                                }
                            }
                            const blob: Blob = await new Promise((resolve, reject) => {
                                flat.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob returned null'))), 'image/png');
                            });
                            const fileHandle = await snapshotDir.getFileHandle(name, { create: true });
                            const writable = await fileHandle.createWritable();
                            await writable.write(blob);
                            await writable.close();
                            const pct = (nonBlack / (flat.width * flat.height) * 100).toFixed(1);
                            console.log(`  [${k}] ${name}  camera ${i} (${camNames[i]})  non-black ${pct}%${nonBlack === 0 ? '  <-- BLACK' : ''}`);
                        }
                        console.log('[ftgs] batch snapshot done.');
                    } catch (err) {
                        console.error(`[ftgs] snapshot FAILED (${name}):`, err);
                    } finally {
                        scene.camera.camera.fov = savedFov;
                        scene.camera.setPoseOverride(savedOverride);
                        if (resized) {
                            scene.canvasResize = { width: prevWidth, height: prevHeight };
                            scene.forceRender = true;
                        }
                    }
                };
                snapshotKeyAbort = new AbortController();
                window.addEventListener('keydown', snapshotKey, { capture: true, signal: snapshotKeyAbort.signal });
                console.log(`[ftgsDebug] [${dataset.id}] press Shift+K to capture the COLMAP cross-check snapshots`);
            }

            let elapsed = 0;
            let frames = 0;
            // Resort only when the camera actually moved AND at most
            // RESORT_MIN_INTERVAL apart - see the eponymous constants'
            // history in ftgs-webgpu-splat-integration memory (bugs 8/9):
            // an exact view-matrix comparison never throttled anything
            // (float jitter never repeats bit-identically), and this
            // deployment's displays can run well past 200Hz, so even a
            // working motion-gate alone still isn't enough.
            // next-segment pre-upload budget per tick: a makeup segment
            // (191,808 x 256 B = 49 MB) lands in ~6 ticks, well inside the
            // >= 0.65 s it sits decoded before its swap (2026-10-02 trace)
            const STAGE_UPLOAD_BYTES_PER_TICK = 8 * 1024 * 1024;
            const VIEW_EPSILON = 1e-4;
            const RESORT_MIN_INTERVAL = 1 / 20;
            let lastView: number[] | null = null;
            let lastResortTime = -Infinity;

            let hudTicks = 0;
            let hudResorts = 0;
            let hudRenders = 0;
            let hudWindowStart = 0;
            let hudLastLine = '';

            // ?ftgsNoResort=1 - diagnostic escape hatch: disable ALL
            // resorting after the very first frame.
            const noResort = new URLSearchParams(window.location.search).has('ftgsNoResort');
            let everSorted = false;

            // while __ftgsGpuBench (below) is measuring, the normal per-tick
            // driver must go fully quiet - any forceRender/resort it issued
            // between the bench's awaits would land on the same GPU queue
            // being timed and contaminate the numbers.
            let benchMode = false;

            // ?ftgsRenderHz=N - diagnostic: cap how often we actually
            // trigger a draw (scene.forceRender), independent of resort.
            const renderHzParam = new URLSearchParams(window.location.search).get('ftgsRenderHz');
            const renderMinInterval = renderHzParam ? 1 / Number(renderHzParam) : 0;
            let lastRenderTime = -Infinity;

            // Playback clock: real wall-clock advance + wrap-on-overflow,
            // matching freetime_viewer_demo/freetime_viewer/playback/
            // clock.py's PlaybackClock.advance() exactly (elapsed %=
            // loop_duration, not a hard reset-to-start, so a long single
            // frame hitch doesn't cause a visible jump-back), and its
            // absolute-zero-origin convention (scene/manager.py: t_offset=
            // 0.0, loop_duration=segmentCount*segmentDuration). The rate
            // comes from playbackTiming (see its doc), resolved here from
            // this dataset's config; ?ftgsPaused=1 starts paused.
            Object.assign(playbackTiming, resolvePlaybackTiming(dataset.playback));
            reflectPlaybackTiming();
            // dataset.startPaused is the per-dataset default (see its field
            // doc); the URL override keeps working for every dataset, and
            // with the flag unset this reads exactly as it always did.
            let paused = dataset.startPaused === true ||
                new URLSearchParams(window.location.search).has('ftgsPaused');
            const loopDuration = dataset.segmentCount * segmentDuration;
            let playT = 0;
            let pendingResortAfterSwap = false;

            playbackBar.onTogglePlay = () => {
                paused = !paused;
            };
            playbackBar.onSeek = (t: number) => {
                // wrap into [0, loopDuration) the same way the clock's own
                // advance-and-wrap does, so dragging past either end behaves
                // like scrubbing a looping video rather than clamping
                playT = ((t % loopDuration) + loopDuration) % loopDuration;
            };

            // Model Quality tier switch, live during playback: swap the
            // SegmentManager for one whose manifest points at the other
            // tier's directory. `playT` lives in this closure (not in the
            // manager), so playback time carries over untouched; the
            // update loop below then fetches the new tier's segment for the
            // current time and swaps the GPU buffer once it's ready - until
            // then playback CONTINUES on the old tier via fallbackSegments
            // (the swap check compares data identity, not index, so the
            // same-index new-tier arrival still replaces it).
            const setQualityTier = (tier: 'high' | 'low') => {
                if (tier === qualityTier || (tier === 'low' && !lowAvailable)) {
                    return;
                }
                qualityTier = tier;
                saveTierChoice(tier);
                manifest = buildManifest(tier);
                // completed-data-is-kept Rule: everything the outgoing
                // manager fully buffered becomes fallback render data (see
                // fallbackSegments' doc) - merged over any survivors of a
                // PREVIOUS switch (rapid high->low->high: an index the
                // middle tier never re-downloaded still has the oldest
                // tier's data, which beats freezing on it)
                const snapshot = segmentManager.snapshotReady();
                if (fallbackSegments) {
                    for (const [index, data] of fallbackSegments) {
                        if (!snapshot.has(index)) {
                            snapshot.set(index, data);
                        }
                    }
                }
                fallbackSegments = snapshot;
                // destroy() also aborts the old tier's in-flight downloads
                // (cancellation Rule) so the new tier gets the whole link
                segmentManager.destroy();
                segmentManager = createSegmentManager();
                // destroy() makes the OLD manager silently swallow its
                // in-flight results (see its own doc), so if the bar was
                // mid-flight for a segment on the old manager it would
                // otherwise sit there frozen forever - nothing will ever
                // call onReady/onError for that index again
                inflightProgress.clear();
                downloadProgressBar.hide();
                segmentManager.setTime(playT);
                console.log(`[ftgsDebug] [${dataset.id}] model quality -> ${tier}`);
            };
            // Confirm the badges against the session that actually came up
            // (they deliberately don't move their own highlight on click -
            // see ui/ftgs-source-badges.ts). For a laddered dataset these
            // fields were resolved by withVariant() below; for a legacy one
            // they are whatever dataset.json declared, defaulting to
            // BIN / Gauss / r0.
            sourceBadges.configure({
                source: dataset.source,
                quant: dataset.quantization,
                rate: dataset.rate,
                rates: dataset.rates,
                sources: dataset.sources,
                quants: dataset.quantizations
            });

            // The Model Quality row was removed from the stats overlay (same
            // request). The tier switch itself still works - kept reachable
            // from the console alongside the other __ftgs* debug hooks so the
            // low/ tier stays testable without a UI for it.
            (window as unknown as { __ftgsQualityTier?: (tier: 'high' | 'low') => void }).__ftgsQualityTier = setQualityTier;

            // Issue panel: playback held by the buffering gate (the segment
            // for the current time is not ready) for longer than this is
            // reported with what it is waiting on. Counted from frame dt,
            // clamped per frame, so time with the page hidden (no frames)
            // does not count as a stall.
            const PLAYBACK_STALL_MS = 3000;
            const stallKey = `playback-stall:${dataset.id}`;
            let stallMs = 0;
            let stallReported = false;
            const reportPlaybackStall = async (index: number, ms: number) => {
                const status = segmentManager.status(index);
                const cause = status === 'error' ?
                    `청크 ${index} 로딩이 실패해 2초마다 다시 시도하고 있습니다. 실패 원인은 같은 패널의 "청크 ${index} 로딩" 항목에 있습니다.` :
                    status === 'loading' ?
                        `재생에 필요한 청크 ${index}가 아직 준비되지 않아(수신 또는 디코딩 중) 화면이 마지막 장면에서 기다리고 있습니다. 근거의 단계 정보가 어디서 기다리는지 보여 줍니다.` :
                        binPrepare && !binPrepare.isReady(index) ?
                            `청크(클립) ${index}의 브라우저 디코딩(10-bit 경로)이 아직 끝나지 않았습니다.` :
                            `청크 ${index}의 로딩이 아직 시작되지 않았습니다. 앞 청크의 로딩이 끝나기를 기다리고 있습니다.`;
                const evidence = [
                    `재생 위치 ${playT.toFixed(2)} s, 필요한 청크 ${index} (상태 ${status})`,
                    `앞쪽 청크 상태: ${[0, 1, 2].map((d) => {
                        const i = (index + d) % manifest.length;
                        return `${i}:${segmentManager.status(i)}`;
                    }).join(' ')}`
                ];
                if (gofStream) {
                    evidence.push(`비트스트림 수신 ${gofStream.stats.received}/${gofStream.stats.total} B`, ...(await gofStream.diag()).split('\n'));
                }
                if (!stallReported) {
                    // playback resumed while the diag was in flight
                    return;
                }
                issues.report({
                    key: stallKey, severity: 'warn', title: `재생이 ${(ms / 1000).toFixed(0)}초 넘게 멈춤 (청크 ${index} 대기)`, cause, evidence
                });
            };

            const updateHandler = (dt: number) => {
                if (benchMode) {
                    return;
                }
                const traceStart = frameTrace ? performance.now() : 0;
                let traceSwap = false;
                elapsed += dt;
                frames++;

                // frame timings (and the FPS derived from them) are
                // measured by the app-level frameupdate/framerender/frameend
                // hooks up where statsOverlay is created - they're a
                // property of the engine's frame, not of this dataset's
                // playback, so they keep working across dataset switches
                // and while nothing is loaded at all
                if (statsOverlay.visible) {
                    // the look-at point as the camera actually uses it this
                    // frame (the tween's current value, like the position)
                    statsOverlay.update(readFrameTimings(), scene.camera.mainCamera.getPosition(), editorUI.canvas, node.splatCount, scene.camera.focalPointTween.value);
                }

                // Ensure the segment covering the CURRENT playT (plus the
                // prefetch window ahead) is loading/loaded, then swap the
                // live GPU buffer to it once ready and different from what's
                // currently shown. The clock does NOT free-run: it only
                // advances while the segment covering playT is actually
                // buffered (see the `desiredData` gate below). Over a remote
                // link (WireGuard/VPN access became a real scenario on
                // 2026-08-31) a free-running clock permanently outran the
                // sequential downloads - every segment arrived after playback
                // had already moved past it, so the very first buffer swap
                // never happened and the screen stayed black through the
                // whole first loop.
                segmentManager.setTime(playT);
                const desiredIndex = segmentIndexAtTime(manifest, playT);
                // current-tier data first; a fully-buffered old-tier segment
                // (kept across a tier switch, see fallbackSegments) fills in
                // until the current tier's download for this index lands -
                // at which point the fallback entry is dropped for good
                const currentTierData = segmentManager.getSegment(desiredIndex);
                if (currentTierData && fallbackSegments) {
                    fallbackSegments.delete(desiredIndex);
                    if (fallbackSegments.size === 0) {
                        fallbackSegments = null;
                    }
                }
                let desiredData = currentTierData ?? fallbackSegments?.get(desiredIndex) ?? null;
                // GPU 언팩 청크(SegmentData.gpu)의 칸이 이미 다른 청크로 덮였으면 없는 것으로 봄
                // (돌려 쓰는 칸이 창보다 많아 차례대로 재생할 때는 일어나지 않음)
                if (desiredData?.gpu && !desiredData.gpu.valid()) {
                    console.warn(`[ftgs] gpu unpack: segment ${desiredIndex}'s GPU slot was reused before it played - skipped`);
                    desiredData = null;
                }
                if (node.residentSegments) {
                    // RESIDENT MODE ONLY (see FtgsSplatSceneNode.residentSegments).
                    // A segment already uploaded can be shown with no CPU-side
                    // data in hand at all - SegmentManager has long since
                    // evicted it - so the swap is gated on the index changing
                    // rather than on a new buffer object arriving. The `else
                    // if` below is the original path, unchanged, and is what
                    // every dataset without the flag still runs.
                    if (desiredIndex !== loadedSegmentIndex && node.setResidentSegment(desiredIndex, desiredData)) {
                        loadedSegmentIndex = desiredIndex;
                        loadedSegmentData = desiredData;
                        pendingResortAfterSwap = true;
                        sceneElement.invalidateBound();
                    }
                } else if (desiredData && desiredData !== loadedSegmentData) {
                    node.setSegment(desiredData);
                    traceSwap = true;
                    loadedSegmentIndex = desiredIndex;
                    loadedSegmentData = desiredData;
                    pendingResortAfterSwap = true;
                    sceneElement.invalidateBound();
                }
                if (!node.residentSegments && manifest.length > 1) {
                    // upload the NEXT segment a slice per tick while this one
                    // plays, so its swap is a rebind instead of a whole-
                    // segment upload + allocation in one frame (see
                    // FtgsSplatSceneNode.stageSegment)
                    const nextData = segmentManager.getSegment((desiredIndex + 1) % manifest.length);
                    if (nextData && nextData !== loadedSegmentData && (!nextData.gpu || nextData.gpu.valid())) {
                        node.stageSegment(nextData, STAGE_UPLOAD_BYTES_PER_TICK);
                    }
                }

                // buffering gate: advance the clock only while the segment
                // covering the current playT is buffered. On first load this
                // holds playback at t=0 until segment 0 arrives (then starts
                // cleanly from the top); mid-playback it freezes - YouTube
                // style, still showing the last-swapped buffer - whenever a
                // slow link falls behind, and resumes the moment the segment
                // lands. Tier switches and seeks get the same treatment for
                // free since they too just change which segment is desired.
                // Resident mode adds one more reason a frame is available:
                // it is already on the GPU. With the flag off this is exactly
                // the `desiredData` truthiness test it replaced.
                const frameAvailable = desiredData !== null ||
                    (node.residentSegments && node.hasResidentSegment(desiredIndex));
                if (!paused && !frameAvailable) {
                    stallMs += Math.min(dt, 0.1) * 1000;
                    if (stallMs >= PLAYBACK_STALL_MS && !stallReported) {
                        stallReported = true;
                        reportPlaybackStall(desiredIndex, stallMs);
                    }
                } else if (!paused && stallMs > 0) {
                    if (stallReported) {
                        issues.resolve(stallKey, `${(stallMs / 1000).toFixed(1)}초 만에 재생 재개`);
                    }
                    stallMs = 0;
                    stallReported = false;
                }
                if (!paused && frameAvailable) {
                    playT += dt * contentRate(playbackTiming);
                    if (playT >= loopDuration || playT < 0) {
                        playT = ((playT % loopDuration) + loopDuration) % loopDuration;
                    }
                }
                playbackBar.update(playT, loopDuration, !paused);
                const pd = cam.projectionMatrix.data;
                // stereo split view: each eye draws inside a half-width
                // hardware viewport, so every pixel-unit quantity (focal
                // length, viewport size for the shader's NDC conversions)
                // must be computed against the PER-EYE width, not the full
                // canvas. Both eyes share fov/aspect, so one set of
                // material uniforms still serves both draws.
                const eyeWidth = scene.camera.splitEnabled ? graphicsDevice.width / 2 : graphicsDevice.width;
                const focalX = pd[0] * (eyeWidth / 2);
                const focalY = pd[5] * (graphicsDevice.height / 2);
                const viewData = Array.from(cam.viewMatrix.data);
                const viewChanged = !lastView || viewData.some((v, i) => Math.abs(v - lastView![i]) > VIEW_EPSILON);
                const intervalOk = elapsed - lastResortTime >= RESORT_MIN_INTERVAL;
                let doResort = viewChanged && intervalOk;
                if (!sortLimit.enabled) {
                    // Enable Sort Limit OFF (stats panel): re-sort every
                    // tick like the reference player, view change or not -
                    // no stale ordering during fast/head-tracked motion
                    doResort = true;
                }
                if (noResort) {
                    doResort = !everSorted;
                }
                if (pendingResortAfterSwap) {
                    // a segment swap invalidates the previous sort result
                    // outright (sortedIndices/keys were computed against the
                    // OLD GPU buffer's splat count/positions) - force a
                    // resort this tick regardless of the throttle/no-resort
                    // flags above, which only make sense for "same buffer,
                    // camera moved" resorts.
                    doResort = true;
                    pendingResortAfterSwap = false;
                }
                if (viewChanged) {
                    lastView = viewData;
                }
                if (doResort) {
                    lastResortTime = elapsed;
                    everSorted = true;
                }
                // While the segment covering playT hasn't finished loading
                // (loop wrap before the prefetch caught up, a seek, a tier
                // switch, or a slow network), the OLD segment's buffer is
                // what actually draws - evaluating it at a time far outside
                // its own coverage velocity-extrapolates every long-lived
                // splat into a full-screen smear (dt of up to
                // -loopDuration at the wrap). Clamp the shader time to the
                // loaded segment's own [startTime, endTime] instead: a
                // brief freeze on its nearest-in-loop-order boundary frame
                // until the right segment swaps in.
                let renderT = playT;
                if (loadedSegmentIndex >= 0 && loadedSegmentIndex !== segmentIndexAtTime(manifest, playT)) {
                    const loadedEntry = manifest[loadedSegmentIndex];
                    const segStart = loadedEntry.startTime;
                    const segEnd = loadedEntry.startTime + loadedEntry.duration;
                    if (playT < segStart || playT > segEnd) {
                        // pick the boundary that's circularly closer along
                        // the loop (just past the end -> hold the end frame,
                        // approaching from behind -> hold the start frame)
                        const distToStart = (((segStart - playT) % loopDuration) + loopDuration) % loopDuration;
                        const distPastEnd = (((playT - segEnd) % loopDuration) + loopDuration) % loopDuration;
                        renderT = distPastEnd <= distToStart ? segEnd : segStart;
                    }
                }
                // presentation rate (playbackTiming.presentHz): snap the
                // shader's scene time onto the wall-clock presentation grid,
                // so redraws between two steps repeat the same scene state.
                // Applied after the clamp above so a held boundary frame
                // stays held.
                renderT = presentedSceneTime(renderT, playbackTiming);
                node.update(cam.viewMatrix, {
                    time: renderT,
                    focalX,
                    focalY,
                    resort: doResort,
                    viewportWidth: eyeWidth,
                    viewportHeight: graphicsDevice.height
                });
                if (frameTrace && frameTrace.frames.length < frameTrace.max) {
                    frameTrace.frames.push({
                        t: traceStart, dt, playT, renderT, ms: performance.now() - traceStart,
                        seg: loadedSegmentIndex, resort: doResort, swap: traceSwap, avail: frameAvailable
                    });
                }

                const doRender = !renderHzParam || (elapsed - lastRenderTime >= renderMinInterval);
                if (doRender) {
                    lastRenderTime = elapsed;
                    scene.forceRender = true;
                }

                hudTicks++;
                if (doResort) {
                    hudResorts++;
                }
                if (doRender) {
                    hudRenders++;
                }
                if (elapsed - hudWindowStart >= 1) {
                    hudLastLine = `[${dataset.id}] t=${elapsed.toFixed(1)}s ticks/s=${hudTicks} resorts/s=${hudResorts} renders/s=${hudRenders} gpuMaxMs=${gpuMaxLatencyMs.toFixed(0)} near=${cam.nearClip.toFixed(3)} far=${cam.farClip.toFixed(1)}${noResort ? ' [NORESORT MODE]' : ''}${renderHzParam ? ` [RENDER CAP ${renderHzParam}Hz]` : ''}\nplayback ${playT.toFixed(2)}/${loopDuration.toFixed(2)}s seg=${loadedSegmentIndex}${paused ? ' [PAUSED]' : ''} [${playbackTiming.playbackFps}/${playbackTiming.sourceFps}fps -> ${playbackTiming.presentHz || 'cont.'}Hz]`;
                    gpuMaxLatencyMs = 0;
                    hudWindowStart = elapsed;
                    hudRenders = 0;
                    hudTicks = 0;
                    hudResorts = 0;
                }
                // live mesh-instance/material state - if this stays
                // "normal" throughout a blackout while GPU latency (above)
                // also stays normal, the draw call itself is legitimately
                // being asked to render nothing (or something is
                // corrupting realCount/sortedIndices), not a GPU stall.
                const mi = node.meshInstance;
                const stateLine = `visible=${mi.visible} instCount=${mi.instancingCount}`;
                hud.textContent = `${hudLastLine}\n${stateLine}${deviceLostMsg ? `\n${deviceLostMsg}` : ''}`;
            };
            scene.app.on('update', updateHandler);

            (window as unknown as { __ftgsLiveReady?: boolean }).__ftgsLiveReady = true;
            // only worth a console line when a debug-only URL flag actually
            // changes behavior from the default - a normal load stays quiet
            if (noResort || renderHzParam) {
                console.log(`[ftgsDebug] [${dataset.id}]${noResort ? ' NORESORT MODE ACTIVE' : ''}${renderHzParam ? ` RENDER CAP ${renderHzParam}Hz` : ''}`);
            }
            (window as unknown as { __ftgsLiveStats?: () => unknown }).__ftgsLiveStats = () => {
                const readCanvas = editorUI.canvas;
                const sample = document.createElement('canvas');
                sample.width = readCanvas.width;
                sample.height = readCanvas.height;
                const ctx2d = sample.getContext('2d')!;
                ctx2d.drawImage(readCanvas, 0, 0);
                const { data } = ctx2d.getImageData(0, 0, sample.width, sample.height);
                let nonBackground = 0;
                for (let i = 0; i < data.length; i += 4) {
                    if (data[i] > 8 || data[i + 1] > 8 || data[i + 2] > 8) {
                        nonBackground++;
                    }
                }
                return { dataset: dataset.id, frames, elapsed, nonBackgroundPixels: nonBackground };
            };
            // debug-only: per-segment load status snapshot - answers "did
            // only segment 0 ever actually finish downloading" without
            // digging through the Network tab. status() is 'idle' for any
            // index the manager never even tried to fetch, so a run of
            // 'loading'/'idle' behind a stuck 'ready' 0 is exactly the LAN-
            // bandwidth-can't-keep-up symptom described in this module's
            // "known simplification" doc comment above (playT keeps
            // advancing in real time regardless of load state).
            (window as unknown as { __ftgsSegmentStatus?: () => unknown }).__ftgsSegmentStatus = () => ({
                dataset: dataset.id,
                qualityTier,
                playT: Number(playT.toFixed(2)),
                loadedSegmentIndex,
                desiredSegmentIndex: segmentIndexAtTime(manifest, playT),
                segments: manifest.map(seg => ({ index: seg.index, status: segmentManager.status(seg.index), url: seg.url }))
            });
            // debug-only: position the camera at an arbitrary RAW-COLMAP-
            // frame position/target (same D=diag(1,-1,-1) correction applied
            // everywhere else a raw position needs to become a world
            // position).
            (window as unknown as { __ftgsSetRawPose?: (pos: [number, number, number], target: [number, number, number], ticks?: number, fovDeg?: number) => unknown }).__ftgsSetRawPose = (pos, target, ticks, fovDeg) => {
                const Draw = (v: [number, number, number]) => new Vec3(v[0], -v[1], -v[2]);
                scene.camera.setPose(Draw(pos), Draw(target), 0);
                if (fovDeg !== undefined) {
                    cam.fov = fovDeg;
                }
                for (let i = 0; i < (ticks ?? 8); i++) {
                    scene.app.tick();
                }
                const pd = cam.projectionMatrix.data;
                return {
                    camPos: scene.camera.mainCamera.getPosition().clone(),
                    near: cam.nearClip,
                    far: cam.farClip,
                    fovDeg: cam.fov,
                    focalXOverWidth: pd[0] * 0.5,
                    focalYOverHeight: pd[5] * 0.5
                };
            };
            // debug-only: drive the render loop manually (real rAF doesn't
            // fire in the sandboxed dev-tool browser tab - see memory) and
            // optionally move the camera to a given RELATIVE distance
            // (fraction of scene.camera.sceneRadius) from the focal point.
            // speed:0 snaps instantly instead of damping over several frames.
            (window as unknown as { __ftgsProbe?: (relDistance?: number, ticks?: number) => unknown }).__ftgsProbe = (relDistance?: number, ticks?: number) => {
                if (relDistance !== undefined) {
                    scene.camera.setDistance(relDistance, 0);
                }
                for (let i = 0; i < (ticks ?? 5); i++) {
                    scene.app.tick();
                }
                return {
                    near: cam.nearClip,
                    far: cam.farClip,
                    camPos: scene.camera.mainCamera.getPosition().clone(),
                    hud: hud.textContent
                };
            };
            // debug-only: attribute the frame budget between the bitonic
            // depth sort and the actual splat draw. Measures wall-clock from
            // submit to queue.onSubmittedWorkDone() on an otherwise-idle
            // queue (benchMode silences the per-tick driver for the
            // duration) - not a hardware timestamp query, but idle-queue
            // isolation is enough to attribute at this granularity. Three
            // phases: sort alone (compute submit, no render), full engine
            // frame with the splat draw, and the same frame with the splat
            // mesh hidden (baseline) - splat draw cost ~= the difference.
            // MUST run in the user's real browser at the real canvas size
            // (the sandboxed pane composites no pixels/0x0 canvas, so its
            // draw numbers are meaningless there - sort numbers are fine):
            //   await __ftgsGpuBench()
            (window as unknown as { __ftgsGpuBench?: (iterations?: number) => Promise<unknown> }).__ftgsGpuBench = async (iterations = 30) => {
                const drain = () => gpuDevice.queue.onSubmittedWorkDone();
                const summarize = (arr: number[]) => {
                    const s = [...arr].sort((a, b) => a - b);
                    const avg = s.reduce((a, b) => a + b, 0) / s.length;
                    return {
                        avgMs: +avg.toFixed(2),
                        medMs: +s[Math.floor(s.length / 2)].toFixed(2),
                        minMs: +s[0].toFixed(2),
                        maxMs: +s[s.length - 1].toFixed(2)
                    };
                };
                benchMode = true;
                try {
                    await drain();
                    const pd2 = cam.projectionMatrix.data;
                    const fx = pd2[0] * (graphicsDevice.width / 2);
                    const fy = pd2[5] * (graphicsDevice.height / 2);

                    // --- sort alone (one full bitonic sort submit per iteration) ---
                    const sortMs: number[] = [];
                    for (let i = 0; i < iterations; i++) {
                        const t0 = performance.now();
                        node.update(cam.viewMatrix, { time: playT, focalX: fx, focalY: fy, resort: true });
                        await drain();
                        sortMs.push(performance.now() - t0);
                    }

                    // --- full engine frame including the splat draw ---
                    const frameCpuMs: number[] = [];
                    const frameTotalMs: number[] = [];
                    for (let i = 0; i < iterations; i++) {
                        const t0 = performance.now();
                        scene.forceRender = true;
                        scene.app.tick();
                        frameCpuMs.push(performance.now() - t0);
                        await drain();
                        frameTotalMs.push(performance.now() - t0);
                    }

                    // --- baseline frame: identical, splat draw hidden ---
                    node.meshInstance.visible = false;
                    const baseTotalMs: number[] = [];
                    for (let i = 0; i < iterations; i++) {
                        const t0 = performance.now();
                        scene.forceRender = true;
                        scene.app.tick();
                        await drain();
                        baseTotalMs.push(performance.now() - t0);
                    }
                    node.meshInstance.visible = true;

                    const frame = summarize(frameTotalMs);
                    const base = summarize(baseTotalMs);
                    return {
                        canvas: `${graphicsDevice.width}x${graphicsDevice.height}`,
                        splats: node.meshInstance.instancingCount,
                        passes: scene.camera.camera.framePasses?.length ?? -1,
                        sort: summarize(sortMs),
                        frameWithSplats: frame,
                        frameCpuOnly: summarize(frameCpuMs),
                        frameBaseline: base,
                        splatDrawApproxMs: +(frame.medMs - base.medMs).toFixed(2)
                    };
                } finally {
                    benchMode = false;
                }
            };
            // debug-only: JS-side replica of the vertex shader's per-splat
            // covariance/radius math (ftgs-splat-scene.wgsl.ts), run against
            // the segment's real data + the CURRENT camera transform, to
            // get actual numeric distributions instead of guessing shader
            // constants blindly. Mirrors the WGSL exactly: quaternion ->
            // rotation, RS*RS^T world covariance, Jacobian perspective
            // projection, view/model composition, det clamp, radiusPx. Must
            // be re-synced whenever the real shader's per-splat math
            // changes, or its numbers silently go stale/misleading.
            (window as unknown as { __ftgsAnalyzeSplats?: (sampleCount?: number) => unknown }).__ftgsAnalyzeSplats = (sampleCount = 5000) => {
                const mm = node.node.getWorldTransform().data; // matrix_model, column-major
                const mv = cam.viewMatrix.data; // matrix_view, column-major
                const pd = cam.projectionMatrix.data;
                const focalX = pd[0] * (graphicsDevice.width / 2);
                const focalY = pd[5] * (graphicsDevice.height / 2);
                const modelRot: number[][] = [[mm[0], mm[1], mm[2]], [mm[4], mm[5], mm[6]], [mm[8], mm[9], mm[10]]];
                const viewRot: number[][] = [[mv[0], mv[1], mv[2]], [mv[4], mv[5], mv[6]], [mv[8], mv[9], mv[10]]];

                const mulM3V3 = (m: number[][], v: number[]) => [
                    m[0][0] * v[0] + m[1][0] * v[1] + m[2][0] * v[2],
                    m[0][1] * v[0] + m[1][1] * v[1] + m[2][1] * v[2],
                    m[0][2] * v[0] + m[1][2] * v[1] + m[2][2] * v[2]
                ];
                const mulM3M3 = (a: number[][], b: number[][]) => [mulM3V3(a, b[0]), mulM3V3(a, b[1]), mulM3V3(a, b[2])];
                const transposeM3 = (m: number[][]) => [[m[0][0], m[1][0], m[2][0]], [m[0][1], m[1][1], m[2][1]], [m[0][2], m[1][2], m[2][2]]];
                const mulM4Point = (m: Float32Array, p: number[]) => [
                    m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12],
                    m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13],
                    m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14]
                ];

                // matches ftgs-splat-scene.wgsl.ts's reference-tangent-clamp
                // math exactly - re-sync this every time the shader's
                // per-splat math changes.
                const tanFovX = 0.5 * graphicsDevice.width / focalX;
                const tanFovY = 0.5 * graphicsDevice.height / focalY;

                const mulM4Point4 = (m: Float32Array, p: number[]) => [
                    m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12] * p[3],
                    m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13] * p[3],
                    m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14] * p[3],
                    m[3] * p[0] + m[7] * p[1] + m[11] * p[2] + m[15] * p[3]
                ];

                const n2 = segment.header.vertexCount;
                const step = Math.max(1, Math.floor(n2 / sampleCount));
                let excludedNearClip = 0;
                let excludedDetNonPositive = 0;
                let excludedOffscreen = 0;
                let tangentClampTriggered = 0;
                const radiiX: number[] = [];
                const radiiY: number[] = [];
                const eccentricities: number[] = [];
                const depths: number[] = [];

                for (let i = 0; i < n2; i += step) {
                    const base = i * stride;
                    const pos = [view.getFloat32(base + 0, true), view.getFloat32(base + 4, true), view.getFloat32(base + 8, true)];
                    const s0raw = view.getFloat32(base + 12, true);
                    const s1raw = view.getFloat32(base + 16, true);
                    const s2raw = view.getFloat32(base + 20, true);
                    const q0 = view.getFloat32(base + 24, true);
                    const q1 = view.getFloat32(base + 28, true);
                    const q2 = view.getFloat32(base + 32, true);
                    const q3 = view.getFloat32(base + 36, true);
                    const vx = view.getFloat32(base + 244, true);
                    const vy = view.getFloat32(base + 248, true);
                    const vz = view.getFloat32(base + 252, true);
                    const timeCenter = view.getFloat32(base + 236, true);

                    const dt = 0.5 - timeCenter;
                    const meanT = [pos[0] + dt * vx, pos[1] + dt * vy, pos[2] + dt * vz];

                    const qlen = Math.max(Math.hypot(q0, q1, q2, q3), 1e-8);
                    const w = q0 / qlen; const x = q1 / qlen; const y = q2 / qlen; const z = q3 / qlen;
                    const rot: number[][] = [
                        [1 - 2 * (y * y + z * z), 2 * (x * y + w * z), 2 * (x * z - w * y)],
                        [2 * (x * y - w * z), 1 - 2 * (x * x + z * z), 2 * (y * z + w * x)],
                        [2 * (x * z + w * y), 2 * (y * z - w * x), 1 - 2 * (x * x + y * y)]
                    ];
                    const scale = [Math.exp(s0raw), Math.exp(s1raw), Math.exp(s2raw)];
                    const rs = [
                        [rot[0][0] * scale[0], rot[0][1] * scale[0], rot[0][2] * scale[0]],
                        [rot[1][0] * scale[1], rot[1][1] * scale[1], rot[1][2] * scale[1]],
                        [rot[2][0] * scale[2], rot[2][1] * scale[2], rot[2][2] * scale[2]]
                    ];
                    const sigma = mulM3M3(rs, transposeM3(rs));

                    const meanWorld = mulM4Point(mm, meanT);
                    const viewPos = mulM4Point(mv, meanWorld);
                    const depth = -viewPos[2];
                    depths.push(depth);
                    if (depth < 0.01) {
                        excludedNearClip++;
                        continue;
                    }

                    const invDepth = 1 / depth;
                    const invDepth2 = invDepth * invDepth;

                    // D/W composition-order fix (see ftgs-webgpu-splat-integration
                    // memory): camY flips sign (D's Y-flip applied post-hoc to our
                    // real view-space position), and the covariance rotation is
                    // modelRot*viewRot*modelRot (D*W*D conjugation), not
                    // viewRot*modelRot - re-synced here to match
                    // ftgs-splat-scene.wgsl.ts exactly.
                    const camY = -viewPos[1];
                    const rawTanX = viewPos[0] / depth;
                    const rawTanY = camY / depth;
                    const tx = depth * Math.max(-1.3 * tanFovX, Math.min(1.3 * tanFovX, rawTanX));
                    const ty = depth * Math.max(-1.3 * tanFovY, Math.min(1.3 * tanFovY, rawTanY));
                    if (tx !== viewPos[0] || ty !== camY) {
                        tangentClampTriggered++;
                    }

                    const jac: number[][] = [
                        [focalX * invDepth, 0, 0],
                        [0, focalY * invDepth, 0],
                        [-focalX * tx * invDepth2, -focalY * ty * invDepth2, 0]
                    ];

                    const t = mulM3M3(mulM3M3(mulM3M3(jac, modelRot), viewRot), modelRot);
                    const cov3 = mulM3M3(mulM3M3(t, sigma), transposeM3(t));
                    const covA = cov3[0][0] + 0.3;
                    const covB = cov3[0][1];
                    const covC = cov3[1][1] + 0.3;

                    const det = covA * covC - covB * covB;
                    if (det <= 0) {
                        excludedDetNonPositive++;
                        continue;
                    }
                    const mid = 0.5 * (covA + covC);
                    const discriminant = Math.max(0, mid * mid - det);
                    const lambdaMax = mid + Math.sqrt(discriminant);
                    const lambdaMin = Math.max(mid - Math.sqrt(discriminant), 1e-9);

                    const opacityLogit = view.getFloat32(base + 40, true); // OFF_OPACITY=10
                    const sigmaT = Math.exp(view.getFloat32(base + 240, true)); // OFF_DURATION=60
                    const dtNorm = dt / (sigmaT + 1e-8);
                    const opacityT = (1 / (1 + Math.exp(-opacityLogit))) * Math.exp(-0.5 * dtNorm * dtNorm);
                    const extend = Math.min(3.33, Math.sqrt(Math.max(0, 2 * Math.log(Math.max(opacityT, 1e-9) / (1 / 255)))));
                    const rX = Math.ceil(extend * Math.sqrt(covA));
                    const rY = Math.ceil(extend * Math.sqrt(covC));

                    // offscreen bounding-box cull, matching the shader's
                    // check (see ftgs-splat-scene.wgsl.ts).
                    const viewPos4 = mulM4Point4(mv, [meanWorld[0], meanWorld[1], meanWorld[2], 1]);
                    const clip = mulM4Point4(pd, viewPos4);
                    if (clip[3] <= 1e-6) {
                        excludedOffscreen++;
                        continue;
                    }
                    const ndcX = clip[0] / clip[3];
                    const ndcY = clip[1] / clip[3];
                    const pixelX = (ndcX * 0.5 + 0.5) * graphicsDevice.width;
                    const pixelY = (1 - (ndcY * 0.5 + 0.5)) * graphicsDevice.height;
                    if (pixelX + rX <= 0 || pixelX - rX >= graphicsDevice.width ||
                        pixelY + rY <= 0 || pixelY - rY >= graphicsDevice.height) {
                        excludedOffscreen++;
                        continue;
                    }

                    radiiX.push(rX);
                    radiiY.push(rY);
                    eccentricities.push(lambdaMax / lambdaMin);
                }

                radiiX.sort((a, b) => a - b);
                radiiY.sort((a, b) => a - b);
                eccentricities.sort((a, b) => a - b);
                const pct = (arr: number[], p: number) => arr.length ? arr[Math.min(arr.length - 1, Math.floor(p * arr.length))] : NaN;

                return {
                    dataset: dataset.id,
                    sampled: radiiX.length + excludedNearClip + excludedDetNonPositive + excludedOffscreen,
                    excludedNearClip,
                    excludedDetNonPositive,
                    excludedOffscreen,
                    tangentClampTriggered,
                    tangentClampTriggeredPct: (tangentClampTriggered / radiiX.length * 100).toFixed(2) + '%',
                    radiusX: { median: pct(radiiX, 0.5), p95: pct(radiiX, 0.95), p99: pct(radiiX, 0.99), max: radiiX[radiiX.length - 1] },
                    radiusY: { median: pct(radiiY, 0.5), p95: pct(radiiY, 0.95), p99: pct(radiiY, 0.99), max: radiiY[radiiY.length - 1] },
                    eccentricity: { median: pct(eccentricities, 0.5), p95: pct(eccentricities, 0.95), p99: pct(eccentricities, 0.99), max: eccentricities[eccentricities.length - 1] },
                    depth: { min: Math.min(...depths), median: pct(depths.slice().sort((a, b) => a - b), 0.5), max: Math.max(...depths) }
                };
            };
            // debug-only: compare 4 D/W composition variants side by side on
            // the SAME sample+pose (A=pre-fix baseline, B=camY flip only,
            // C=covariance-order flip only, D=both/current shader) to find
            // which actually minimizes streaking, instead of guessing.
            (window as unknown as { __ftgsCompareVariants?: (sampleCount?: number) => unknown }).__ftgsCompareVariants = (sampleCount = 3000) => {
                const mm = node.node.getWorldTransform().data;
                const mv = cam.viewMatrix.data;
                const pd = cam.projectionMatrix.data;
                const focalX = pd[0] * (graphicsDevice.width / 2);
                const focalY = pd[5] * (graphicsDevice.height / 2);
                const modelRot: number[][] = [[mm[0], mm[1], mm[2]], [mm[4], mm[5], mm[6]], [mm[8], mm[9], mm[10]]];
                const viewRot: number[][] = [[mv[0], mv[1], mv[2]], [mv[4], mv[5], mv[6]], [mv[8], mv[9], mv[10]]];
                const mulM3V3 = (m: number[][], v: number[]) => [
                    m[0][0] * v[0] + m[1][0] * v[1] + m[2][0] * v[2],
                    m[0][1] * v[0] + m[1][1] * v[1] + m[2][1] * v[2],
                    m[0][2] * v[0] + m[1][2] * v[1] + m[2][2] * v[2]
                ];
                const mulM3M3 = (a: number[][], b: number[][]) => [mulM3V3(a, b[0]), mulM3V3(a, b[1]), mulM3V3(a, b[2])];
                const transposeM3 = (m: number[][]) => [[m[0][0], m[1][0], m[2][0]], [m[0][1], m[1][1], m[2][1]], [m[0][2], m[1][2], m[2][2]]];
                const mulM4Point = (m: Float32Array, p: number[]) => [
                    m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12],
                    m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13],
                    m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14]
                ];
                const tanFovX = 0.5 * graphicsDevice.width / focalX;
                const tanFovY = 0.5 * graphicsDevice.height / focalY;

                const n2 = segment.header.vertexCount;
                const step = Math.max(1, Math.floor(n2 / sampleCount));

                const eccByVariant: Record<string, number[]> = { A_pre_fix: [], B_camYflip_only: [], C_covOrder_only: [], D_both_current: [] };
                const keys = ['A_pre_fix', 'B_camYflip_only', 'C_covOrder_only', 'D_both_current'] as const;

                for (let i = 0; i < n2; i += step) {
                    const base = i * stride;
                    const pos = [view.getFloat32(base + 0, true), view.getFloat32(base + 4, true), view.getFloat32(base + 8, true)];
                    const s0raw = view.getFloat32(base + 12, true);
                    const s1raw = view.getFloat32(base + 16, true);
                    const s2raw = view.getFloat32(base + 20, true);
                    const q0 = view.getFloat32(base + 24, true);
                    const q1 = view.getFloat32(base + 28, true);
                    const q2 = view.getFloat32(base + 32, true);
                    const q3 = view.getFloat32(base + 36, true);
                    const vx = view.getFloat32(base + 244, true);
                    const vy = view.getFloat32(base + 248, true);
                    const vz = view.getFloat32(base + 252, true);
                    const timeCenter = view.getFloat32(base + 236, true);

                    const dt = 0.5 - timeCenter;
                    const meanT = [pos[0] + dt * vx, pos[1] + dt * vy, pos[2] + dt * vz];

                    const qlen = Math.max(Math.hypot(q0, q1, q2, q3), 1e-8);
                    const w = q0 / qlen; const x = q1 / qlen; const y = q2 / qlen; const z = q3 / qlen;
                    const rot: number[][] = [
                        [1 - 2 * (y * y + z * z), 2 * (x * y + w * z), 2 * (x * z - w * y)],
                        [2 * (x * y - w * z), 1 - 2 * (x * x + z * z), 2 * (y * z + w * x)],
                        [2 * (x * z + w * y), 2 * (y * z - w * x), 1 - 2 * (x * x + y * y)]
                    ];
                    const scale = [Math.exp(s0raw), Math.exp(s1raw), Math.exp(s2raw)];
                    const rs = [
                        [rot[0][0] * scale[0], rot[0][1] * scale[0], rot[0][2] * scale[0]],
                        [rot[1][0] * scale[1], rot[1][1] * scale[1], rot[1][2] * scale[1]],
                        [rot[2][0] * scale[2], rot[2][1] * scale[2], rot[2][2] * scale[2]]
                    ];
                    const sigma = mulM3M3(rs, transposeM3(rs));

                    const meanWorld = mulM4Point(mm, meanT);
                    const viewPos = mulM4Point(mv, meanWorld);
                    const depth = -viewPos[2];
                    if (depth < 0.01) continue;
                    const invDepth = 1 / depth;
                    const invDepth2 = invDepth * invDepth;

                    for (const key of keys) {
                        const useCamYFlip = key === 'B_camYflip_only' || key === 'D_both_current';
                        const useCovOrderFlip = key === 'C_covOrder_only' || key === 'D_both_current';
                        const rawTanX = viewPos[0] / depth;
                        const yForTan = useCamYFlip ? -viewPos[1] : viewPos[1];
                        const rawTanY = yForTan / depth;
                        const tx = depth * Math.max(-1.3 * tanFovX, Math.min(1.3 * tanFovX, rawTanX));
                        const ty = depth * Math.max(-1.3 * tanFovY, Math.min(1.3 * tanFovY, rawTanY));
                        const jac: number[][] = [
                            [focalX * invDepth, 0, 0],
                            [0, focalY * invDepth, 0],
                            [-focalX * tx * invDepth2, -focalY * ty * invDepth2, 0]
                        ];
                        const t = useCovOrderFlip
                            ? mulM3M3(mulM3M3(mulM3M3(jac, modelRot), viewRot), modelRot)
                            : mulM3M3(mulM3M3(jac, viewRot), modelRot);
                        const cov3 = mulM3M3(mulM3M3(t, sigma), transposeM3(t));
                        const covA = cov3[0][0] + 0.3;
                        const covB = cov3[0][1];
                        const covC = cov3[1][1] + 0.3;
                        const det = covA * covC - covB * covB;
                        if (det <= 0) continue;
                        const mid = 0.5 * (covA + covC);
                        const discriminant = Math.max(0, mid * mid - det);
                        const lambdaMax = mid + Math.sqrt(discriminant);
                        const lambdaMin = Math.max(mid - Math.sqrt(discriminant), 1e-9);
                        eccByVariant[key].push(lambdaMax / lambdaMin);
                    }
                }

                const summarize = (arr: number[]) => {
                    arr.sort((a, b) => a - b);
                    const pct = (p: number) => arr.length ? arr[Math.min(arr.length - 1, Math.floor(p * arr.length))] : NaN;
                    return { n: arr.length, median: pct(0.5), p95: pct(0.95), p99: pct(0.99), max: arr[arr.length - 1] };
                };
                const out: Record<string, unknown> = { dataset: dataset.id };
                for (const key of keys) out[key] = summarize(eccByVariant[key]);
                return out;
            };
            // debug-only: dump every intermediate value for exactly ONE
            // splat index, to hand-trace where a bad value first appears.
            (window as unknown as { __ftgsDebugSplat?: (index?: number) => unknown }).__ftgsDebugSplat = (index = 0) => {
                const mm = node.node.getWorldTransform().data;
                const mv = cam.viewMatrix.data;
                const pd = cam.projectionMatrix.data;
                const focalX = pd[0] * (graphicsDevice.width / 2);
                const focalY = pd[5] * (graphicsDevice.height / 2);
                const modelRot: number[][] = [[mm[0], mm[1], mm[2]], [mm[4], mm[5], mm[6]], [mm[8], mm[9], mm[10]]];
                const viewRot: number[][] = [[mv[0], mv[1], mv[2]], [mv[4], mv[5], mv[6]], [mv[8], mv[9], mv[10]]];
                const mulM3V3 = (m: number[][], v: number[]) => [
                    m[0][0] * v[0] + m[1][0] * v[1] + m[2][0] * v[2],
                    m[0][1] * v[0] + m[1][1] * v[1] + m[2][1] * v[2],
                    m[0][2] * v[0] + m[1][2] * v[1] + m[2][2] * v[2]
                ];
                const mulM3M3 = (a: number[][], b: number[][]) => [mulM3V3(a, b[0]), mulM3V3(a, b[1]), mulM3V3(a, b[2])];
                const transposeM3 = (m: number[][]) => [[m[0][0], m[1][0], m[2][0]], [m[0][1], m[1][1], m[2][1]], [m[0][2], m[1][2], m[2][2]]];
                const mulM4Point = (m: Float32Array, p: number[]) => [
                    m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12],
                    m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13],
                    m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14]
                ];
                const tanFovX = 0.5 * graphicsDevice.width / focalX;
                const tanFovY = 0.5 * graphicsDevice.height / focalY;

                const base = index * stride;
                const pos = [view.getFloat32(base + 0, true), view.getFloat32(base + 4, true), view.getFloat32(base + 8, true)];
                const s0raw = view.getFloat32(base + 12, true);
                const s1raw = view.getFloat32(base + 16, true);
                const s2raw = view.getFloat32(base + 20, true);
                const q0 = view.getFloat32(base + 24, true);
                const q1 = view.getFloat32(base + 28, true);
                const q2 = view.getFloat32(base + 32, true);
                const q3 = view.getFloat32(base + 36, true);
                const vx = view.getFloat32(base + 244, true);
                const vy = view.getFloat32(base + 248, true);
                const vz = view.getFloat32(base + 252, true);
                const timeCenter = view.getFloat32(base + 236, true);

                const dt = 0.5 - timeCenter;
                const meanT = [pos[0] + dt * vx, pos[1] + dt * vy, pos[2] + dt * vz];

                const qlen = Math.max(Math.hypot(q0, q1, q2, q3), 1e-8);
                const w = q0 / qlen; const x = q1 / qlen; const y = q2 / qlen; const z = q3 / qlen;
                const rot: number[][] = [
                    [1 - 2 * (y * y + z * z), 2 * (x * y + w * z), 2 * (x * z - w * y)],
                    [2 * (x * y - w * z), 1 - 2 * (x * x + z * z), 2 * (y * z + w * x)],
                    [2 * (x * z + w * y), 2 * (y * z - w * x), 1 - 2 * (x * x + y * y)]
                ];
                const scale = [Math.exp(s0raw), Math.exp(s1raw), Math.exp(s2raw)];
                const rs = [
                    [rot[0][0] * scale[0], rot[0][1] * scale[0], rot[0][2] * scale[0]],
                    [rot[1][0] * scale[1], rot[1][1] * scale[1], rot[1][2] * scale[1]],
                    [rot[2][0] * scale[2], rot[2][1] * scale[2], rot[2][2] * scale[2]]
                ];
                const sigma = mulM3M3(rs, transposeM3(rs));

                const meanWorld = mulM4Point(mm, meanT);
                const viewPos = mulM4Point(mv, meanWorld);
                const depth = -viewPos[2];
                const camY = -viewPos[1];
                const rawTanX = viewPos[0] / depth;
                const rawTanY = camY / depth;
                const tx = depth * Math.max(-1.3 * tanFovX, Math.min(1.3 * tanFovX, rawTanX));
                const ty = depth * Math.max(-1.3 * tanFovY, Math.min(1.3 * tanFovY, rawTanY));
                const jac: number[][] = [
                    [focalX / depth, 0, 0],
                    [0, focalY / depth, 0],
                    [-focalX * tx / (depth * depth), -focalY * ty / (depth * depth), 0]
                ];
                const t = mulM3M3(mulM3M3(mulM3M3(jac, modelRot), viewRot), modelRot);
                const cov3 = mulM3M3(mulM3M3(t, sigma), transposeM3(t));

                return {
                    dataset: dataset.id,
                    index, focalX, focalY, tanFovX, tanFovY,
                    modelRot, viewRot,
                    pos, meanT, meanWorld, viewPos, depth, camY, rawTanX, rawTanY, tx, ty,
                    sigma, jac, t, cov3
                };
            };
            // debug-only: readback-based correctness check (see
            // FtgsDepthSorter.verify()'s doc) - independent of whether the
            // canvas is actually compositing pixels anywhere, so this can
            // confirm/rule out sort correctness even from an environment
            // that can't take a real screenshot.
            (window as unknown as { __ftgsVerifySort?: (maxK?: number) => Promise<unknown> }).__ftgsVerifySort = async (maxK?: number) => {
                const sorter = node.debugSorter;
                if (!sorter) {
                    return { error: 'no sorter yet' };
                }
                try {
                    // IMPORTANT: must match the live render path's own
                    // composite (ftgs-splat-scene-node.ts's update(): view *
                    // node.getWorldTransform(), NOT the bare camera view
                    // matrix) - compute_keys computes depth against LOCAL-
                    // space splat positions, so it needs the model transform
                    // folded in exactly like the render shader does.
                    const viewModel = new Mat4().mul2(cam.viewMatrix, node.node.getWorldTransform());
                    // bisection aid: maxK=1 runs compute_keys only (no
                    // bitonic passes at all - k starts at 2), so the result
                    // should trivially be a valid (unsorted) permutation;
                    // pass increasing powers of two to find the first k
                    // whose pass breaks the permutation
                    sorter.sort(new Float32Array(viewModel.data), 0.5, maxK);
                    const result = await sorter.verify({ checkOrder: maxK === undefined });
                    return { dataset: dataset.id, ok: true, maxK, realCount: sorter.realCount, paddedCount: sorter.paddedCount, sampleFirst: Array.from(result.indices.slice(0, 5)), sampleLast: Array.from(result.indices.slice(-5)) };
                } catch (error) {
                    return { dataset: dataset.id, ok: false, maxK, error: String(error) };
                }
            };
            // raw, non-throwing dump for bisecting verify() failures - reads
            // the buffers directly with no permutation/order validation
            (window as unknown as { __ftgsDumpSort?: (maxK?: number) => Promise<unknown> }).__ftgsDumpSort = async (maxK?: number) => {
                const sorter = node.debugSorter;
                if (!sorter) {
                    return { error: 'no sorter yet' };
                }
                sorter.sort(new Float32Array(cam.viewMatrix.data), 0.5, maxK);
                const raw = await sorter.readRawDirect(32);
                return { dataset: dataset.id, maxK, realCount: sorter.realCount, paddedCount: sorter.paddedCount, ...raw };
            };
            // ?ftgsVerifySort=1 - device test (Galaxy S22 Ultra, 2026-10-16:
            // every chunk's luma passed its MD5 check yet the scene drew
            // almost black with a few stray splats - the shape of a sort
            // whose indices are mostly garbage). A phone has no console to
            // call __ftgsVerifySort from, so this runs the readback check a
            // few seconds in, for both sort schedules, and leaves the verdict
            // as an issue report (sent to the server like the others).
            if (new URLSearchParams(window.location.search).has('ftgsVerifySort')) {
                const verifySortOnDevice = async () => {
                    const t0 = performance.now();
                    while (!node.debugSorter && performance.now() - t0 < 60000) {
                        await new Promise(r => setTimeout(r, 500));
                    }
                    await new Promise(r => setTimeout(r, 3000));
                    const saved = FtgsDepthSorter.sharedMemory;
                    const lines: string[] = [];
                    let failed = false;
                    type VerifyOut = { ok: boolean; error?: string; realCount?: number; paddedCount?: number; sampleFirst?: number[]; sampleLast?: number[] };
                    const verify = (window as unknown as { __ftgsVerifySort: () => Promise<VerifyOut> }).__ftgsVerifySort;
                    for (const shared of [true, false]) {
                        FtgsDepthSorter.sharedMemory = shared;
                        const name = shared ? 'shared-memory 정렬(기본)' : 'global-memory 정렬(?ftgsSortGlobal)';
                        const r = await verify();
                        if (r.ok) {
                            lines.push(`${name}: 정상 (realCount ${r.realCount}, paddedCount ${r.paddedCount}, 앞 ${r.sampleFirst?.join(',')} / 뒤 ${r.sampleLast?.join(',')})`);
                        } else {
                            failed = true;
                            lines.push(`${name}: 실패 - ${r.error}`);
                        }
                    }
                    FtgsDepthSorter.sharedMemory = saved;
                    const key = `sort-verify:${dataset.id}`;
                    issues.report({
                        key, severity: failed ? 'error' : 'warn',
                        title: failed ? 'GPU 깊이 정렬 결과가 틀림 (?ftgsVerifySort)' : 'GPU 깊이 정렬 검증 통과 (?ftgsVerifySort)',
                        cause: failed ?
                            '스플랫을 그리기 전에 GPU 컴퓨트 셰이더로 깊이 순서를 정렬하는데, 그 결과를 읽어 확인하니 올바른 순열이 아니거나 오름차순이 아닙니다. 그리기 셰이더는 이 순서로 스플랫 데이터를 찾으므로, 틀린 순서는 화면에 스플랫이 사라지거나 엉뚱한 곳에 그려지는 현상으로 나타납니다. 이 기기의 GPU/드라이버에서 정렬 셰이더가 다르게 동작하는 경우입니다.' :
                            'GPU 깊이 정렬 결과를 읽어 확인했고, 두 정렬 방식 모두 올바른 순열과 오름차순이었습니다. 화면 이상이 있다면 정렬이 아니라 그 뒤 단계(그리기 셰이더, 블렌딩, 캔버스 출력)의 문제입니다.',
                        evidence: lines
                    });
                    if (!failed) {
                        issues.resolve(key, '두 정렬 방식 모두 정상');
                    }
                };
                verifySortOnDevice().catch(e => console.warn('[ftgsDebug] verify-sort failed to run', e));
            }

            return () => {
                resetCameraToInitial = null;
                releaseCameraLockAbort?.abort();
                snapshotKeyAbort?.abort();
                scene.app.off('update', updateHandler);
                scene.remove(sceneElement);
                node.removeFromLayer(scene.worldLayer);
                node.destroy();
                if (rootRotationNode === node) {
                    rootRotationNode = null;
                }
                segmentManager.destroy();
                // stop any still-running background clip decodes (worker
                // pool + download) - a switched-away bin dataset must not
                // keep burning cores/link (cancellation Rule)
                binPrepare?.destroy();
                // same for a GOF stream's download + hardware decoders
                gofStream?.destroy();
                issues.resolve(stallKey, '데이터셋 전환으로 종료');
                // the destroyed manager reports no further progress (see its
                // streaming-callback guards), so without this the bar would
                // freeze at whatever fraction the old dataset's download had
                // reached until the next dataset's own first progress event
                downloadProgressBar.hide();
            };
        };

        // Dataset discovery + picker wiring. A folder in datasets.json whose
        // own dataset.json fails to load is skipped by fetchDatasetConfigs()
        // (logged, not fatal) rather than taking the whole picker down.
        try {
            const datasets = await fetchDatasetConfigs();
            if (datasets.length === 0) {
                throw new Error('uploads/datasets.json listed no usable datasets');
            }

            const picker = new FtgsDatasetPicker(datasets);
            editorUI.canvasContainer.append(picker);
            // left-anchored: the original stays in the left half, where the
            // rail's left-edge reveal zone (real screen x) is
            addStereoMirror(picker.dom, 'left');
            addStereoMirror(picker.hintElement, 'left');

            let disposeCurrentDataset: (() => void) | null = null;
            let currentDataset: FtgsDatasetConfig | null = null;

            // --- rate ladder ------------------------------------------------
            // The stats overlay's Source Type / Velocity Quant / Rate pills
            // pick ONE folder of a dataset's ladder,
            // `uploads/<id>/ours_<arm>_r<N>/`, holding that rate point's
            // `bitstream.bin` and its `decoded/<id>_<variant>.ply`. Switching
            // any of the three reloads the dataset from that folder through
            // the ordinary selectDataset() path, so teardown, abort and the
            // generation guard are all shared with the picker.
            //
            // Only datasets that declare `ladder: true` have those folders;
            // everything else keeps the flat `uploads/<id>/segment_NNN.*`
            // layout and its pills stay inert.
            type LadderPoint = { source: 'ply' | 'bin'; quant: 'gauss' | 'lin'; rate: number };
            let currentBase: FtgsDatasetConfig | null = null;
            let currentPoint: LadderPoint = { source: 'bin', quant: 'gauss', rate: 0 };

            /** where a dataset starts: its own declaration, else the badge defaults */
            const initialPoint = (base: FtgsDatasetConfig): LadderPoint => ({
                source: base.source ?? 'bin',
                quant: base.quantization ?? 'gauss',
                rate: base.rate ?? 0
            });

            /** the config startFtgsDataset() actually runs - identical to `base` for a non-laddered dataset */
            const withVariant = (base: FtgsDatasetConfig, point: LadderPoint): FtgsDatasetConfig => (base.ladder ? {
                ...base,
                source: point.source,
                quantization: point.quant,
                rate: point.rate,
                variantDir: variantDirName(point.quant, point.rate, base.variantDirTemplate)
            } : base);
            // guards against a switch landing while a previous
            // startFtgsDataset is STILL AWAITING (typically mid-bootstrap-
            // download): the generation stamp lets the superseded call
            // detect it lost, and the abort controller actually cancels its
            // in-flight segment download (cancellation Rule) instead of
            // letting both datasets' downloads share the link
            let selectGeneration = 0;
            let bootstrapAbort: AbortController | null = null;

            const selectDataset = async (dataset: FtgsDatasetConfig): Promise<boolean> => {
                const generation = ++selectGeneration;
                bootstrapAbort?.abort();
                bootstrapAbort = new AbortController();
                const abortSignal = bootstrapAbort.signal;
                picker.setLoading(dataset.id);
                try {
                    // tear down whatever's currently loaded FIRST - the new
                    // dataset's startFtgsDataset() reassigns the same
                    // window.__ftgs* hooks/playbackBar callbacks the old one
                    // owned, but the old session's scene.app 'update'
                    // listener and GPU resources need to actually stop
                    // before the new ones start, not just get shadowed.
                    disposeCurrentDataset?.();
                    disposeCurrentDataset = null;
                    const dispose = await startFtgsDataset(dataset, abortSignal);
                    if (generation !== selectGeneration) {
                        // a newer selection took over while this one was
                        // loading and its abort didn't land in time - tear
                        // this late arrival straight down
                        dispose();
                        return false;
                    }
                    disposeCurrentDataset = dispose;
                    currentDataset = dataset;
                    picker.setActive(dataset.id);
                    issues.resolve(`dataset-load:${dataset.id}/${dataset.variantDir ?? ''}`, '다시 선택해 정상 로딩됨');
                    return true;
                } catch (error) {
                    if ((error as Error)?.name === 'AbortError') {
                        // superseded by a newer selection - expected, not a
                        // failure
                        return false;
                    }
                    console.error(`[ftgsDebug] failed to load dataset "${dataset.id}"`, error);
                    (window as unknown as { __ftgsLiveError?: unknown }).__ftgsLiveError = String(error);
                    // the bootstrap's download bar would otherwise stay frozen
                    // at the fraction the failure hit, reading as a stalled
                    // download (seen on a Galaxy Note 9, 2026-10-12)
                    downloadProgressBar.hide();
                    issues.report(classifyLoadError(`dataset-load:${dataset.id}/${dataset.variantDir ?? ''}`, `데이터셋 "${dataset.id}" 로딩 실패`, error,
                        [`파일: ${dataset.source === 'bin' ? binSegmentUrl(dataset, 0) : `${dataset.id} (ply)`}`]));
                    return false;
                } finally {
                    // only the LATEST selection owns the loading indicator -
                    // a superseded call clearing it would wipe the newer
                    // selection's spinner mid-load
                    if (generation === selectGeneration) {
                        picker.setLoading(null);
                    }
                }
            };

            /** load `base` at ladder point `point`, remembering both for the next switch */
            /** the last ladder point that actually rendered - what a failed switch falls back to */
            let lastGoodPoint: LadderPoint | null = null;

            const selectLadderPoint = async (base: FtgsDatasetConfig, point: LadderPoint, isFallback = false) => {
                currentBase = base;
                currentPoint = point;
                sourceBadges.setSwitchable(false);
                try {
                    const ok = await selectDataset(withVariant(base, point));
                    if (ok) {
                        lastGoodPoint = point;
                        return;
                    }
                    // A failed switch has ALREADY torn the previous session
                    // down (selectDataset disposes before it loads), so
                    // leaving it here means a black screen under a badge
                    // still showing the old variant. Go back to the last
                    // point that rendered instead - once only, so a broken
                    // dataset can't ping-pong.
                    if (!isFallback && lastGoodPoint && lastGoodPoint !== point) {
                        console.warn(`[ftgsDebug] [${base.id}] ${variantDirName(point.quant, point.rate, base.variantDirTemplate)} (${point.source}) failed - restoring ${variantDirName(lastGoodPoint.quant, lastGoodPoint.rate, base.variantDirTemplate)} (${lastGoodPoint.source})`);
                        await selectLadderPoint(base, lastGoodPoint, true);
                    }
                } finally {
                    // re-armed on the BASE dataset's ladder flag, not on
                    // whether this particular variant loaded: a 404 on one
                    // rate point must still leave the pills usable, or the
                    // only way out of a broken variant is a page reload
                    sourceBadges.setSwitchable(!!base.ladder);
                }
            };

            picker.onSelect = (id: string) => {
                const dataset = datasets.find(d => d.id === id);
                if (dataset) {
                    // a fresh dataset starts at its own declared point, not at
                    // whatever the previous one happened to be showing
                    void selectLadderPoint(dataset, initialPoint(dataset));
                }
            };

            sourceBadges.onChange = (selection) => {
                if (!currentBase?.ladder) {
                    return;
                }
                console.log(`[ftgsDebug] [${currentBase.id}] ladder -> ${selection.source}/${variantDirName(selection.quant, selection.rate, currentBase.variantDirTemplate)}`);
                void selectLadderPoint(currentBase, {
                    source: selection.source,
                    quant: selection.quant,
                    rate: selection.rate
                });
            };

            // WebGPU device-lost recovery (candidate cause of the mobile-
            // Chrome black screen on rotate/fullscreen): the engine restores
            // ITS OWN resources across a device loss, but the FTGS
            // renderer's custom GPU state (segment storage buffers, sorter
            // pipelines) is not re-uploaded - the engine would come back
            // while the splats stay black. Tearing the session down and
            // re-running startFtgsDataset rebuilds everything from scratch;
            // segments re-fetch through the service worker's cache, so
            // recovery costs no re-download.
            scene.graphicsDevice.on('devicerestored', () => {
                if (currentDataset) {
                    console.warn(`[ftgsDebug] device restored - restarting dataset "${currentDataset.id}"`);
                    void selectDataset(currentDataset);
                }
            });

            // ?ftgsDataset=<id> picks the initial dataset (falls back to the
            // first one) - lets a link/bookmark point straight at a specific
            // dataset instead of always landing on whichever is first in
            // datasets.json.
            const requestedId = new URLSearchParams(window.location.search).get('ftgsDataset');
            const initial = datasets.find(d => d.id === requestedId) ?? datasets[0];
            if (initial) {
                await selectLadderPoint(initial, initialPoint(initial));
            } else {
                console.warn('[ftgsDebug] no dataset found in uploads/datasets.json');
            }
        } catch (error) {
            console.error('[ftgsDebug] live scene node FAILED', error);
            (window as unknown as { __ftgsLiveError?: unknown }).__ftgsLiveError = String(error);
            issues.report(classifyLoadError('viewer-start', '뷰어 시작 실패', error));
        }
    }

    // handle load params
    const loadList = url.searchParams.getAll('load');
    const filenameList = url.searchParams.getAll('filename');
    for (const [i, value] of loadList.entries()) {
        const decoded = decodeURIComponent(value);
        const filename = i < filenameList.length ?
            decodeURIComponent(filenameList[i]) :
            decoded.split('/').pop();

        await events.invoke('import', [{
            filename,
            url: decoded
        }]);
    }


    // handle OS-based file association in PWA mode
    if ('launchQueue' in window) {
        window.launchQueue.setConsumer(async (launchParams: LaunchParams) => {
            for (const file of launchParams.files) {
                await events.invoke('import', [{
                    filename: file.name,
                    contents: await file.getFile()
                }]);
            }
        });
    }
};

export { main };
